import { useState, useEffect, useMemo } from "react";
import axios from "axios";

const API = "/api";

// ── Combined Club Admin + Player Account — Phase 2 dual-JWT plumbing ──────────
// `localStorage.token` is the active session JWT (super admin / club admin /
// player). When a Club Admin has approved player links, login also stores
// `localStorage.linkedPlayerTokens` (JSON map keyed by player_id) and
// `localStorage.viewMode` (one of "admin" | <player_id>). getAuthHeaders()
// reads viewMode at call time and returns the right Bearer token — every
// network call in this module routes through it. There is intentionally no
// way to override the token from a component prop: the helper IS the source
// of truth, so a forgotten call site cannot use the admin token in player view.
const LS_TOKEN = "token";
const LS_LINKED_TOKENS = "linkedPlayerTokens";
const LS_VIEW_MODE = "viewMode";

function getAuthHeaders() {
  const viewMode = localStorage.getItem(LS_VIEW_MODE) || "admin";
  if (viewMode === "admin") {
    const t = localStorage.getItem(LS_TOKEN);
    return t ? { Authorization: `Bearer ${t}` } : {};
  }
  // Player view: viewMode is a player_id; look up its JWT in the map.
  let map = {};
  try { map = JSON.parse(localStorage.getItem(LS_LINKED_TOKENS) || "{}"); } catch (_) {}
  const t = map[viewMode];
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function getLinkedPlayerTokens() {
  try { return JSON.parse(localStorage.getItem(LS_LINKED_TOKENS) || "{}"); }
  catch (_) { return {}; }
}

function clearLinkedPlayerTokens() {
  localStorage.removeItem(LS_LINKED_TOKENS);
  localStorage.setItem(LS_VIEW_MODE, "admin");
}

const fmt = (n, prefix = true) => {
  if (n === undefined || n === null) return "—";
  const s = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (!prefix) return s;
  return (n >= 0 ? "+" : "-") + s;
};
const pct = (n) => n !== undefined ? n.toFixed(1) + "%" : "—";
// Aggregate-row rate range. 0% is a legitimate configured rate, so render
// "0%" (not em-dash) when the subtree has no contributing rows.
const rangePct1 = (has, min, max) => {
  if (!has) return "0%";
  if (min === max) return min.toFixed(1) + "%";
  return `${min.toFixed(1)}-${max.toFixed(1)}%`;
};
const rangeInt = (has, min, max) => {
  if (!has) return "0";
  if (min === max) return min.toFixed(0);
  return `${min.toFixed(0)}-${max.toFixed(0)}`;
};
const clr = (n) => (n || 0) >= 0 ? "#2ecc71" : "#e74c3c";


// ── Phase 3: shared download helper + HierarchyTree ───────────────────────────

async function downloadExport({ kind, club, target, period, headers, onError }) {
  try {
    const qs = new URLSearchParams({ club, target });
    if (period && period !== "current") qs.set("period", period);
    const res = await fetch(`${API}/export/${kind}?${qs.toString()}`, { headers });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try { const j = await res.json(); detail = j.detail || detail; } catch {}
      if (onError) onError(detail); else alert(`Export failed: ${detail}`);
      return;
    }
    const cd = res.headers.get("content-disposition") || "";
    const m = cd.match(/filename="?([^";]+)"?/);
    const fname = m ? m[1] : `settlement_${target}.${kind}`;
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  } catch (e) {
    if (onError) onError(String(e)); else alert(`Export failed: ${e}`);
  }
}

function buildHierarchyTree(rows, skipIds) {
  // rows: filtered list with .club attached. skipIds: Set of transparent SA ids.
  // Placement is role-driven:
  //   - role "Super Agent" → SA self-row (saId := p.member_id).
  //   - role "Agent"       → Agent self-row (under p.super_agent_id's SA if present,
  //                           else standalone at the top level).
  //   - everything else    → leaf placed per super_agent_id / agent_id.
  // Returns { saGroups, standaloneAgents, orphans }.
  const sa = new Map();           // saId → { id, nick, selfRow, clubs, agents:Map, leaves }
  const standaloneAg = new Map(); // agId → { id, nick, selfRow, clubs, leaves }
  const orphans = [];

  const ensureSa = (saId, nick) => {
    if (!sa.has(saId)) sa.set(saId, { id: saId, nick: nick || saId, selfRow: null, clubs: new Set(), agents: new Map(), leaves: [] });
    const node = sa.get(saId);
    if (nick && node.nick === node.id) node.nick = nick;
    return node;
  };
  const ensureAg = (agMap, agId, nick) => {
    if (!agMap.has(agId)) agMap.set(agId, { id: agId, nick: nick || agId, selfRow: null, clubs: new Set(), leaves: [] });
    const node = agMap.get(agId);
    if (nick && node.nick === node.id) node.nick = nick;
    return node;
  };

  for (const p of rows) {
    // A transparent SA's own row is never rendered, under any parent.
    if (skipIds.has(p.member_id)) continue;

    let saId = (p.super_agent_id && p.super_agent_id !== "-") ? p.super_agent_id : null;
    let agId = (p.agent_id && p.agent_id !== "-") ? p.agent_id : null;
    if (saId && skipIds.has(saId)) saId = null;

    if (p.role === "Super Agent") {
      const node = ensureSa(p.member_id, p.nickname);
      node.selfRow = p;
      node.clubs.add(p.club);
      continue;
    }

    if (p.role === "Agent") {
      if (saId) {
        const saNode = ensureSa(saId, p.super_agent_nick);
        saNode.clubs.add(p.club);
        const agNode = ensureAg(saNode.agents, p.member_id, p.nickname);
        agNode.selfRow = p;
        agNode.clubs.add(p.club);
      } else {
        const agNode = ensureAg(standaloneAg, p.member_id, p.nickname);
        agNode.selfRow = p;
        agNode.clubs.add(p.club);
      }
      continue;
    }

    // Regular player leaf
    if (saId) {
      const saNode = ensureSa(saId, p.super_agent_nick);
      saNode.clubs.add(p.club);
      if (agId && agId !== saId && agId !== p.member_id) {
        const agNode = ensureAg(saNode.agents, agId, p.agent_nick);
        agNode.leaves.push(p);
        agNode.clubs.add(p.club);
      } else {
        saNode.leaves.push(p);
      }
    } else if (agId && agId !== p.member_id) {
      const agNode = ensureAg(standaloneAg, agId, p.agent_nick);
      agNode.leaves.push(p);
      agNode.clubs.add(p.club);
    } else {
      orphans.push(p);
    }
  }

  // Aggregation: self contributes when it has games > 0.
  // Configured-rate columns (RB%, Tax) track min and max across the subtree.
  // Render shows a single value if min === max, else "min-max" — never a
  // weighted average, so the collapsed row matches what you'd see expanded.
  const AGG_KEYS = [
    "rake", "settlement", "active", "count",
    "ring_win", "mtt_win", "ring_rake", "mtt_rake", "ring_pts", "mtt_pts",
  ];
  const RATE_KEYS = ["ring_rb", "mtt_rb", "ring_tax", "mtt_tax"];
  const zero = () => {
    const o = {};
    for (const k of AGG_KEYS) o[k] = 0;
    for (const k of RATE_KEYS) { o[`${k}_min`] = 0; o[`${k}_max`] = 0; o[`${k}_has`] = false; }
    return o;
  };
  const plus = (a, b) => {
    const o = {};
    for (const k of AGG_KEYS) o[k] = (a[k] || 0) + (b[k] || 0);
    for (const k of RATE_KEYS) {
      const ah = !!a[`${k}_has`], bh = !!b[`${k}_has`];
      if (ah && bh) {
        o[`${k}_min`] = Math.min(a[`${k}_min`], b[`${k}_min`]);
        o[`${k}_max`] = Math.max(a[`${k}_max`], b[`${k}_max`]);
        o[`${k}_has`] = true;
      } else if (ah) {
        o[`${k}_min`] = a[`${k}_min`]; o[`${k}_max`] = a[`${k}_max`]; o[`${k}_has`] = true;
      } else if (bh) {
        o[`${k}_min`] = b[`${k}_min`]; o[`${k}_max`] = b[`${k}_max`]; o[`${k}_has`] = true;
      } else {
        o[`${k}_min`] = 0; o[`${k}_max`] = 0; o[`${k}_has`] = false;
      }
    }
    return o;
  };
  const sumRow = (p) => {
    const rbr = p.ring_rakeback_pct || 0, rbm = p.mtt_rakeback_pct || 0;
    const rtp = p.ring_tax_pct || 0, mtp = p.mtt_tax_pct || 0;
    return {
      rake: p.total_rake || 0,
      settlement: p.total_points || 0,
      active: ((p.total_games || 0) > 0) ? 1 : 0,
      count: 1,
      ring_win: p.ring_winnings || 0,
      mtt_win: p.mtt_winnings || 0,
      ring_rake: p.ring_rake || 0,
      mtt_rake: p.mtt_rake || 0,
      ring_pts: p.ring_points || 0,
      mtt_pts: p.mtt_points || 0,
      ring_rb_min: rbr, ring_rb_max: rbr, ring_rb_has: true,
      mtt_rb_min: rbm, mtt_rb_max: rbm, mtt_rb_has: true,
      ring_tax_min: rtp, ring_tax_max: rtp, ring_tax_has: true,
      mtt_tax_min: mtp, mtt_tax_max: mtp, mtt_tax_has: true,
    };
  };
  const sumRows = (arr) => arr.reduce((acc, p) => plus(acc, sumRow(p)), zero());
  const hasActiveSelf = (n) => !!(n.selfRow && (n.selfRow.total_games || 0) > 0);
  const selfContrib = (n) => hasActiveSelf(n) ? sumRow(n.selfRow) : zero();

  const standaloneAgents = [];
  for (const agNode of standaloneAg.values()) {
    if (agNode.leaves.length === 0 && !hasActiveSelf(agNode)) continue;
    const agg = plus(selfContrib(agNode), sumRows(agNode.leaves));
    standaloneAgents.push({ ...agNode, agg });
  }

  const saGroups = [];
  for (const saNode of sa.values()) {
    let agg = plus(selfContrib(saNode), sumRows(saNode.leaves));
    const agents = [];
    for (const agNode of saNode.agents.values()) {
      if (agNode.leaves.length === 0 && !hasActiveSelf(agNode)) continue;
      const agAgg = plus(selfContrib(agNode), sumRows(agNode.leaves));
      agents.push({ ...agNode, agg: agAgg });
      agg = plus(agg, agAgg);
    }
    if (saNode.leaves.length === 0 && agents.length === 0 && !hasActiveSelf(saNode)) continue;
    agents.sort((a, b) => b.agg.rake - a.agg.rake);
    saGroups.push({ ...saNode, agents, agg });
  }
  saGroups.sort((a, b) => b.agg.rake - a.agg.rake);
  standaloneAgents.sort((a, b) => b.agg.rake - a.agg.rake);

  return { saGroups, standaloneAgents, orphans };
}

function HierarchyTree({ rows, headers, skipIds, clubFilter, period }) {
  const _um = useMemo, _us = useState, _ue = useEffect;
  const tree = _um(() => buildHierarchyTree(rows, skipIds || new Set()), [rows, skipIds]);
  const [expanded, setExpanded] = _us(new Set());
  // Collapse all on filter change
  _ue(() => { setExpanded(new Set()); }, [clubFilter, period]);
  const toggle = (key) => {
    setExpanded(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  };

  // Decide the export club for an aggregate node: selected clubFilter if specific,
  // else the single club in the subtree if unambiguous, else null (buttons disabled).
  const aggClub = (clubsSet) => {
    if (clubFilter && clubFilter !== "all") return clubFilter;
    if (clubsSet && clubsSet.size === 1) return [...clubsSet][0];
    return null;
  };

  const Btns = ({ club, target, disabledReason }) => {
    if (disabledReason) {
      return (
        <div style={{ display: "flex", gap: 4 }} title={disabledReason}>
          <button style={{ ...S.editBtn, padding: "3px 8px", fontSize: 11, opacity: 0.4, cursor: "not-allowed" }} disabled>CSV</button>
          <button style={{ ...S.saveBtn, padding: "3px 8px", fontSize: 11, marginRight: 0, opacity: 0.4, cursor: "not-allowed" }} disabled>PDF</button>
        </div>
      );
    }
    const stop = (e) => e.stopPropagation();
    return (
      <div style={{ display: "flex", gap: 4 }}>
        <button style={{ ...S.editBtn, padding: "3px 8px", fontSize: 11 }} onClick={(e) => { stop(e); downloadExport({ kind: "csv", club, target, period, headers }); }}>CSV</button>
        <button style={{ ...S.saveBtn, padding: "3px 8px", fontSize: 11, marginRight: 0 }} onClick={(e) => { stop(e); downloadExport({ kind: "pdf", club, target, period, headers }); }}>PDF</button>
      </div>
    );
  };

  const renderLeaf = (p, indent) => (
    <tr key={`leaf:${p.club}:${p.member_id}`} style={{ background: "#0d1520" }}>
      <td style={{ ...S.td, paddingLeft: 8 + indent * 20 }}><strong style={{ color: "#e0e0e0" }}>{p.nickname}</strong></td>
      <td style={{ ...S.td, color: "#6b7280", fontFamily: "monospace" }}>{p.member_id}</td>
      <td style={S.td}><span style={S.badge("#818cf8")}>{p.club}</span></td>
      <td style={S.td}><span style={S.badge(p.role === "Super Agent" ? "#f59e0b" : p.role === "Agent" ? "#60a5fa" : "#6b7280")}>{p.role}</span></td>
      <td style={{ ...S.td, color: clr(p.ring_winnings) }}>{fmt(p.ring_winnings)}</td>
      <td style={{ ...S.td, color: clr(p.mtt_winnings) }}>{fmt(p.mtt_winnings)}</td>
      <td style={{ ...S.td, color: "#f59e0b" }}>{fmt(p.total_rake, false)}</td>
      <td style={S.td}>{pct(p.ring_rakeback_pct)}</td>
      <td style={S.td}>
        <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.3 }}>
          {(p.ring_tax_pct ?? 0).toFixed(0)}/{(p.mtt_tax_pct ?? 0).toFixed(0)}%
        </div>
      </td>
      <td style={{ ...S.td, color: clr(p.ring_points) }}>{fmt(p.ring_points)}</td>
      <td style={{ ...S.td, color: clr(p.mtt_points) }}>{fmt(p.mtt_points)}</td>
      <td style={{ ...S.td, color: clr(p.total_points), fontWeight: 700 }}>{fmt(p.total_points)}</td>
      <td style={S.td}>{p.total_games}</td>
      <td style={S.td}><Btns club={p.club} target={p.member_id} /></td>
    </tr>
  );

  const aggRow = (key, indent, roleLabel, roleColor, nick, id, clubsSet, agg, expandedFlag) => {
    const club = aggClub(clubsSet);
    const disabledReason = club ? null : "Select a specific club to export this subtree.";
    return (
      <tr key={key} style={{ background: "#111827", cursor: "pointer" }} onClick={() => toggle(key)}>
        <td style={{ ...S.td, paddingLeft: 8 + indent * 20 }}>
          <span style={{ color: "#9ca3af", marginRight: 6 }}>{expandedFlag ? "▾" : "▸"}</span>
          <strong style={{ color: "#e0e0e0" }}>{nick}</strong>
          <span style={{ color: "#6b7280", fontSize: 11, marginLeft: 6 }}>({agg.active} active)</span>
        </td>
        <td style={{ ...S.td, color: "#6b7280", fontFamily: "monospace" }}>{id}</td>
        <td style={S.td}>{clubsSet && clubsSet.size > 0 ? [...clubsSet].map(c => <span key={c} style={{ ...S.badge("#818cf8"), marginRight: 4 }}>{c}</span>) : "—"}</td>
        <td style={S.td}><span style={S.badge(roleColor)}>{roleLabel}</span></td>
        <td style={{ ...S.td, color: clr(agg.ring_win) }}>{fmt(agg.ring_win)}</td>
        <td style={{ ...S.td, color: clr(agg.mtt_win) }}>{fmt(agg.mtt_win)}</td>
        <td style={{ ...S.td, color: "#f59e0b" }}>{fmt(agg.rake, false)}</td>
        <td style={S.td}>{rangePct1(agg.ring_rb_has, agg.ring_rb_min, agg.ring_rb_max)}</td>
        <td style={S.td}>
          <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.3 }}>
            {rangeInt(agg.ring_tax_has, agg.ring_tax_min, agg.ring_tax_max)}/{rangeInt(agg.mtt_tax_has, agg.mtt_tax_min, agg.mtt_tax_max)}%
          </div>
        </td>
        <td style={{ ...S.td, color: clr(agg.ring_pts) }}>{fmt(agg.ring_pts)}</td>
        <td style={{ ...S.td, color: clr(agg.mtt_pts) }}>{fmt(agg.mtt_pts)}</td>
        <td style={{ ...S.td, color: clr(agg.settlement), fontWeight: 700 }}>{fmt(agg.settlement)}</td>
        <td style={S.td}>{agg.active}</td>
        <td style={S.td}><Btns club={club} target={id} disabledReason={disabledReason} /></td>
      </tr>
    );
  };

  const rowsJsx = [];
  for (const g of tree.saGroups) {
    const saKey = `sa:${g.id}`;
    const saExpanded = expanded.has(saKey);
    rowsJsx.push(aggRow(saKey, 0, "SA", "#f59e0b", g.nick, g.id, g.clubs, g.agg, saExpanded));
    if (!saExpanded) continue;
    // Self-leaf first (if SA plays)
    if (g.selfRow && (g.selfRow.total_games || 0) > 0) rowsJsx.push(renderLeaf(g.selfRow, 1));
    // Nested Agents
    for (const ag of g.agents) {
      const agKey = `ag:${g.id}:${ag.id}`;
      const agExpanded = expanded.has(agKey);
      rowsJsx.push(aggRow(agKey, 1, "Agent", "#60a5fa", ag.nick, ag.id, ag.clubs, ag.agg, agExpanded));
      if (!agExpanded) continue;
      if (ag.selfRow && (ag.selfRow.total_games || 0) > 0) rowsJsx.push(renderLeaf(ag.selfRow, 2));
      for (const p of ag.leaves) rowsJsx.push(renderLeaf(p, 2));
    }
    // Direct leaves (players with no agent layer under this SA)
    for (const p of g.leaves) rowsJsx.push(renderLeaf(p, 1));
  }
  // Standalone Agents — top-level siblings of SAs (replaces old orphan bucket)
  for (const ag of tree.standaloneAgents) {
    const agKey = `stag:${ag.id}`;
    const agExpanded = expanded.has(agKey);
    rowsJsx.push(aggRow(agKey, 0, "Agent", "#60a5fa", ag.nick, ag.id, ag.clubs, ag.agg, agExpanded));
    if (!agExpanded) continue;
    if (ag.selfRow && (ag.selfRow.total_games || 0) > 0) rowsJsx.push(renderLeaf(ag.selfRow, 1));
    for (const p of ag.leaves) rowsJsx.push(renderLeaf(p, 1));
  }
  for (const p of tree.orphans) rowsJsx.push(renderLeaf(p, 0));

  return (
    <div style={S.card}>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>{["Nickname", "ID", "Club", "Role", "Ring Win", "MTT Win", "Total Rake", "RB%", "Tax", "Ring Pts", "MTT Pts", "Settlement", "Games", "Export"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>{rowsJsx.length === 0 ? <tr><td style={{ ...S.td, textAlign: "center", color: "#6b7280" }} colSpan={14}>No players match filters.</td></tr> : rowsJsx}</tbody>
        </table>
      </div>
    </div>
  );
}

const S = {
  app: { fontFamily: "'Inter', Arial, sans-serif", minHeight: "100vh", background: "#0a0f1a", color: "#e0e0e0" },
  header: { background: "#111827", padding: "14px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #2ecc71", position: "sticky", top: 0, zIndex: 100 },
  headerTitle: { color: "#fff", margin: 0, fontSize: 20, fontWeight: 700 },
  headerSub: { color: "#6b7280", fontSize: 12, marginTop: 2 },
  logoutBtn: { background: "#e74c3c", color: "#fff", border: "none", padding: "7px 18px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 },
  tabs: { display: "flex", gap: 4, padding: "14px 28px", background: "#111827", borderBottom: "1px solid #1f2937", flexWrap: "wrap", alignItems: "center" },
  tab: { background: "#1f2937", color: "#9ca3af", border: "1px solid #374151", padding: "8px 18px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 500 },
  activeTab: { background: "#2ecc71", color: "#000", border: "1px solid #2ecc71", padding: "8px 18px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 700 },
  content: { padding: "24px 28px" },
  card: { background: "#111827", borderRadius: 10, border: "1px solid #1f2937", padding: 20, marginBottom: 20 },
  statGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16, marginBottom: 24 },
  statCard: { background: "#111827", borderRadius: 10, border: "1px solid #1f2937", padding: "16px 20px" },
  statLabel: { color: "#6b7280", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 },
  statValue: { fontSize: 22, fontWeight: 700 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { background: "#1f2937", color: "#9ca3af", padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, borderBottom: "1px solid #374151", whiteSpace: "nowrap" },
  td: { padding: "9px 14px", borderBottom: "1px solid #1a2232", color: "#d1d5db", whiteSpace: "nowrap" },
  searchBar: { background: "#1f2937", border: "1px solid #374151", color: "#e0e0e0", borderRadius: 6, padding: "8px 14px", fontSize: 13, width: 280, outline: "none" },
  filterRow: { display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" },
  select: { background: "#1f2937", border: "1px solid #374151", color: "#e0e0e0", borderRadius: 6, padding: "8px 14px", fontSize: 13, outline: "none", cursor: "pointer" },
  editBtn: { background: "#3b82f6", color: "#fff", border: "none", padding: "4px 12px", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 600 },
  saveBtn: { background: "#2ecc71", color: "#000", border: "none", padding: "4px 12px", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 700, marginRight: 4 },
  cancelBtn: { background: "#4b5563", color: "#fff", border: "none", padding: "4px 12px", borderRadius: 4, cursor: "pointer", fontSize: 12 },
  smallInput: { width: 56, padding: "3px 8px", borderRadius: 4, border: "1px solid #374151", background: "#0a0f1a", color: "#fff", fontSize: 12 },
  loginContainer: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", background: "#0a0f1a" },
  loginBox: { background: "#111827", padding: "40px 36px", borderRadius: 14, width: 360, border: "1px solid #2ecc71", boxShadow: "0 0 40px #2ecc7122" },
  loginTitle: { color: "#2ecc71", textAlign: "center", marginBottom: 6, fontSize: 24, fontWeight: 700 },
  loginSub: { color: "#6b7280", textAlign: "center", marginBottom: 28, fontSize: 13 },
  loginToggle: { color: "#2ecc71", textAlign: "center", marginTop: 16, fontSize: 13, cursor: "pointer", textDecoration: "underline" },
  input: { width: "100%", padding: "10px 14px", marginBottom: 12, borderRadius: 7, border: "1px solid #374151", background: "#1f2937", color: "#fff", fontSize: 14, boxSizing: "border-box", outline: "none" },
  loginBtn: { width: "100%", padding: "12px", background: "#2ecc71", color: "#000", border: "none", borderRadius: 7, fontSize: 15, fontWeight: 700, cursor: "pointer" },
  error: { color: "#e74c3c", textAlign: "center", marginBottom: 10, fontSize: 13 },
  success: { color: "#2ecc71", textAlign: "center", marginBottom: 10, fontSize: 13 },
  tableWrap: { overflowX: "auto", borderRadius: 8, border: "1px solid #1f2937" },
  badge: (c) => ({ background: c + "22", color: c, border: `1px solid ${c}44`, borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600, display: "inline-block" }),
};

function StatCard({ label, value, color: c, prefix = true }) {
  return (
    <div style={S.statCard}>
      <div style={S.statLabel}>{label}</div>
      <div style={{ ...S.statValue, color: c || (typeof value === "number" ? (value >= 0 ? "#2ecc71" : "#e74c3c") : "#e0e0e0") }}>
        {typeof value === "number" ? fmt(value, prefix) : value}
      </div>
    </div>
  );
}

// Cards tab — one card per club where the player has activity. Aggregates
// come from /player/me's `cards` array (computed server-side); Net is
// derived here. The label is "Net" — not "Profit" — because the math
// (My Settlement − Downline Settlement) is a visual indicator, not a
// financial profit calculation. Color coding: positive green, negative
// red, zero gray. Total Rake stays neutral (it can't be negative).
const _signedColor = (n) => (n > 0 ? "#2ecc71" : n < 0 ? "#e74c3c" : "#6b7280");

function CardsTab({ cards }) {
  if (!cards || cards.length === 0) {
    return (
      <div style={S.card}>
        <p style={{ color: "#6b7280" }}>
          No clubs to display. You'll see cards here once you have activity.
        </p>
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
      {cards.map(card => {
        const net = (card.my_settlement || 0) - (card.downline_settlement || 0);
        return (
          <div key={card.club_name} style={{ ...S.card, marginBottom: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 4 }}>♠️ {card.club_name}</div>
            <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 16 }}>{card.period}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={S.statCard}>
                <div style={S.statLabel}>Total Rake</div>
                <div style={{ ...S.statValue, color: "#f59e0b" }}>{fmt(card.total_rake, false)}</div>
              </div>
              <div style={S.statCard}>
                <div style={S.statLabel}>My Settlement</div>
                <div style={{ ...S.statValue, color: _signedColor(card.my_settlement) }}>{fmt(card.my_settlement)}</div>
              </div>
              <div style={S.statCard}>
                <div style={S.statLabel}>Downline Settlement</div>
                <div style={{ ...S.statValue, color: _signedColor(card.downline_settlement) }}>{fmt(card.downline_settlement)}</div>
              </div>
              <div style={S.statCard}>
                <div style={S.statLabel}>Net</div>
                <div style={{ ...S.statValue, color: _signedColor(net) }}>{fmt(net)}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Login({ onAdminLogin, onClubAdminLogin, onPlayerLogin }) {
  const [mode, setMode] = useState("player");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const form = new FormData();
      form.append("username", mode === "superadmin" ? "admin" : username);
      form.append("password", password);
      if (mode === "superadmin") {
        const res = await axios.post(`${API}/token`, form);
        localStorage.setItem("token", res.data.access_token);
        localStorage.setItem("userType", "superadmin");
        onAdminLogin(res.data.access_token);
      } else if (mode === "clubadmin") {
        const res = await axios.post(`${API}/admin/token`, form);
        localStorage.setItem("token", res.data.access_token);
        localStorage.setItem("userType", "clubadmin");
        localStorage.setItem("displayName", res.data.display_name || username);
        // Phase 2: cache the per-link player JWTs returned by the dual-JWT
        // login. Default view-mode is always "admin" on a fresh login —
        // the user opts into player view via the banner toggle.
        const linkedTokens = res.data.linked_player_tokens || {};
        if (Object.keys(linkedTokens).length > 0) {
          localStorage.setItem(LS_LINKED_TOKENS, JSON.stringify(linkedTokens));
        } else {
          localStorage.removeItem(LS_LINKED_TOKENS);
        }
        localStorage.setItem(LS_VIEW_MODE, "admin");
        onClubAdminLogin(res.data.access_token, res.data.display_name || username);
      } else {
        const res = await axios.post(`${API}/player/token`, form);
        localStorage.setItem("token", res.data.access_token);
        localStorage.setItem("userType", "player");
        localStorage.setItem("playerId", username);
        onPlayerLogin(res.data.access_token, res.data.must_change_password, username);
      }
    } catch { setError("Invalid credentials"); }
    setLoading(false);
  };
  const modes = ["player", "clubadmin", "superadmin"];
  const modeLabels = { player: "Player Portal", clubadmin: "Club Admin", superadmin: "Super Admin" };
  return (
    <div style={S.loginContainer}>
      <div style={S.loginBox}>
        <div style={{ fontSize: 40, textAlign: "center", marginBottom: 12 }}>♠️</div>
        <h1 style={S.loginTitle}>Accounto Bot</h1>
        <div style={{ display: "flex", gap: 6, marginBottom: 20, justifyContent: "center" }}>
          {modes.map(m => <button key={m} type="button" style={{ ...S.tab, ...(mode === m ? S.activeTab : {}), padding: "6px 12px", fontSize: 12 }} onClick={() => { setMode(m); setError(""); setUsername(""); setPassword(""); }}>{modeLabels[m]}</button>)}
        </div>
        <form onSubmit={handleLogin}>
          {(mode === "player" || mode === "clubadmin") && <input style={S.input} value={username} onChange={e => setUsername(e.target.value)} placeholder={mode === "player" ? "Player ID (e.g. 1234-5678)" : "Username"} />}
          <input style={S.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" />
          {error && <p style={S.error}>{error}</p>}
          <button style={S.loginBtn} type="submit" disabled={loading}>{loading ? "Signing in..." : "Sign In"}</button>
        </form>
      </div>
    </div>
  );
}

function ChangePassword({ token, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try { await axios.post(`${API}/player/change-password`, { new_password: password }, { headers: getAuthHeaders() }); onDone(); }
    catch { setError("Failed to change password"); }
    setLoading(false);
  };
  return (
    <div style={S.loginContainer}>
      <div style={S.loginBox}>
        <div style={{ fontSize: 40, textAlign: "center", marginBottom: 12 }}>🔐</div>
        <h1 style={{ ...S.loginTitle, fontSize: 20 }}>Set Your Password</h1>
        <p style={S.loginSub}>Please set a new password before continuing</p>
        <form onSubmit={handleSubmit}>
          <input style={S.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="New password" />
          <input style={S.input} type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirm password" />
          {error && <p style={S.error}>{error}</p>}
          <button style={S.loginBtn} type="submit" disabled={loading}>{loading ? "Saving..." : "Set Password"}</button>
        </form>
      </div>
    </div>
  );
}

function PlayerAccounting({ headers, playerId, period }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const url = period && period !== "current" ? `${API}/player/internal-accounting?period=${encodeURIComponent(period)}` : `${API}/player/internal-accounting`;
    axios.get(url, { headers }).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [headers, period]);
  if (loading) return <p style={{ color: "#6b7280" }}>Loading...</p>;
  if (data.length === 0) return <div style={S.card}><p style={{ color: "#6b7280" }}>No internal accounting data. Set deals for your downstream players first.</p></div>;
  return (
    <div>
      {data.map(club => (
        <div key={club.club_name} style={S.card}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 4 }}>♠️ {club.club_name}</div>
          <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 16 }}>{club.period}</div>
          <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={S.statCard}><div style={S.statLabel}>I Get</div><div style={{ ...S.statValue, color: clr(club.total_i_get) }}>{fmt(club.total_i_get)}</div></div>
            <div style={S.statCard}><div style={S.statLabel}>They Get</div><div style={{ ...S.statValue, color: "#e74c3c" }}>{fmt(club.total_they_get)}</div></div>
            <div style={S.statCard}><div style={S.statLabel}>My Profit</div><div style={{ ...S.statValue, color: clr(club.total_profit) }}>{fmt(club.total_profit)}</div></div>
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>{["Player", "ID", "Ring Win", "MTT Win", "Rake", "RB%", "Tax%", "Mode", "I Get", "They Get", "My Profit", "Export"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {club.players.map((p, i) => (
                  <tr key={p.player_id} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                    <td style={S.td}><strong style={{ color: "#e0e0e0" }}>{p.nickname}</strong></td>
                    <td style={{ ...S.td, fontFamily: "monospace", color: "#6b7280" }}>{p.player_id}</td>
                    <td style={{ ...S.td, color: clr(p.ring_winnings) }}>{fmt(p.ring_winnings)}</td>
                    <td style={{ ...S.td, color: clr(p.mtt_winnings) }}>{fmt(p.mtt_winnings)}</td>
                    <td style={{ ...S.td, color: "#f59e0b" }}>{fmt(p.total_rake, false)}</td>
                    <td style={S.td}><span style={{ color: "#2ecc71" }}>{p.deal.ring_rb}%</span></td>
                    <td style={S.td}><span style={{ color: "#f59e0b" }}>{p.deal.ring_tax}%</span></td>
                    <td style={{ ...S.td, color: "#9ca3af", fontSize: 12 }}>{p.tax_mode || "simple"}</td>
                    <td style={{ ...S.td, color: clr(p.i_get) }}>{fmt(p.i_get)}</td>
                    <td style={{ ...S.td, color: clr(p.they_get) }}>{fmt(p.they_get)}</td>
                    <td style={{ ...S.td, color: clr(p.profit), fontWeight: 700 }}>{fmt(p.profit)}</td>
                    <td style={S.td}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button style={{ ...S.editBtn, padding: "3px 8px", fontSize: 11 }}
                                onClick={(e) => { e.stopPropagation(); downloadExport({ kind: "csv", club: club.club_name, target: p.player_id, period, headers }); }}>CSV</button>
                        <button style={{ ...S.saveBtn, padding: "3px 8px", fontSize: 11, marginRight: 0 }}
                                onClick={(e) => { e.stopPropagation(); downloadExport({ kind: "pdf", club: club.club_name, target: p.player_id, period, headers }); }}>PDF</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function PlayerReferrals({ headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/player/referrals`, { headers }).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [headers]);
  if (loading) return <p style={{ color: "#6b7280" }}>Loading...</p>;
  if (!data || data.weekly.length === 0) return <div style={S.card}><p style={{ color: "#6b7280" }}>No referral earnings found.</p></div>;
  return (
    <div>
      <div style={S.statGrid}>
        <div style={S.statCard}><div style={S.statLabel}>Total Referral Earnings</div><div style={{ ...S.statValue, color: "#2ecc71" }}>{fmt(data.total)}</div></div>
        <div style={S.statCard}><div style={S.statLabel}>Active Referrals</div><div style={{ ...S.statValue, color: "#60a5fa" }}>{data.referrals.length}</div></div>
      </div>
      {data.weekly.map(week => (
        <div key={week.period} style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#e0e0e0" }}>{week.period}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#2ecc71" }}>{fmt(week.total)}</div>
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>{["Player", "ID", "Club", "Pct", "Their Rake", "Your Earnings"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {week.rows.map((r, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                    <td style={S.td}><strong style={{ color: "#e0e0e0" }}>{r.target_nick}</strong></td>
                    <td style={{ ...S.td, fontFamily: "monospace", color: "#6b7280" }}>{r.target_id}</td>
                    <td style={S.td}><span style={S.badge("#818cf8")}>{r.club}</span></td>
                    <td style={S.td}><span style={{ color: "#60a5fa" }}>{r.pct}%</span></td>
                    <td style={{ ...S.td, color: "#f59e0b" }}>{fmt(r.target_rake, false)}</td>
                    <td style={{ ...S.td, color: "#2ecc71", fontWeight: 700 }}>{fmt(r.earnings)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function AddDealForm({ headers, playerId, onSaved }) {
  const [clubs, setClubs] = useState([]);
  const [form, setForm] = useState({ club: "", target_id: "", ring_rb: "", mtt_rb: "", ring_tax: "", mtt_tax: "", tax_mode: "simple" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    axios.get(`${API}/player/me`, { headers }).then(r => {
      const clubNames = (r.data.clubs || []).map(c => c.club_name);
      setClubs(clubNames);
      if (clubNames.length > 0) setForm(f => ({ ...f, club: clubNames[0] }));
    }).catch(() => {});
  }, [headers]);
  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const key = `${form.club}:${playerId}:${form.target_id.trim()}`;
      await axios.post(`${API}/player/deals`, null, { headers, params: { key, ring_rb: parseFloat(form.ring_rb), mtt_rb: parseFloat(form.mtt_rb), ring_tax: parseFloat(form.ring_tax), mtt_tax: parseFloat(form.mtt_tax), tax_mode: form.tax_mode }});
      setForm(f => ({ ...f, target_id: "", ring_rb: "", mtt_rb: "", ring_tax: "", mtt_tax: "", tax_mode: "simple" }));
      onSaved();
    } catch (err) { setError(err.response?.data?.detail || "Failed to add deal"); }
    setLoading(false);
  };
  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
      <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>CLUB</div><select style={S.select} value={form.club} onChange={e => setForm({ ...form, club: e.target.value })}>{clubs.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
      <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>PLAYER ID</div><input style={{ ...S.searchBar, width: 120 }} value={form.target_id} onChange={e => setForm({ ...form, target_id: e.target.value })} placeholder="1234-5678" required /></div>
      <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>RING RB%</div><input style={{ ...S.searchBar, width: 70 }} value={form.ring_rb} onChange={e => setForm({ ...form, ring_rb: e.target.value })} placeholder="75" required /></div>
      <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>MTT RB%</div><input style={{ ...S.searchBar, width: 70 }} value={form.mtt_rb} onChange={e => setForm({ ...form, mtt_rb: e.target.value })} placeholder="75" required /></div>
      <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>RING TAX%</div><input style={{ ...S.searchBar, width: 70 }} value={form.ring_tax} onChange={e => setForm({ ...form, ring_tax: e.target.value })} placeholder="15" required /></div>
      <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>MTT TAX%</div><input style={{ ...S.searchBar, width: 70 }} value={form.mtt_tax} onChange={e => setForm({ ...form, mtt_tax: e.target.value })} placeholder="5" required /></div>
      <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>TAX MODE</div><select style={S.select} value={form.tax_mode} onChange={e => setForm({ ...form, tax_mode: e.target.value })}><option value="simple">simple</option><option value="complex">complex</option></select></div>
      <button style={{ ...S.saveBtn, padding: "8px 20px" }} type="submit" disabled={loading}>{loading ? "Adding..." : "Add Deal"}</button>
      {error && <p style={{ ...S.error, width: "100%", textAlign: "left" }}>{error}</p>}
    </form>
  );
}

function GroupPicker({ visibleMembers, initialSelected = [], onChange, filter, onFilterChange }) {
  const initialSet = useMemo(() => new Set(initialSelected), [initialSelected]);
  const [selected, setSelected] = useState(initialSet);

  useEffect(() => { setSelected(new Set(initialSelected)); }, [initialSelected]);

  const toggle = (pid) => {
    const next = new Set(selected);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    setSelected(next);
    if (onChange) onChange(Array.from(next));
  };

  return (
    <div>
      <input
        style={{ ...S.input, marginBottom: 10 }}
        placeholder="Filter by nickname or ID…"
        value={filter || ""}
        onChange={e => onFilterChange && onFilterChange(e.target.value)}
      />
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
        {selected.size} selected · {visibleMembers.length} shown
      </div>
      <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #1f2937", borderRadius: 6 }}>
        {visibleMembers.length === 0 ? (
          <div style={{ padding: 14, color: "#6b7280", fontSize: 13 }}>No matches.</div>
        ) : visibleMembers.map((m, i) => (
          <label
            key={m.player_id}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 12px", cursor: "pointer",
              background: i % 2 === 0 ? "transparent" : "#0d1520",
              borderBottom: "1px solid #1a2232",
            }}
          >
            <input
              type="checkbox"
              checked={selected.has(m.player_id)}
              onChange={() => toggle(m.player_id)}
            />
            <div style={{ flex: 1 }}>
              <div style={{ color: "#e0e0e0", fontWeight: 600 }}>{m.nickname}</div>
              <div style={{ color: "#6b7280", fontSize: 11, fontFamily: "monospace" }}>
                {m.player_id} · {m.role}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function GroupsTab({ headers, token }) {
  const [groups, setGroups] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [visibleMembers, setVisibleMembers] = useState([]);
  const [pickerFilter, setPickerFilter] = useState("");
  const [debouncedPickerFilter, setDebouncedPickerFilter] = useState("");
  const [modal, setModal] = useState(null);  // null | 'new' | {type:'edit', id}
  const [form, setForm] = useState({ name: "", members: [] });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("current");
  const [hideProfit, setHideProfit] = useState(false);

  const refreshGroups = () => axios.get(`${API}/groups`, { headers }).then(r => setGroups(r.data));

  useEffect(() => {
    refreshGroups().catch(() => {});
    axios.get(`${API}/history/periods`, { headers }).then(r => setPeriods(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce the picker filter 300ms before hitting the server. A Club
  // Admin with thousands of players shouldn't burn a round-trip on every
  // keystroke; the list is paged through server-side ?q= filter.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedPickerFilter(pickerFilter), 300);
    return () => clearTimeout(t);
  }, [pickerFilter]);

  useEffect(() => {
    const q = debouncedPickerFilter.trim();
    const url = q ? `${API}/groups/visible-members?q=${encodeURIComponent(q)}`
                  : `${API}/groups/visible-members`;
    axios.get(url, { headers }).then(r => setVisibleMembers(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPickerFilter]);

  useEffect(() => {
    if (!selectedId) { setStats(null); return; }
    setStatsLoading(true);
    const url = selectedPeriod && selectedPeriod !== "current"
      ? `${API}/groups/${selectedId}/stats?period=${encodeURIComponent(selectedPeriod)}`
      : `${API}/groups/${selectedId}/stats`;
    axios.get(url, { headers }).then(r => setStats(r.data)).finally(() => setStatsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selectedPeriod]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 3000); };
  const flashErr = (m) => { setErr(m); setTimeout(() => setErr(""), 5000); };

  const openNew = () => { setForm({ name: "", members: [] }); setPickerFilter(""); setModal("new"); setErr(""); };
  const openEdit = (g) => { setForm({ name: g.name, members: [...g.members] }); setPickerFilter(""); setModal({ type: "edit", id: g.id }); setErr(""); };

  const saveGroup = async () => {
    if (!form.name.trim()) { setErr("Name is required"); return; }
    try {
      if (modal === "new") {
        const r = await axios.post(`${API}/groups`, { name: form.name.trim(), members: form.members }, { headers });
        flash("Group created");
        await refreshGroups();
        setSelectedId(r.data.id);
      } else {
        await axios.put(`${API}/groups/${modal.id}`, { name: form.name.trim(), members: form.members }, { headers });
        flash("Group updated");
        await refreshGroups();
      }
      setModal(null);
    } catch (e) {
      flashErr(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm("Delete this group?")) return;
    try {
      await axios.delete(`${API}/groups/${id}`, { headers });
      if (selectedId === id) setSelectedId(null);
      flash("Group deleted");
      await refreshGroups();
    } catch (e) {
      flashErr(e?.response?.data?.detail || "Delete failed");
    }
  };

  const downloadFile = (url, filename) => {
    fetch(url, { headers: getAuthHeaders() })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename; a.click();
      });
  };

  const selectedGroup = groups.find(g => g.id === selectedId);
  const pSuffix = (selectedPeriod && selectedPeriod !== "current") ? `?period=${encodeURIComponent(selectedPeriod)}` : "";

  return (
    <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 20 }}>
      {/* Left pane: group list */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#e0e0e0" }}>My Groups</div>
          <button style={S.saveBtn} onClick={openNew}>+ New</button>
        </div>
        {msg && <p style={S.success}>{msg}</p>}
        {err && <p style={{ color: "#f87171", fontSize: 12, marginBottom: 8 }}>{err}</p>}
        {groups.length === 0 ? (
          <p style={{ color: "#6b7280", fontSize: 13 }}>No groups yet. Click "New" to create one.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {groups.map(g => (
              <div
                key={g.id}
                style={{
                  padding: "10px 12px", borderRadius: 6,
                  background: selectedId === g.id ? "#1a3a2a" : "#1f2937",
                  border: `1px solid ${selectedId === g.id ? "#2ecc71" : "#374151"}`,
                  cursor: "pointer",
                }}
                onClick={() => setSelectedId(g.id)}
              >
                <div style={{ fontWeight: 600, color: "#e0e0e0", marginBottom: 3 }}>{g.name}</div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>{g.member_count} member{g.member_count === 1 ? "" : "s"}</div>
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <button style={{ ...S.editBtn, padding: "3px 10px", fontSize: 11 }} onClick={(e) => { e.stopPropagation(); openEdit(g); }}>Edit</button>
                  <button style={{ ...S.cancelBtn, padding: "3px 10px", fontSize: 11, background: "#7f1d1d" }} onClick={(e) => { e.stopPropagation(); deleteGroup(g.id); }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right pane: selected group's stats */}
      <div>
        {!selectedGroup ? (
          <div style={S.card}>
            <p style={{ color: "#6b7280" }}>Select a group on the left to view its stats.</p>
          </div>
        ) : (
          <div style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0" }}>👥 {selectedGroup.name}</div>
                <div style={{ color: "#6b7280", fontSize: 12 }}>
                  {selectedGroup.member_count} member{selectedGroup.member_count === 1 ? "" : "s"}
                  {stats?.aggregate?.unavailable_count ? ` · ${stats.aggregate.unavailable_count} unavailable` : ""}
                  {stats?.period ? ` · ${stats.period}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <select style={S.select} value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}>
                  <option value="current">Current Week</option>
                  {periods.map(p => <option key={p.period} value={p.period}>{p.period}</option>)}
                </select>
                <label style={{ color: "#9ca3af", fontSize: 12, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", userSelect: "none" }}
                       title="Drops 'I Get' and 'Profit' columns from the export — safe to forward to the group settler.">
                  <input type="checkbox" checked={hideProfit} onChange={e => setHideProfit(e.target.checked)} />
                  Hide my profit
                </label>
                {(() => {
                  const sep = pSuffix ? "&" : "?";
                  const hp = hideProfit ? `${sep}hide_profit=true` : "";
                  return (<>
                    <button style={S.editBtn} onClick={() => downloadFile(`${API}/groups/${selectedId}/export.csv${pSuffix}${hp}`, `group_${selectedGroup.name}.csv`)}>📥 CSV</button>
                    <button style={S.saveBtn} onClick={() => downloadFile(`${API}/groups/${selectedId}/export.pdf${pSuffix}${hp}`, `group_${selectedGroup.name}.pdf`)}>📄 PDF</button>
                  </>);
                })()}
              </div>
            </div>
            {statsLoading ? (
              <p style={{ color: "#6b7280" }}>Loading stats…</p>
            ) : !stats ? (
              <p style={{ color: "#6b7280" }}>No data.</p>
            ) : (
              <>
                <div style={{ ...S.statGrid, marginBottom: 16 }}>
                  <StatCard label="I Get (total)" value={stats.aggregate.total_i_get} />
                  <StatCard label="Total Settlement" value={stats.aggregate.total_settlement} color="#818cf8" />
                  <StatCard label="Profit" value={stats.aggregate.total_profit} />
                  <StatCard label="Total Rake" value={stats.aggregate.total_rake} color="#f59e0b" prefix={false} />
                  <StatCard label="Total Games" value={stats.aggregate.total_games.toLocaleString()} color="#60a5fa" prefix={false} />
                </div>
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead>
                      <tr>{["Nickname", "ID", "Role", "Ring Win", "MTT Win", "Total Rake", "Games", "I Get", "Settlement", "Profit"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {stats.members.map((m, i) => (
                        <tr key={m.player_id} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520", opacity: m.unavailable ? 0.55 : 1 }}>
                          <td style={S.td}>
                            <strong style={{ color: "#e0e0e0" }}>{m.nickname}</strong>
                            {m.unavailable && <span style={{ ...S.badge("#f87171"), marginLeft: 6 }}>unavailable</span>}
                          </td>
                          <td style={{ ...S.td, fontFamily: "monospace", color: "#6b7280" }}>{m.player_id}</td>
                          <td style={S.td}>{m.unavailable ? <span style={{ color: "#6b7280" }}>—</span> : <span style={S.badge(m.role === "Super Agent" ? "#f59e0b" : m.role === "Agent" ? "#60a5fa" : "#6b7280")}>{m.role}</span>}</td>
                          <td style={{ ...S.td, color: m.unavailable ? "#6b7280" : clr(m.ring_winnings) }}>{m.unavailable ? "—" : fmt(m.ring_winnings)}</td>
                          <td style={{ ...S.td, color: m.unavailable ? "#6b7280" : clr(m.mtt_winnings) }}>{m.unavailable ? "—" : fmt(m.mtt_winnings)}</td>
                          <td style={{ ...S.td, color: m.unavailable ? "#6b7280" : "#f59e0b" }}>{m.unavailable ? "—" : fmt(m.total_rake, false)}</td>
                          <td style={S.td}>{m.unavailable ? "—" : m.total_games}</td>
                          <td style={{ ...S.td, color: m.unavailable ? "#6b7280" : clr(m.i_get), fontWeight: 600 }}>{m.unavailable ? "—" : fmt(m.i_get)}</td>
                          <td style={{ ...S.td, color: m.unavailable ? "#6b7280" : clr(m.total_settlement) }}>{m.unavailable ? "—" : fmt(m.total_settlement)}</td>
                          <td style={{ ...S.td, color: m.unavailable ? "#6b7280" : clr(m.profit), fontWeight: 700 }}>{m.unavailable ? "—" : fmt(m.profit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* New/Edit modal */}
      {modal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setModal(null)}>
          <div style={{ ...S.card, width: 520, maxHeight: "85vh", overflowY: "auto", marginBottom: 0 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 14 }}>
              {modal === "new" ? "New Group" : "Edit Group"}
            </div>
            <label style={{ fontSize: 12, color: "#9ca3af" }}>Name</label>
            <input style={S.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Weekly VIPs" />
            <label style={{ fontSize: 12, color: "#9ca3af" }}>Members</label>
            <GroupPicker
              visibleMembers={visibleMembers}
              initialSelected={form.members}
              onChange={(members) => setForm(f => ({ ...f, members }))}
              filter={pickerFilter}
              onFilterChange={setPickerFilter}
            />
            {err && <p style={{ color: "#f87171", fontSize: 12, marginTop: 10 }}>{err}</p>}
            <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
              <button style={S.cancelBtn} onClick={() => setModal(null)}>Cancel</button>
              <button style={S.saveBtn} onClick={saveGroup}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Player Notes ────────────────────────────────────────────────────────
// Single-select picker reuses /groups/visible-members?q= — same scope-
// filtered, debounced endpoint the Groups picker uses, but rendered as
// click-to-select instead of multi-checkbox.
function PlayerSinglePicker({ headers, value, onChange }) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(!value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!showSearch) return;
    setSearching(true);
    const url = debounced
      ? `${API}/groups/visible-members?q=${encodeURIComponent(debounced)}`
      : `${API}/groups/visible-members`;
    axios.get(url, { headers })
      .then(r => setResults(r.data || []))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, [debounced, showSearch, headers]);

  if (!showSearch && value) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 16, color: "#e0e0e0" }}>
          👤 <strong>{value.nickname}</strong>{" "}
          <span style={{ color: "#6b7280", fontFamily: "monospace" }}>({value.player_id})</span>
        </span>
        <button style={S.editBtn} onClick={() => setShowSearch(true)}>Change</button>
      </div>
    );
  }

  return (
    <div>
      <input
        style={{ ...S.searchBar, width: "100%" }}
        placeholder="Search by nickname or player ID…"
        value={query}
        onChange={e => setQuery(e.target.value)}
        autoFocus
      />
      <div style={{
        marginTop: 8, maxHeight: 240, overflowY: "auto",
        border: "1px solid #2a3242", borderRadius: 4,
      }}>
        {searching && <div style={{ padding: 8, color: "#6b7280" }}>Searching…</div>}
        {!searching && results.length === 0 && (
          <div style={{ padding: 8, color: "#6b7280" }}>No matches.</div>
        )}
        {!searching && results.map(r => (
          <div
            key={r.player_id}
            style={{ padding: "6px 10px", cursor: "pointer",
                     borderBottom: "1px solid #1a2030" }}
            onClick={() => { onChange({ player_id: r.player_id, nickname: r.nickname }); setShowSearch(false); setQuery(""); }}
          >
            <strong style={{ color: "#e0e0e0" }}>{r.nickname || r.player_id}</strong>
            <span style={{ color: "#6b7280", marginLeft: 8, fontFamily: "monospace" }}>({r.player_id})</span>
            {r.role && r.role !== "Player" && (
              <span style={{ color: "#9ca3af", fontSize: 11, marginLeft: 8 }}>· {r.role}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function NotesTab({ headers }) {
  const [view, setView] = useState("player"); // 'player' or 'mine'
  const [picked, setPicked] = useState(null);  // {player_id, nickname} | null
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [newBody, setNewBody] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");

  const refresh = () => {
    setErr("");
    if (view === "mine") {
      setLoading(true);
      axios.get(`${API}/notes/mine`, { headers })
        .then(r => setNotes(r.data || []))
        .catch(() => setNotes([]))
        .finally(() => setLoading(false));
    } else if (picked) {
      setLoading(true);
      axios.get(`${API}/notes/player/${encodeURIComponent(picked.player_id)}`, { headers })
        .then(r => setNotes(r.data || []))
        .catch((e) => { setNotes([]); setErr(e.response?.data?.detail || "Failed to load notes"); })
        .finally(() => setLoading(false));
    } else {
      setNotes([]);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [view, picked]);

  const save = async () => {
    if (!picked) return;
    const body = newBody.trim();
    if (!body || body.length > 4000) return;
    setErr(""); setMsg("");
    try {
      await axios.post(`${API}/notes/player/${encodeURIComponent(picked.player_id)}`, { body }, { headers });
      setNewBody("");
      setMsg("Note saved.");
      setTimeout(() => setMsg(""), 2500);
      refresh();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save");
    }
  };

  const startEdit = (n) => { setEditingId(n.id); setEditBody(n.body); setErr(""); };
  const cancelEdit = () => { setEditingId(null); setEditBody(""); };
  const saveEdit = async () => {
    const body = editBody.trim();
    if (!body || body.length > 4000) return;
    try {
      await axios.put(`${API}/notes/${editingId}`, { body }, { headers });
      cancelEdit();
      setMsg("Note updated.");
      setTimeout(() => setMsg(""), 2500);
      refresh();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to update");
    }
  };
  const del = async (n) => {
    if (!window.confirm("Delete this note? Cannot be undone.")) return;
    try {
      await axios.delete(`${API}/notes/${n.id}`, { headers });
      setMsg("Note deleted.");
      setTimeout(() => setMsg(""), 2500);
      refresh();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to delete");
    }
  };

  // Esc cancels edit
  useEffect(() => {
    if (editingId === null) return;
    const onKey = (e) => { if (e.key === "Escape") cancelEdit(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editingId]);

  const fmtTs = (s) => (s || "").slice(0, 16).replace("T", " ");

  return (
    <div>
      {/* View toggle + picker */}
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button style={view === "player" ? S.activeTab : S.tab}
                  onClick={() => setView("player")}>By player</button>
          <button style={view === "mine" ? S.activeTab : S.tab}
                  onClick={() => setView("mine")}>My notes</button>
        </div>
        {view === "player" ? (
          <>
            <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 8 }}>
              Pick a player to view or add notes.
            </div>
            <PlayerSinglePicker headers={headers} value={picked} onChange={setPicked} />
          </>
        ) : (
          <div style={{ color: "#6b7280", fontSize: 13 }}>
            Notes you've authored. Includes notes for players outside your current scope —
            useful for cleaning up after a downline change.
          </div>
        )}
      </div>

      {/* Notes list */}
      {(view === "mine" || picked) && (
        <div style={S.card}>
          {loading && <p style={{ color: "#6b7280" }}>Loading…</p>}
          {!loading && notes.length === 0 && (
            <p style={{ color: "#6b7280", fontStyle: "italic" }}>No notes yet.</p>
          )}
          {!loading && notes.map(n => (
            <div key={n.id} style={{ borderBottom: "1px solid #1a2030", padding: "12px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div style={{ color: "#9ca3af", fontSize: 12 }}>
                  <strong style={{ color: "#e0e0e0" }}>{n.author_name}</strong>
                  {" · "}{fmtTs(n.created_at)}
                  {n.is_edited && <span style={{ marginLeft: 6, fontStyle: "italic" }}>(edited)</span>}
                  {view === "mine" && (
                    <span style={{ marginLeft: 8, color: "#60a5fa", fontFamily: "monospace" }}>
                      → {n.player_id}
                    </span>
                  )}
                </div>
                {n.is_mine && editingId !== n.id && (
                  <div>
                    <button style={{ ...S.editBtn, marginRight: 6 }} onClick={() => startEdit(n)}>Edit</button>
                    <button style={{ ...S.cancelBtn, background: "#1f2937", color: "#e74c3c", border: "1px solid #e74c3c66" }} onClick={() => del(n)}>Delete</button>
                  </div>
                )}
              </div>
              {editingId === n.id ? (
                <div style={{ marginTop: 8 }}>
                  <textarea
                    style={{ ...S.searchBar, width: "100%", minHeight: 80, fontFamily: "inherit", whiteSpace: "pre-wrap" }}
                    value={editBody}
                    onChange={e => setEditBody(e.target.value)}
                    maxLength={4001}
                    autoFocus
                  />
                  <div style={{ color: "#6b7280", fontSize: 11, margin: "4px 0 8px" }}>
                    {editBody.length}/4000 · Esc to cancel
                  </div>
                  <button style={S.saveBtn} onClick={saveEdit}
                          disabled={!editBody.trim() || editBody.length > 4000}>Save</button>
                  <button style={S.cancelBtn} onClick={cancelEdit}>Cancel</button>
                </div>
              ) : (
                <div style={{ marginTop: 6, color: "#e0e0e0", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {n.body}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new (player view only, requires picked) */}
      {view === "player" && picked && (
        <div style={{ ...S.card, marginTop: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#e0e0e0", marginBottom: 8 }}>
            Add note for {picked.nickname}
          </div>
          <textarea
            style={{ ...S.searchBar, width: "100%", minHeight: 80, fontFamily: "inherit", whiteSpace: "pre-wrap" }}
            value={newBody}
            onChange={e => setNewBody(e.target.value)}
            placeholder="Plain text. e.g. 'Contact via WhatsApp' or 'Owed $200 from Tuesday'"
            maxLength={4001}
          />
          <div style={{ color: "#6b7280", fontSize: 11, margin: "4px 0 8px" }}>
            {newBody.length}/4000
          </div>
          <button style={S.saveBtn} onClick={save}
                  disabled={!newBody.trim() || newBody.length > 4000}>Save note</button>
        </div>
      )}

      {msg && <div style={{ color: "#2ecc71", fontSize: 13, marginTop: 10 }}>✓ {msg}</div>}
      {err && <div style={{ color: "#e74c3c", fontSize: 13, marginTop: 10 }}>✗ {err}</div>}
    </div>
  );
}

function PlayerDashboard({ token, playerId, onLogout, viaLink = false }) {
  // viaLink: true when this dashboard is rendered for a Club Admin viewing a
  // linked player (Phase 2 dual-JWT). In that mode the "deals", "groups",
  // and "notes" tabs are hidden — Phase 2 v1 ships read-only essentials only;
  // Groups + Notes interactions land in Phase 3. The locked player_auth row
  // also has no usable password, so any rate change attempted from this view
  // would write through to a credential-less account; the banner copy
  // directs the user to log in as the player directly for those flows.
  const [tab, setTab] = useState("cards");
  // Defensive: if the component is reused with stale tab state (e.g. saved
  // selection of "groups"/"notes"/"deals" from before a viaLink toggle),
  // snap back to "stats" so we never flash restricted content. Today the
  // parent gives PlayerDashboard a key tied to viewMode so it always
  // remounts on toggle and `tab` is initialized to "cards" — but this
  // guard makes the invariant explicit and survives any future change to
  // the parent's mount strategy.
  useEffect(() => {
    if (viaLink && (tab === "groups" || tab === "notes" || tab === "deals")) {
      setTab("stats");
    }
  }, [viaLink, tab]);
  const [myData, setMyData] = useState(null);
  const [deals, setDeals] = useState({});
  const [loading, setLoading] = useState(true);
  const [editKey, setEditKey] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [saveMsg, setSaveMsg] = useState("");
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("current");

  const headers = useMemo(() => getAuthHeaders(), []);

  useEffect(() => {
    axios.get(`${API}/history/periods`, { headers }).then(r => setPeriods(r.data)).catch(() => {});
  }, [headers]);

  useEffect(() => {
    setLoading(true);
    axios.get(selectedPeriod && selectedPeriod !== 'current' ? `${API}/player/me?period=${encodeURIComponent(selectedPeriod)}` : `${API}/player/me`, { headers }).then(r => { setMyData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [headers, selectedPeriod]);

  useEffect(() => {
    if (tab === "deals") axios.get(`${API}/player/deals`, { headers }).then(r => setDeals(r.data));
  }, [tab, headers]);

  const saveDeal = async (key) => {
    await axios.put(`${API}/player/deals/${encodeURIComponent(key)}`, null, { headers, params: { ring_rb: parseFloat(editVals.ring_rb), mtt_rb: parseFloat(editVals.mtt_rb), ring_tax: parseFloat(editVals.ring_tax), mtt_tax: parseFloat(editVals.mtt_tax), tax_mode: editVals.tax_mode || "simple" } });
    setEditKey(null); setSaveMsg("Deal saved!"); setTimeout(() => setSaveMsg(""), 3000);
    const res = await axios.get(`${API}/player/deals`, { headers }); setDeals(res.data);
  };

  const downloadFile = (url, filename) => {
    fetch(url, { headers: getAuthHeaders() }).then(r => r.blob()).then(blob => {
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
    });
  };

  if (loading) return <div style={{ ...S.app, display: "flex", alignItems: "center", justifyContent: "center" }}><p style={{ color: "#6b7280" }}>Loading...</p></div>;

  const allClubs = myData?.clubs || [];
  const totals = {
    total_points: allClubs.reduce((s, c) => s + (c.me?.total_points || 0) + c.visible.reduce((vs, v) => vs + (v.total_points || 0), 0), 0),
    total_rake: allClubs.reduce((s, c) => s + (c.me?.total_rake || 0) + c.visible.reduce((vs, v) => vs + (v.total_rake || 0), 0), 0),
    total_games: allClubs.reduce((s, c) => s + (c.me?.total_games || 0) + c.visible.reduce((vs, v) => vs + (v.total_games || 0), 0), 0),
  };
  const referralEarnings = myData?.referral_earnings || 0;

  return (
    <div style={S.app}>
      <div style={S.header}>
        <div><h1 style={S.headerTitle}>♠️ Player Portal</h1><div style={S.headerSub}>ID: {playerId}</div></div>
        <button style={S.logoutBtn} onClick={onLogout}>Logout</button>
      </div>
      <div style={S.tabs}>
        {(viaLink
          ? ["cards", "stats", "accounting", "referrals", "charts"]
          : ["cards", "stats", "accounting", "groups", "notes", "deals", "referrals", "charts"]
        ).map(t => (
          <button key={t} style={tab === t ? S.activeTab : S.tab} onClick={() => setTab(t)}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <select style={S.select} value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}>
            <option value="current">Current Week</option>
            {periods.map(p => <option key={p.period} value={p.period}>{p.period}</option>)}
          </select>
          <button style={{ ...S.editBtn, padding: "7px 14px" }} onClick={() => downloadFile(`${API}/player/download/csv`, "settlement.csv")}>📥 CSV</button>
          <button style={{ ...S.saveBtn, padding: "7px 14px", marginRight: 0 }} onClick={() => downloadFile(`${API}/player/download/pdf`, "settlement.pdf")}>📄 PDF</button>
        </div>
      </div>
      <div style={S.content}>
        {tab === "cards" && <CardsTab cards={myData?.cards || []} />}
        {tab === "accounting" && <PlayerAccounting headers={headers} playerId={playerId} period={selectedPeriod} />}
        {tab === "groups" && <GroupsTab headers={headers} token={token} />}
        {tab === "notes" && <NotesTab headers={headers} />}
        {tab === "referrals" && <PlayerReferrals headers={headers} />}
        {tab === "charts" && <ChartsTab headers={headers} isPlayer={true} playerId={playerId} />}

        {tab === "stats" && (
          <div>
            <div style={S.statGrid}>
              <StatCard label="Settlement" value={totals.total_points} />
              <StatCard label="Referral Earnings" value={referralEarnings} color="#818cf8" />
              <StatCard label="Total (incl. Referrals)" value={totals.total_points + referralEarnings} />
              <StatCard label="Total Rake" value={totals.total_rake} color="#f59e0b" prefix={false} />
              <StatCard label="Total Games" value={totals.total_games.toLocaleString()} color="#60a5fa" prefix={false} />
            </div>
            {allClubs.map(club => {
              const everyone = club.me ? [club.me, ...club.visible] : club.visible;
              return (
                <div key={club.club_name} style={S.card}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 4 }}>♠️ {club.club_name}</div>
                  <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 16 }}>{club.period}</div>
                  <div style={S.tableWrap}>
                    <table style={S.table}>
                      <thead><tr>{["Nickname", "ID", "Role", "Ring Win", "MTT Win", "Rake", "RB%", "Settlement"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {everyone.map((p, i) => (
                          <tr key={p.member_id} style={{ background: p.member_id === playerId ? "#1a3a2a" : i % 2 === 0 ? "transparent" : "#0d1520" }}>
                            <td style={S.td}><strong style={{ color: p.member_id === playerId ? "#2ecc71" : "#e0e0e0" }}>{p.nickname}{p.member_id === playerId ? " (you)" : ""}</strong></td>
                            <td style={{ ...S.td, fontFamily: "monospace", color: "#6b7280" }}>{p.member_id}</td>
                            <td style={S.td}><span style={S.badge(p.role === "Super Agent" ? "#f59e0b" : p.role === "Agent" ? "#60a5fa" : "#6b7280")}>{p.role}</span></td>
                            <td style={{ ...S.td, color: clr(p.ring_winnings) }}>{fmt(p.ring_winnings)}</td>
                            <td style={{ ...S.td, color: clr(p.mtt_winnings) }}>{fmt(p.mtt_winnings)}</td>
                            <td style={{ ...S.td, color: "#f59e0b" }}>{fmt(p.total_rake, false)}</td>
                            <td style={S.td}>{pct(p.ring_rakeback_pct)}</td>
                            <td style={{ ...S.td, color: clr(p.total_points), fontWeight: 700 }}>{fmt(p.total_points)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "deals" && (
          <div>
          <div style={S.card}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 16 }}>Add New Deal</div>
            <AddDealForm headers={headers} playerId={playerId} onSaved={() => { axios.get(`${API}/player/deals`, { headers }).then(r => setDeals(r.data)); setSaveMsg("Deal added!"); setTimeout(() => setSaveMsg(""), 3000); }} />
          </div>
          <div style={S.card}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 16 }}>My Deals</div>
            {saveMsg && <p style={S.success}>{saveMsg}</p>}
            {Object.keys(deals).length === 0 ? <p style={{ color: "#6b7280" }}>No deals set yet.</p> : (
              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead><tr>{["Club", "Player ID", "Ring RB%", "MTT RB%", "Ring Tax%", "MTT Tax%", "Mode", "Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {Object.entries(deals).map(([key, val], i) => {
                      const [club, , player] = key.split(":");
                      return (
                        <tr key={key} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                          <td style={S.td}><span style={S.badge("#818cf8")}>{club}</span></td>
                          <td style={{ ...S.td, fontFamily: "monospace", color: "#a5b4fc" }}>{player}<span style={{ color: "#6b7280", marginLeft: 6, fontSize: 11 }}>{val.player_nick}</span></td>
                          <td style={S.td}>{editKey === key ? <input style={S.smallInput} value={editVals.ring_rb} onChange={e => setEditVals({ ...editVals, ring_rb: e.target.value })} /> : <span style={{ color: "#2ecc71", fontWeight: 600 }}>{val.ring_rb}%</span>}</td>
                          <td style={S.td}>{editKey === key ? <input style={S.smallInput} value={editVals.mtt_rb} onChange={e => setEditVals({ ...editVals, mtt_rb: e.target.value })} /> : <span style={{ color: "#2ecc71", fontWeight: 600 }}>{val.mtt_rb}%</span>}</td>
                          <td style={S.td}>{editKey === key ? <input style={S.smallInput} value={editVals.ring_tax} onChange={e => setEditVals({ ...editVals, ring_tax: e.target.value })} /> : <span style={{ color: "#f59e0b" }}>{val.ring_tax}%</span>}</td>
                          <td style={S.td}>{editKey === key ? <input style={S.smallInput} value={editVals.mtt_tax} onChange={e => setEditVals({ ...editVals, mtt_tax: e.target.value })} /> : <span style={{ color: "#f59e0b" }}>{val.mtt_tax}%</span>}</td>
                          <td style={{ ...S.td, color: "#9ca3af", fontSize: 12 }}>{editKey === key ? <select style={S.select} value={editVals.tax_mode || "simple"} onChange={e => setEditVals({ ...editVals, tax_mode: e.target.value })}><option value="simple">simple</option><option value="complex">complex</option></select> : (val.tax_mode || <span style={{ color: "#6b7280" }}>simple</span>)}</td>
                          <td style={S.td}>{editKey === key ? <><button style={S.saveBtn} onClick={() => saveDeal(key)}>Save</button><button style={S.cancelBtn} onClick={() => setEditKey(null)}>Cancel</button></> : <button style={S.editBtn} onClick={() => { setEditKey(key); setEditVals({ ring_rb: val.ring_rb, mtt_rb: val.mtt_rb, ring_tax: val.ring_tax, mtt_tax: val.mtt_tax, tax_mode: val.tax_mode || "simple" }); }}>Edit</button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PlayerAccountsTab({ headers }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newId, setNewId] = useState("");
  const [newPass, setNewPass] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const load = async () => { setLoading(true); const res = await axios.get(`${API}/admin/player-accounts`, { headers }); setAccounts(res.data); setLoading(false); };
  const [playerClubsMap, setPlayerClubsMap] = useState({});
  useEffect(() => {
    axios.get(`${API}/week`, { headers }).then(r => {
      const m = {};
      for (const [clubName, cd] of Object.entries(r.data.clubs || {})) {
        for (const p of cd.results || []) {
          if (!m[p.member_id]) m[p.member_id] = [];
          if (!m[p.member_id].includes(clubName)) m[p.member_id].push(clubName);
        }
      }
      setPlayerClubsMap(m);
    }).catch(() => {});
  }, [headers]);
  useEffect(() => { load(); }, []);
  const createAccount = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/admin/player-password`, { player_id: newId, password: newPass }, { headers }); setMsg(`Account created for ${newId}`); setNewId(""); setNewPass(""); load(); }
    catch { setError("Failed to create account"); }
    setTimeout(() => { setMsg(""); setError(""); }, 3000);
  };
  const deleteAccount = async (pid) => { if (!window.confirm(`Delete account for ${pid}?`)) return; await axios.delete(`${API}/admin/player-password/${pid}`, { headers }); load(); };
  const resetPassword = async (pid) => { const p = prompt(`New temporary password for ${pid}:`); if (!p) return; await axios.post(`${API}/admin/player-password`, { player_id: pid, password: p }, { headers }); setMsg(`Password reset for ${pid}`); setTimeout(() => setMsg(""), 3000); };
  return (
    <div>
      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 16 }}>Create Player Account</div>
        <form onSubmit={createAccount} style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>PLAYER ID</div><input style={{ ...S.searchBar, width: 160 }} value={newId} onChange={e => setNewId(e.target.value)} placeholder="1234-5678" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>TEMP PASSWORD</div><input style={{ ...S.searchBar, width: 160 }} type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Temporary password" required /></div>
          <button style={{ ...S.saveBtn, padding: "8px 20px" }} type="submit">Create Account</button>
        </form>
        {msg && <p style={{ ...S.success, textAlign: "left", marginTop: 8 }}>{msg}</p>}
        {error && <p style={{ ...S.error, textAlign: "left", marginTop: 8 }}>{error}</p>}
      </div>
      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 16 }}>Player Accounts ({accounts.length})</div>
        {loading ? <p style={{ color: "#6b7280" }}>Loading...</p> : (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>{["Player ID", "Linked IDs", "Must Change Password", "Created", "Last Login", "Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {accounts.map((a, i) => (
                  <tr key={a.player_id} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                    <td style={{ ...S.td, fontFamily: "monospace", color: "#a5b4fc" }}>{a.player_id}</td>
                    <td style={S.td}><div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 4 }}>{(a.linked_player_ids || []).map(pid => <span key={pid} style={{ background: "#60a5fa22", color: "#60a5fa", border: "1px solid #60a5fa44", borderRadius: 4, padding: "2px 6px", fontSize: 11 }}>{pid}</span>)}</div><button style={{ ...S.editBtn, fontSize: 11, padding: "3px 8px" }} onClick={() => { const ids = prompt("Linked player IDs (comma separated):", (a.linked_player_ids || []).join(",")); if (ids !== null) { axios.put(`${API}/admin/player-linked-ids/${a.player_id}`, ids.split(",").map(s => s.trim()).filter(Boolean), { headers }).then(() => load()); } }}>Edit</button></td>
                    <td style={S.td}><span style={S.badge(a.must_change_password ? "#f59e0b" : "#2ecc71")}>{a.must_change_password ? "Yes" : "No"}</span></td>
                    <td style={{ ...S.td, color: "#6b7280", fontSize: 12 }}>{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                    <td style={{ ...S.td, color: "#6b7280", fontSize: 12 }}>{a.last_login ? new Date(a.last_login).toLocaleDateString() : "Never"}</td>
                    <td style={S.td}>
                      {(() => {
                        const clubs = playerClubsMap[a.player_id] || [];
                        if (clubs.length === 1) {
                          const c = clubs[0];
                          return <>
                            <button style={{ ...S.editBtn, marginRight: 6, padding: "3px 8px", fontSize: 11 }} onClick={() => downloadExport({ kind: "csv", club: c, target: a.player_id, headers })}>CSV</button>
                            <button style={{ ...S.saveBtn, marginRight: 6, padding: "3px 8px", fontSize: 11 }} onClick={() => downloadExport({ kind: "pdf", club: c, target: a.player_id, headers })}>PDF</button>
                          </>;
                        }
                        if (clubs.length > 1) {
                          const tip = `Player is in ${clubs.length} clubs (${clubs.join(", ")}). Use the Stats tab to export a specific club.`;
                          return <span title={tip} style={{ marginRight: 6 }}>
                            <button style={{ ...S.editBtn, marginRight: 4, padding: "3px 8px", fontSize: 11, opacity: 0.4, cursor: "not-allowed" }} disabled>CSV</button>
                            <button style={{ ...S.saveBtn, marginRight: 4, padding: "3px 8px", fontSize: 11, opacity: 0.4, cursor: "not-allowed" }} disabled>PDF</button>
                          </span>;
                        }
                        return null;
                      })()}
                      <button style={{ ...S.editBtn, marginRight: 6 }} onClick={() => resetPassword(a.player_id)}>Reset Password</button><button style={S.cancelBtn} onClick={() => deleteAccount(a.player_id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Combined Club Admin + Player Account — Phase 1: link-request UI.
// MyLinksTab    → Club Admin: see linked player_ids + pending/own requests +
//                 submit a new request.
// LinkRequestsTab → Super Admin: queue with filter + Approve/Reject/Revoke.
// Phase 1 stops at populating linked_player_ids — no view toggle, no
// player-mode dashboard, no JWT changes.
// ─────────────────────────────────────────────────────────────────────────────

const LINK_STATUS_COLOR = {
  pending:  "#f59e0b",
  approved: "#2ecc71",
  rejected: "#e74c3c",
  revoked:  "#6b7280",
};

function MyLinksTab({ headers }) {
  const [linked, setLinked] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newId, setNewId] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, mineRes] = await Promise.all([
        axios.get(`${API}/me`, { headers }),
        axios.get(`${API}/admin/link-requests/mine`, { headers }),
      ]);
      setLinked(meRes.data?.linked_player_ids || []);
      setRequests(mineRes.data || []);
    } catch (e) {
      setError("Failed to load link data.");
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setError("");
    const pid = newId.trim();
    if (!/^\d{4}-\d{4}$/.test(pid)) {
      setError("Player ID must be in the format ####-####.");
      return;
    }
    try {
      await axios.post(`${API}/admin/link-requests`, {
        player_id: pid,
        notes: newNotes.trim() || null,
      }, { headers });
      setMsg(`Request submitted for ${pid}.`);
      setNewId(""); setNewNotes("");
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to submit request.");
    }
    setTimeout(() => { setMsg(""); setError(""); }, 4000);
  };

  return (
    <div>
      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 8 }}>
          Linked Player IDs
        </div>
        <div style={{ color: "#9ca3af", fontSize: 12, marginBottom: 14 }}>
          Player accounts a Super Admin has approved for linking to your admin
          account. Phase 1 only records the link — view-mode toggle ships in a
          later release.
        </div>
        {loading ? <p style={{ color: "#6b7280" }}>Loading...</p> : (
          linked.length === 0
            ? <div style={{ color: "#6b7280", fontSize: 13 }}>No linked player IDs yet.</div>
            : <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {linked.map(pid => (
                  <span key={pid} style={S.badge("#60a5fa")}>{pid}</span>
                ))}
              </div>
        )}
      </div>

      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 8 }}>
          Request Link
        </div>
        <form onSubmit={submit} style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>PLAYER ID</div>
            <input style={{ ...S.searchBar, width: 160 }} value={newId} onChange={e => setNewId(e.target.value)} placeholder="1234-5678" required />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>NOTES (optional)</div>
            <textarea style={{ ...S.searchBar, width: "100%", minHeight: 60, fontFamily: "inherit" }} value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Why this player_id should be linked to your admin account…" />
          </div>
          <button style={{ ...S.saveBtn, padding: "8px 20px" }} type="submit">Submit Request</button>
        </form>
        {msg && <p style={{ ...S.success, textAlign: "left", marginTop: 8 }}>{msg}</p>}
        {error && <p style={{ ...S.error, textAlign: "left", marginTop: 8 }}>{error}</p>}
      </div>

      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 16 }}>
          My Requests ({requests.length})
        </div>
        {loading ? <p style={{ color: "#6b7280" }}>Loading...</p> : (
          requests.length === 0
            ? <div style={{ color: "#6b7280", fontSize: 13 }}>No requests yet.</div>
            : <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead><tr>{["Player ID", "Status", "Requested", "Resolved", "Resolved By", "Notes"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {requests.map((r, i) => (
                      <tr key={r.id} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                        <td style={{ ...S.td, fontFamily: "monospace", color: "#a5b4fc" }}>{r.player_id}</td>
                        <td style={S.td}><span style={S.badge(LINK_STATUS_COLOR[r.status] || "#6b7280")}>{r.status}</span></td>
                        <td style={{ ...S.td, color: "#6b7280", fontSize: 12 }}>{r.requested_at ? new Date(r.requested_at).toLocaleString() : "—"}</td>
                        <td style={{ ...S.td, color: "#6b7280", fontSize: 12 }}>{r.resolved_at ? new Date(r.resolved_at).toLocaleString() : "—"}</td>
                        <td style={{ ...S.td, color: "#6b7280", fontSize: 12 }}>{r.resolved_by || "—"}</td>
                        <td style={{ ...S.td, whiteSpace: "normal", color: "#9ca3af", fontSize: 12, maxWidth: 320 }}>{r.notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
        )}
      </div>
    </div>
  );
}

function LinkRequestsTab({ headers }) {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const url = statusFilter
        ? `${API}/admin/link-requests?status=${encodeURIComponent(statusFilter)}`
        : `${API}/admin/link-requests`;
      const r = await axios.get(url, { headers });
      setRows(r.data || []);
    } catch (e) {
      setError("Failed to load link requests.");
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  const flash = (s, isErr = false) => {
    if (isErr) setError(s); else setMsg(s);
    setTimeout(() => { setMsg(""); setError(""); }, 3500);
  };

  const approve = async (row) => {
    if (!window.confirm(`Approve link: ${row.admin_username} → ${row.player_id}?`)) return;
    try {
      await axios.post(`${API}/admin/link-requests/${row.id}/approve`, {}, { headers });
      flash(`Approved.`);
      load();
    } catch (e) {
      flash(e.response?.data?.detail || "Approve failed.", true);
    }
  };

  const reject = async (row) => {
    const reason = window.prompt(`Reject link ${row.admin_username} → ${row.player_id}.\nReason (optional):`, "");
    if (reason === null) return;
    try {
      await axios.post(`${API}/admin/link-requests/${row.id}/reject`, { reason: reason || null }, { headers });
      flash(`Rejected.`);
      load();
    } catch (e) {
      flash(e.response?.data?.detail || "Reject failed.", true);
    }
  };

  const revoke = async (row) => {
    if (!window.confirm(`Revoke approved link: ${row.admin_username} → ${row.player_id}? The player_id will be removed from their linked_player_ids.`)) return;
    try {
      await axios.post(`${API}/admin/link-requests/${row.id}/revoke`, {}, { headers });
      flash(`Revoked.`);
      load();
    } catch (e) {
      flash(e.response?.data?.detail || "Revoke failed.", true);
    }
  };

  return (
    <div>
      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 16 }}>
          Link Requests
        </div>
        <div style={S.filterRow}>
          <div style={{ color: "#6b7280", fontSize: 11 }}>STATUS</div>
          <select style={S.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="revoked">Revoked</option>
            <option value="">All</option>
          </select>
          <button style={{ ...S.editBtn, padding: "6px 14px" }} onClick={load}>Refresh</button>
          {msg && <span style={{ ...S.success, marginBottom: 0, marginLeft: 12 }}>{msg}</span>}
          {error && <span style={{ ...S.error, marginBottom: 0, marginLeft: 12 }}>{error}</span>}
        </div>
        {loading ? <p style={{ color: "#6b7280" }}>Loading...</p> : (
          rows.length === 0
            ? <div style={{ color: "#6b7280", fontSize: 13 }}>No requests {statusFilter ? `with status “${statusFilter}”` : ""}.</div>
            : <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead><tr>{["Admin", "Player ID", "Status", "Requested", "Resolved", "Resolved By", "Notes", "Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.id} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                        <td style={{ ...S.td, color: "#e0e0e0" }}>{r.admin_username}</td>
                        <td style={{ ...S.td, fontFamily: "monospace", color: "#a5b4fc" }}>{r.player_id}</td>
                        <td style={S.td}><span style={S.badge(LINK_STATUS_COLOR[r.status] || "#6b7280")}>{r.status}</span></td>
                        <td style={{ ...S.td, color: "#6b7280", fontSize: 12 }}>{r.requested_at ? new Date(r.requested_at).toLocaleString() : "—"}</td>
                        <td style={{ ...S.td, color: "#6b7280", fontSize: 12 }}>{r.resolved_at ? new Date(r.resolved_at).toLocaleString() : "—"}</td>
                        <td style={{ ...S.td, color: "#6b7280", fontSize: 12 }}>{r.resolved_by || "—"}</td>
                        <td style={{ ...S.td, whiteSpace: "normal", color: "#9ca3af", fontSize: 12, maxWidth: 280 }}>{r.notes || "—"}</td>
                        <td style={S.td}>
                          {r.status === "pending" && <>
                            <button style={{ ...S.saveBtn, padding: "3px 10px", fontSize: 11, marginRight: 4 }} onClick={() => approve(r)}>Approve</button>
                            <button style={{ ...S.cancelBtn, padding: "3px 10px", fontSize: 11 }} onClick={() => reject(r)}>Reject</button>
                          </>}
                          {r.status === "approved" && <button style={{ background: "#e74c3c", color: "#fff", border: "none", padding: "3px 10px", borderRadius: 4, cursor: "pointer", fontSize: 11, fontWeight: 600 }} onClick={() => revoke(r)}>Revoke</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
        )}
      </div>
    </div>
  );
}

function ClubsTab({ headers }) {
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("current");
  const [data, setData] = useState({});
  const [referrals, setReferrals] = useState({ by_club: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [sortCol, setSortCol] = useState("active");
  const [sortDir, setSortDir] = useState("desc");
  const [exportErr, setExportErr] = useState("");

  useEffect(() => { axios.get(`${API}/history/periods`, { headers }).then(r => setPeriods(r.data)).catch(() => {}); }, [headers]);

  useEffect(() => {
    setLoading(true);
    const url = selectedPeriod === "current" ? `${API}/clubs` : `${API}/history/clubs/${encodeURIComponent(selectedPeriod)}`;
    axios.get(url, { headers }).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [headers, selectedPeriod]);

  useEffect(() => {
    const url = selectedPeriod === "current" ? `${API}/referrals/summary` : `${API}/referrals/summary?period=${encodeURIComponent(selectedPeriod)}`;
    axios.get(url, { headers }).then(r => setReferrals(r.data)).catch(() => {});
  }, [headers, selectedPeriod]);

  const enriched = useMemo(() => {
    return Object.values(data).map(c => {
      const clubRefs = referrals.by_club[c.club_name] || [];
      const ref_total = clubRefs.reduce((s, r) => s + r.earnings, 0);
      return { ...c, ref_total, net: c.stl - c.paid - ref_total };
    });
  }, [data, referrals]);

  const visibleClubs = useMemo(() => enriched.filter(c => (c.active || 0) > 0), [enriched]);
  const hiddenCount = enriched.length - visibleClubs.length;

  const sortedClubs = useMemo(() => {
    const cloned = visibleClubs.slice();
    const dir = sortDir === "asc" ? 1 : -1;
    cloned.sort((a, b) => {
      const av = a[sortCol], bv = b[sortCol];
      let cmp;
      if (typeof av === "number" && typeof bv === "number") {
        cmp = av - bv;
      } else {
        cmp = String(av ?? "").toLowerCase().localeCompare(String(bv ?? "").toLowerCase());
      }
      if (cmp !== 0) return cmp * dir;
      // Secondary sort: club_name ascending
      return String(a.club_name).localeCompare(String(b.club_name));
    });
    return cloned;
  }, [visibleClubs, sortCol, sortDir]);

  const onSortClick = (col) => {
    if (sortCol === col) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir(col === "club_name" ? "asc" : "desc");
    }
  };

  const sortArrow = (col) => sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const downloadCsv = async (clubName) => {
    setExportErr("");
    try {
      const response = await fetch(`${API}/clubs/${encodeURIComponent(clubName)}/csv`, { headers });
      if (!response.ok) {
        const txt = await response.text();
        let detail;
        try { detail = JSON.parse(txt).detail; } catch { detail = txt.slice(0, 200); }
        throw new Error(`HTTP ${response.status}: ${detail}`);
      }
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `${clubName}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportErr(`Export failed: ${e.message}`);
      setTimeout(() => setExportErr(""), 6000);
    }
  };

  const headClick = { ...S.th, cursor: "pointer", userSelect: "none" };
  const thCenter  = { ...headClick, textAlign: "center" };
  const thRight   = { ...headClick, textAlign: "right" };
  const tdCenter  = { ...S.td, textAlign: "center" };
  const tdRight   = { ...S.td, textAlign: "right" };

  return (
    <div>
      <div style={S.filterRow}>
        <select style={S.select} value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}>
          <option value="current">Current Week</option>
          {periods.map(p => <option key={p.period} value={p.period}>{p.period}</option>)}
        </select>
      </div>
      {loading ? <p style={{ color: "#6b7280" }}>Loading...</p> : (
        <>
          {hiddenCount > 0 && (
            <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 10 }}>
              {hiddenCount} {hiddenCount === 1 ? "club" : "clubs"} hidden (no activity this period)
            </div>
          )}
          {exportErr && (
            <div style={{ background: "#e74c3c22", color: "#e74c3c", padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
              {exportErr}
            </div>
          )}
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={headClick} onClick={() => onSortClick("club_name")}>Club{sortArrow("club_name")}</th>
                  <th style={thCenter} onClick={() => onSortClick("active")}>Active{sortArrow("active")}</th>
                  <th style={thCenter} onClick={() => onSortClick("club_rb")}>RB%{sortArrow("club_rb")}</th>
                  <th style={{ ...S.th, textAlign: "center" }}>Tax</th>
                  <th style={thRight} onClick={() => onSortClick("stl")}>Settlement{sortArrow("stl")}</th>
                  <th style={thRight} onClick={() => onSortClick("paid")}>Paid Out{sortArrow("paid")}</th>
                  <th style={thRight} onClick={() => onSortClick("ref_total")}>Referrals{sortArrow("ref_total")}</th>
                  <th style={thRight} onClick={() => onSortClick("net")}>Net{sortArrow("net")}</th>
                  <th style={thRight} onClick={() => onSortClick("rake")}>Rake{sortArrow("rake")}</th>
                  <th style={{ ...S.th, textAlign: "right" }} title="Download raw xlsx">Export</th>
                </tr>
              </thead>
              <tbody>
                {sortedClubs.length === 0 && (
                  <tr><td colSpan={10} style={{ ...S.td, textAlign: "center", color: "#6b7280", padding: 30 }}>
                    {enriched.length === 0 ? "No clubs loaded." : "No active clubs this period."}
                  </td></tr>
                )}
                {sortedClubs.map(c => (
                  <tr key={c.club_name}>
                    <td style={{ ...S.td, color: "#e0e0e0", fontWeight: 600 }}>{c.club_name}</td>
                    <td style={{ ...tdCenter, color: "#818cf8", fontWeight: 600 }}>{c.active}</td>
                    <td style={tdCenter}>{c.club_rb}%</td>
                    <td style={tdCenter}>R{c.ring_tax}%/M{c.mtt_tax}%</td>
                    <td style={{ ...tdRight, color: clr(c.stl) }}>{fmt(c.stl)}</td>
                    <td style={{ ...tdRight, color: clr(c.paid) }}>{fmt(c.paid)}</td>
                    <td style={{ ...tdRight, color: clr(-c.ref_total) }}>{fmt(-c.ref_total)}</td>
                    <td style={{ ...tdRight, color: clr(c.net), fontWeight: 700 }}>{fmt(c.net)}</td>
                    <td style={{ ...tdRight, color: "#f59e0b" }}>{fmt(c.rake, false)}</td>
                    <td style={tdRight}>
                      <button style={S.editBtn} onClick={() => downloadCsv(c.club_name)} title="Download original raw xlsx for this club">📥 XLSX</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function StatsTab({ headers }) {
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("current");
  const [weekData, setWeekData] = useState({ clubs: {} });
  const [clubFilter, setClubFilter] = useState("all");
  const [saFilter, setSaFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [skipIds, setSkipIds] = useState(new Set());
  useEffect(() => { axios.get(`${API}/skip-ids`, { headers }).then(r => setSkipIds(new Set(r.data.skip_ids || []))).catch(() => {}); }, [headers]);

  useEffect(() => { axios.get(`${API}/history/periods`, { headers }).then(r => setPeriods(r.data)).catch(() => {}); }, [headers]);
  useEffect(() => {
    setLoading(true);
    const url = selectedPeriod === "current" ? `${API}/week` : `${API}/history/${encodeURIComponent(selectedPeriod)}`;
    axios.get(url, { headers }).then(r => { setWeekData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [headers, selectedPeriod]);

  const clubs = useMemo(() => Object.keys(weekData.clubs || {}), [weekData]);
  const allResults = useMemo(() => {
    const out = [];
    for (const [club, data] of Object.entries(weekData.clubs || {})) {
      if (clubFilter !== "all" && club !== clubFilter) continue;
      for (const p of data.results || []) out.push({ ...p, club });
    }
    return out;
  }, [weekData, clubFilter]);

  const superAgents = useMemo(() => { const s = new Set(); allResults.forEach(p => { if (p.super_agent_id && p.super_agent_id !== "-") s.add(p.super_agent_id + "|" + (p.super_agent_nick || p.super_agent_id)); }); return [...s].sort(); }, [allResults]);
  const agents = useMemo(() => {
    const s = new Set();
    allResults.forEach(p => {
      if (!p.agent_id || p.agent_id === "-") return;
      // Cascade: when a specific SA is selected, only include agents whose super_agent_id matches it.
      if (saFilter !== "all" && p.super_agent_id !== saFilter) return;
      s.add(p.agent_id + "|" + (p.agent_nick || p.agent_id));
    });
    return [...s].sort();
  }, [allResults, saFilter, skipIds]);

  const filtered = useMemo(() => {
    let results = allResults;
    if (saFilter !== "all") results = results.filter(p => p.super_agent_id === saFilter);
    if (agentFilter !== "all") results = results.filter(p => p.agent_id === agentFilter);
    if (search) { const q = search.toLowerCase(); results = results.filter(p => p.nickname?.toLowerCase().includes(q) || p.member_id?.toLowerCase().includes(q)); }
    return results;
  }, [allResults, saFilter, agentFilter, search]);

  const totals = useMemo(() => ({
    ring_winnings: filtered.reduce((s, p) => s + (p.ring_winnings || 0), 0),
    mtt_winnings: filtered.reduce((s, p) => s + (p.mtt_winnings || 0), 0),
    total_rake: filtered.reduce((s, p) => s + (p.total_rake || 0), 0),
    total_points: filtered.reduce((s, p) => s + (p.total_points || 0), 0),
    ring_points: filtered.reduce((s, p) => s + (p.ring_points || 0), 0),
    mtt_points: filtered.reduce((s, p) => s + (p.mtt_points || 0), 0),
    sng_points: filtered.reduce((s, p) => s + (p.sng_points || 0), 0),
    total_games: filtered.reduce((s, p) => s + (p.total_games || 0), 0),
  }), [filtered]);

  return (
    <div>
      <div style={S.filterRow}>
        <select style={S.select} value={selectedPeriod} onChange={e => { setSelectedPeriod(e.target.value); setSaFilter("all"); setAgentFilter("all"); }}>
          <option value="current">Current Week</option>
          {periods.map(p => <option key={p.period} value={p.period}>{p.period}</option>)}
        </select>
        <select style={S.select} value={clubFilter} onChange={e => { setClubFilter(e.target.value); setSaFilter("all"); setAgentFilter("all"); }}>
          <option value="all">All Clubs</option>
          {clubs.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select style={S.select} value={saFilter} onChange={e => { setSaFilter(e.target.value); setAgentFilter("all"); }}>
          <option value="all">All Super Agents</option>
          {superAgents.map(sa => { const [id, nick] = sa.split("|"); return <option key={id} value={id}>{nick} ({id})</option>; })}
        </select>
        <select style={S.select} value={agentFilter} onChange={e => setAgentFilter(e.target.value)}>
          <option value="all">All Agents</option>
          {agents.map(a => { const [id, nick] = a.split("|"); return <option key={id} value={id}>{nick} ({id})</option>; })}
        </select>
        <input style={S.searchBar} placeholder="Search player, ID..." value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ color: "#6b7280", fontSize: 13 }}>{filtered.length} players</span>
      </div>
      {loading ? <p style={{ color: "#6b7280" }}>Loading...</p> : (
        <>
          <div style={S.statGrid}>
            <StatCard label="Total Settlement" value={totals.total_points} />
            <StatCard label="Ring Points" value={totals.ring_points} />
            <StatCard label="MTT Points" value={totals.mtt_points} />
            <StatCard label="SNG Points" value={totals.sng_points} />
            <StatCard label="Total Rake" value={totals.total_rake} color="#f59e0b" prefix={false} />
            <StatCard label="Ring Winnings" value={totals.ring_winnings} />
            <StatCard label="MTT Winnings" value={totals.mtt_winnings} />
            <StatCard label="Total Games" value={totals.total_games.toLocaleString()} color="#60a5fa" prefix={false} />
          </div>
          <HierarchyTree rows={filtered} headers={headers} skipIds={skipIds} clubFilter={clubFilter} period={selectedPeriod} />
        </>
      )}
    </div>
  );
}

// ── Unified Player Rates (rakeback + tax together) ──────────────────────
function PlayerRateHistoryModal({ headers, playerKey, onClose }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/player-rates/${encodeURIComponent(playerKey)}/history`, { headers });
        setData(res.data);
      } catch (e) {}
      setLoading(false);
    })();
    /* eslint-disable-next-line */
  }, [playerKey]);
  const statusStyle = (s) => ({
    active:     { bg: "#2ecc7122", color: "#2ecc71", border: "#2ecc7166", label: "Active" },
    superseded: { bg: "#6b728022", color: "#9ca3af", border: "#6b728066", label: "Superseded" },
    future:     { bg: "#60a5fa22", color: "#60a5fa", border: "#60a5fa66", label: "Future" },
  }[s] || { bg: "#6b728022", color: "#9ca3af", border: "#6b728066", label: s });
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#0d1520", border: "1px solid #1f2937", borderRadius: 10, padding: 24, width: "100%", maxWidth: 900, maxHeight: "85vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#e0e0e0" }}>Rate History</div>
            <div style={{ fontFamily: "monospace", color: "#a5b4fc", fontSize: 13, marginTop: 4 }}>{playerKey}</div>
          </div>
          <button style={S.cancelBtn} onClick={onClose}>Close</button>
        </div>
        {loading ? <p style={{ color: "#6b7280" }}>Loading...</p> : data.length === 0 ? (
          <p style={{ color: "#6b7280", textAlign: "center", padding: 20 }}>No version history for this player.</p>
        ) : (
          <table style={S.table}>
            <thead><tr>{["Kind", "Ring %", "MTT %", "Effective From", "Status", "Created"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.map((v, i) => {
                const st = statusStyle(v.status);
                return (
                <tr key={`${v.kind}-${v.id}`} style={{ background: i % 2 === 0 ? "transparent" : "#111827" }}>
                  <td style={S.td}>
                    <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                      background: v.kind === "rakeback" ? "#2ecc7122" : "#f59e0b22",
                      color: v.kind === "rakeback" ? "#2ecc71" : "#f59e0b",
                      border: "1px solid " + (v.kind === "rakeback" ? "#2ecc7166" : "#f59e0b66") }}>{v.kind}</span>
                  </td>
                  <td style={{ ...S.td, color: v.kind === "rakeback" ? "#2ecc71" : "#f59e0b", fontWeight: 600 }}>{v.ring}%</td>
                  <td style={{ ...S.td, color: v.kind === "rakeback" ? "#2ecc71" : "#f59e0b", fontWeight: 600 }}>{v.mtt}%</td>
                  <td style={S.td}>{v.effective_from || <span style={{ color: "#6b7280", fontStyle: "italic" }}>all time</span>}</td>
                  <td style={S.td}>
                    <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                      background: st.bg, color: st.color, border: "1px solid " + st.border }}>{st.label}</span>
                  </td>
                  <td style={{ ...S.td, color: "#6b7280", fontSize: 11 }}>{v.created_at ? v.created_at.slice(0, 10) : "—"}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PlayerRatesTab({ headers }) {
  const [data, setData] = useState({});
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [clubFilter, setClubFilter] = useState("");  // "" = All clubs
  const [sortBy, setSortBy] = useState("key");       // default: club:player_id lex
  const [sortDir, setSortDir] = useState("asc");
  const [form, setForm] = useState({ club: "", player_id: "", ring_rb: "0", mtt_rb: "0", ring_tax: "0", mtt_tax: "0", tax_mode: "simple", effective_from: "" });
  const [clubOptions, setClubOptions] = useState([]);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [historyKey, setHistoryKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/player-rates`, { headers }),
        axios.get(`${API}/clubs`, { headers }),
        axios.get(`${API}/players`, { headers }),
      ]);
      setData(r1.data);
      const names = Object.keys(r2.data || {}).sort();
      setClubOptions(names);
      setForm(f => {
        if (f.club) return f;
        let remembered = "";
        try { remembered = localStorage.getItem('player_rates_last_club') || ""; } catch (_) {}
        const initial = (remembered && names.includes(remembered)) ? remembered : (names[0] || "");
        return { ...f, club: initial };
      });
      setPlayers(r3.data || []);
    } catch (e) {
      setError("Failed to load player rates");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const nicknameById = useMemo(() => (
    Object.fromEntries(players.map(p => [p.member_id, p.nickname]))
  ), [players]);

  // Sort-key extraction: returns a primitive (string|number) for the chosen
  // column. "Effective Since" is treated as a string YYYY-MM-DD; null
  // ("All time") is mapped to a very-late sentinel so those rows sort as
  // newest — matches the rate-history sort-key convention shipped earlier.
  const _sortValue = (sortBy, key, val) => {
    const pid = key.split(":").slice(1).join(":");
    switch (sortBy) {
      case "club":     return key.split(":")[0] || "";
      case "pid":      return pid;
      case "username": return (nicknameById[pid] || "").toLowerCase();
      case "ring_rb":  return Number(val.ring_rb) || 0;
      case "mtt_rb":   return Number(val.mtt_rb) || 0;
      case "ring_tax": return Number(val.ring_tax) || 0;
      case "mtt_tax":  return Number(val.mtt_tax) || 0;
      case "mode":     return val.tax_mode || "simple";
      case "eff": {
        const d = val.rb_effective_since || val.tax_effective_since;
        return d || "9999-12-31";  // null → sort as newest
      }
      case "key":
      default:         return key.toLowerCase();
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let entries = Object.entries(data);
    if (clubFilter) {
      const prefix = clubFilter.toLowerCase() + ":";
      entries = entries.filter(([k]) => k.toLowerCase().startsWith(prefix));
    }
    if (q) {
      entries = entries.filter(([k]) => {
        if (k.toLowerCase().includes(q)) return true;
        const pid = k.split(":").slice(1).join(":");
        const nick = nicknameById[pid];
        return !!nick && nick.toLowerCase().includes(q);
      });
    }
    const dir = sortDir === "desc" ? -1 : 1;
    entries.sort(([ka, va], [kb, vb]) => {
      const a = _sortValue(sortBy, ka, va);
      const b = _sortValue(sortBy, kb, vb);
      if (a < b) return -1 * dir;
      if (a > b) return 1 * dir;
      return 0;
    });
    return entries;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, search, clubFilter, sortBy, sortDir, nicknameById]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setMsg("");
    if (!form.club) { setError("Please select a club"); return; }
    if (!form.player_id || !form.player_id.trim()) { setError("Please enter a Player ID"); return; }
    const pid = form.player_id.trim();
    const key = `${form.club}:${pid}`;
    try {
      await axios.post(`${API}/player-rates`, {
        key,
        ring_rb: parseFloat(form.ring_rb) || 0,
        mtt_rb: parseFloat(form.mtt_rb) || 0,
        ring_tax: parseFloat(form.ring_tax) || 0,
        mtt_tax: parseFloat(form.mtt_tax) || 0,
        effective_from: form.effective_from || null,
      }, { headers });
      // Persist last-used club as soon as the primary save succeeds — if the
      // follow-up tax-modes call throws, the row already exists in the DB and
      // the user's club choice is still worth remembering.
      try { localStorage.setItem('player_rates_last_club', form.club); } catch (_) {}
      // Sync per-player tax_mode. 'simple' is the implicit default — if the
      // user picked simple and there's no existing row, skip; if they picked
      // simple after the row existed, delete it so stale complex doesn't
      // linger. 'complex' always writes.
      const prevMode = (data[key] && data[key].tax_mode) || "simple";
      if (form.tax_mode === "complex") {
        await axios.put(`${API}/tax-modes/${encodeURIComponent(pid)}`, null, { headers, params: { mode: "complex" } });
      } else if (prevMode === "complex") {
        await axios.delete(`${API}/tax-modes/${encodeURIComponent(pid)}`, { headers });
      }
      setMsg(`Saved rates for ${key}`);
      setForm({ club: form.club, player_id: "", ring_rb: "0", mtt_rb: "0", ring_tax: "0", mtt_tax: "0", tax_mode: "simple", effective_from: "" });
      load();
      setTimeout(() => setMsg(""), 3000);
    } catch (err) {
      setError("Failed to save rates");
    }
  };

  const del = async (key) => {
    const pid = key.split(":").slice(1).join(":");
    const nick = nicknameById[pid];
    const who = nick ? ` (${nick})` : "";
    if (!window.confirm(`Delete rate for ${key}${who}?\n\nThis removes the rakeback + tax rows. Cannot be undone.`)) return;
    setError(""); setMsg("");
    try {
      await axios.delete(`${API}/player-rates/${encodeURIComponent(key)}`, { headers });
      setMsg(`Deleted rates for ${key}`);
      load();
      setTimeout(() => setMsg(""), 3000);
    } catch (err) {
      setError(err.response?.status === 404 ? "No rate found to delete" : "Failed to delete rates");
    }
  };

  const Badge = ({ source }) => source === "version" ? (
    <span style={{ marginLeft: 6, padding: "1px 5px", borderRadius: 3, fontSize: 10, fontWeight: 700,
      background: "#f59e0b22", color: "#f59e0b", border: "1px solid #f59e0b66" }} title="Versioned">📅</span>
  ) : null;

  const EffectiveSince = ({ date }) => date ? (
    <span style={{ color: "#60a5fa" }}>Since {date}</span>
  ) : (
    <span style={{ color: "#6b7280", fontStyle: "italic" }}>All time</span>
  );

  if (loading) return <p style={{ color: "#6b7280" }}>Loading...</p>;

  return (
    <div>
      {/* Add form */}
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 4 }}>
          {(() => { const k = form.club && form.player_id ? `${form.club}:${form.player_id.trim()}` : ""; return k && Object.prototype.hasOwnProperty.call(data, k) ? "Update Player Rate" : "Add New Player Rate"; })()}
        </div>
        <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 12 }}>Sets both rakeback and tax as one deal. Leave effective date blank for a permanent rate.</div>
        <form onSubmit={submit} style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>CLUB</div>
            <select style={{ ...S.searchBar, width: 180 }} value={form.club} onChange={e => setForm({ ...form, club: e.target.value })} required>
              {clubOptions.length === 0 && <option value="">No clubs available</option>}
              {clubOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>PLAYER ID</div>
            <input style={{ ...S.searchBar, width: 140 }} value={form.player_id} onChange={e => setForm({ ...form, player_id: e.target.value })} placeholder="1234-5678" required />
          </div>
          <div>
            <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>RING RB%</div>
            <input style={{ ...S.searchBar, width: 70 }} value={form.ring_rb} onChange={e => setForm({ ...form, ring_rb: e.target.value })} placeholder="0" />
          </div>
          <div>
            <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>MTT RB%</div>
            <input style={{ ...S.searchBar, width: 70 }} value={form.mtt_rb} onChange={e => setForm({ ...form, mtt_rb: e.target.value })} placeholder="0" />
          </div>
          <div>
            <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>RING TAX%</div>
            <input style={{ ...S.searchBar, width: 70 }} value={form.ring_tax} onChange={e => setForm({ ...form, ring_tax: e.target.value })} placeholder="0" />
          </div>
          <div>
            <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>MTT TAX%</div>
            <input style={{ ...S.searchBar, width: 70 }} value={form.mtt_tax} onChange={e => setForm({ ...form, mtt_tax: e.target.value })} placeholder="0" />
          </div>
          <div>
            <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>MODE</div>
            <select style={{ ...S.searchBar, width: 110 }} value={form.tax_mode} onChange={e => setForm({ ...form, tax_mode: e.target.value })}>
              <option value="simple">simple</option>
              <option value="complex">complex</option>
            </select>
          </div>
          <div>
            <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>EFFECTIVE FROM</div>
            <input style={{ ...S.searchBar, width: 150 }} type="date" value={form.effective_from} onChange={e => setForm({ ...form, effective_from: e.target.value })} />
          </div>
          <button style={S.saveBtn} type="submit">Save Rates</button>
        </form>
        {msg && <div style={{ color: "#2ecc71", fontSize: 13, marginTop: 10 }}>✓ {msg}</div>}
        {error && <div style={{ color: "#e74c3c", fontSize: 13, marginTop: 10 }}>✗ {error}</div>}
      </div>

      {/* List */}
      <div style={S.card}>
        <div style={S.filterRow}>
          <select style={{ ...S.searchBar, width: 200 }} value={clubFilter} onChange={e => setClubFilter(e.target.value)}>
            <option value="">All clubs</option>
            {clubOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input style={S.searchBar} placeholder="Search by club, username, or player ID..." value={search} onChange={e => setSearch(e.target.value)} />
          <span style={{ color: "#6b7280", fontSize: 13 }}>{filtered.length} players</span>
        </div>
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {[
                  { id: "key",      label: "Club : Player ID" },
                  { id: "username", label: "Username" },
                  { id: "ring_rb",  label: "Ring RB%" },
                  { id: "mtt_rb",   label: "MTT RB%" },
                  { id: "ring_tax", label: "Ring Tax%" },
                  { id: "mtt_tax",  label: "MTT Tax%" },
                  { id: "mode",     label: "Mode" },
                  { id: "eff",      label: "Effective Since" },
                ].map(col => {
                  const active = sortBy === col.id;
                  const arrow = active ? (sortDir === "asc" ? " ▲" : " ▼") : "";
                  return (
                    <th key={col.id} style={{ ...S.th, cursor: "pointer", userSelect: "none" }}
                        onClick={() => toggleSort(col.id)}
                        title="Click to sort">
                      {col.label}<span style={{ color: active ? "#60a5fa" : "transparent" }}>{arrow || " ▲"}</span>
                    </th>
                  );
                })}
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(([key, val], i) => {
                const effDate = val.rb_effective_since || val.tax_effective_since;
                const pid = key.split(":").slice(1).join(":");
                const nick = nicknameById[pid];
                return (
                <tr key={key} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                  <td style={{ ...S.td, fontFamily: "monospace", color: "#a5b4fc" }}>{key}</td>
                  <td style={S.td}>{nick ? nick : <em style={{ color: "#6b7280" }}>—</em>}</td>
                  <td style={S.td}><span style={{ color: "#2ecc71", fontWeight: 600 }}>{val.ring_rb}%</span><Badge source={val.rb_source} /></td>
                  <td style={S.td}><span style={{ color: "#2ecc71", fontWeight: 600 }}>{val.mtt_rb}%</span><Badge source={val.rb_source} /></td>
                  <td style={S.td}><span style={{ color: "#f59e0b", fontWeight: 600 }}>{val.ring_tax}%</span><Badge source={val.tax_source} /></td>
                  <td style={S.td}><span style={{ color: "#f59e0b", fontWeight: 600 }}>{val.mtt_tax}%</span><Badge source={val.tax_source} /></td>
                  <td style={S.td}><span style={S.badge(val.tax_mode === "complex" ? "#818cf8" : "#2ecc71")}>{val.tax_mode || "simple"}</span></td>
                  <td style={{ ...S.td, fontSize: 12 }}><EffectiveSince date={effDate} /></td>
                  <td style={S.td}>
                    <button style={{ ...S.editBtn, marginRight: 6 }} onClick={() => {
                      const parts = key.split(":");
                      const clubPart = parts[0] || "";
                      const pidPart = parts.slice(1).join(":");
                      setForm({ club: clubPart, player_id: pidPart,
                        ring_rb: String(val.ring_rb || 0), mtt_rb: String(val.mtt_rb || 0),
                        ring_tax: String(val.ring_tax || 0), mtt_tax: String(val.mtt_tax || 0),
                        tax_mode: val.tax_mode || "simple", effective_from: "" });
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}>Edit</button>
                    <button style={{ ...S.cancelBtn, background: "#1f2937", color: "#60a5fa", border: "1px solid #60a5fa66", marginRight: 6 }} onClick={() => setHistoryKey(key)}>History</button>
                    <button style={{ ...S.cancelBtn, background: "#1f2937", color: "#e74c3c", border: "1px solid #e74c3c66" }} onClick={() => del(key)}>Delete</button>
                  </td>
                </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan="8" style={{ ...S.td, color: "#6b7280", textAlign: "center", padding: 20 }}>No player rates yet. Use the form above to add one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {historyKey && <PlayerRateHistoryModal headers={headers} playerKey={historyKey} onClose={() => setHistoryKey(null)} />}
    </div>
  );
}

// ── Unified Rate History (rakeback + tax versions) ──────────────────────
function RateHistoryTab({ headers }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // default: Active only

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rate-history`, { headers });
      setData(res.data);
    } catch (e) {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const del = async (v) => {
    if (!window.confirm(`Delete this ${v.kind} version for ${v.key}?`)) return;
    await axios.delete(`${API}/rate-history/${v.kind}/${v.id}`, { headers });
    load();
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return data.filter(v => {
      if (!v.key.toLowerCase().includes(q)) return false;
      if (statusFilter === "all") return true;
      return v.status === statusFilter;
    });
  }, [data, search, statusFilter]);

  const statusStyle = (s) => {
    const styles = {
      active:     { bg: "#2ecc7122", color: "#2ecc71", border: "#2ecc7166", label: "Active" },
      superseded: { bg: "#6b728022", color: "#9ca3af", border: "#6b728066", label: "Superseded" },
      future:     { bg: "#60a5fa22", color: "#60a5fa", border: "#60a5fa66", label: "Future" },
    };
    return styles[s] || styles.superseded;
  };

  if (loading) return <p style={{ color: "#6b7280" }}>Loading...</p>;

  return (
    <div style={S.card}>
      <div style={S.filterRow}>
        <input style={S.searchBar} placeholder="Search club or player ID..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...S.searchBar, width: 160 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="active">Active only</option>
          <option value="all">All</option>
          <option value="superseded">Superseded</option>
          <option value="future">Future</option>
        </select>
        <span style={{ color: "#6b7280", fontSize: 13 }}>{filtered.length} versions</span>
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>{["Key", "Kind", "Ring %", "MTT %", "Effective From", "Status", "Created", "Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {filtered.map((v, i) => {
              const st = statusStyle(v.status);
              return (
              <tr key={`${v.kind}-${v.id}`} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                <td style={{ ...S.td, fontFamily: "monospace", color: "#a5b4fc" }}>{v.key}</td>
                <td style={S.td}>
                  <span style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                    background: v.kind === "rakeback" ? "#2ecc7122" : "#f59e0b22",
                    color: v.kind === "rakeback" ? "#2ecc71" : "#f59e0b",
                    border: "1px solid " + (v.kind === "rakeback" ? "#2ecc7166" : "#f59e0b66"),
                  }}>{v.kind}</span>
                </td>
                <td style={{ ...S.td, color: v.kind === "rakeback" ? "#2ecc71" : "#f59e0b", fontWeight: 600 }}>{v.ring}%</td>
                <td style={{ ...S.td, color: v.kind === "rakeback" ? "#2ecc71" : "#f59e0b", fontWeight: 600 }}>{v.mtt}%</td>
                <td style={S.td}>{v.effective_from || <span style={{ color: "#6b7280", fontStyle: "italic" }}>all time</span>}</td>
                <td style={S.td}>
                  <span style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                    background: st.bg, color: st.color, border: "1px solid " + st.border,
                  }}>{st.label}</span>
                </td>
                <td style={{ ...S.td, color: "#6b7280", fontSize: 11 }}>{v.created_at ? v.created_at.slice(0, 10) : "—"}</td>
                <td style={S.td}><button style={S.cancelBtn} onClick={() => del(v)}>Delete</button></td>
              </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="8" style={{ ...S.td, color: "#6b7280", textAlign: "center", padding: 20 }}>No versions match your filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RakebacksTab({ headers }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editKey, setEditKey] = useState(null);
  const [editVals, setEditVals] = useState({});
  const load = async () => { setLoading(true); const res = await axios.get(`${API}/rakebacks`, { headers }); setData(res.data); setLoading(false); };
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => { const q = search.toLowerCase(); return Object.entries(data).filter(([k]) => k.toLowerCase().includes(q)); }, [data, search]);
  const save = async (key) => { await axios.put(`${API}/rakebacks/${encodeURIComponent(key)}`, null, { headers, params: { ring: parseFloat(editVals.ring), mtt: parseFloat(editVals.mtt) } }); setEditKey(null); load(); };
  if (loading) return <p style={{ color: "#6b7280" }}>Loading...</p>;
  return (
    <div style={S.card}>
      <div style={S.filterRow}>
        <input style={S.searchBar} placeholder="Search club or player ID..." value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ color: "#6b7280", fontSize: 13 }}>{filtered.length} entries</span>
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>{["Club : Player ID", "Ring %", "MTT %", "Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {filtered.map(([key, val], i) => {
              const isVersion = val.source === "version";
              return (
              <tr key={key} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                <td style={{ ...S.td, fontFamily: "monospace", color: "#a5b4fc" }}>
                  {key}
                  {isVersion && (
                    <span style={{
                      marginLeft: 8,
                      padding: "2px 6px",
                      borderRadius: 3,
                      fontSize: 10,
                      fontWeight: 700,
                      background: "#f59e0b22",
                      color: "#f59e0b",
                      border: "1px solid #f59e0b66",
                    }} title="This rate comes from Rb-history. Edit it in the Rb-history tab.">📅 version</span>
                  )}
                </td>
                <td style={S.td}>{editKey === key ? <input style={S.smallInput} value={editVals.ring} onChange={e => setEditVals({ ...editVals, ring: e.target.value })} /> : <span style={{ color: "#2ecc71", fontWeight: 600 }}>{val.ring}%</span>}</td>
                <td style={S.td}>{editKey === key ? <input style={S.smallInput} value={editVals.mtt} onChange={e => setEditVals({ ...editVals, mtt: e.target.value })} /> : <span style={{ color: "#2ecc71", fontWeight: 600 }}>{val.mtt}%</span>}</td>
                <td style={S.td}>
                  {isVersion ? (
                    <span style={{ color: "#6b7280", fontSize: 11, fontStyle: "italic" }} title="Managed via Rb-history tab">Edit in Rb-history</span>
                  ) : editKey === key ? (
                    <><button style={S.saveBtn} onClick={() => save(key)}>Save</button><button style={S.cancelBtn} onClick={() => setEditKey(null)}>Cancel</button></>
                  ) : (
                    <button style={S.editBtn} onClick={() => { setEditKey(key); setEditVals({ ring: val.ring, mtt: val.mtt }); }}>Edit</button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TaxesTab({ headers }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editKey, setEditKey] = useState(null);
  const [editVals, setEditVals] = useState({});
  const load = async () => { setLoading(true); const res = await axios.get(`${API}/taxes`, { headers }); setData(res.data); setLoading(false); };
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => { const q = search.toLowerCase(); return Object.entries(data).filter(([k]) => k.toLowerCase().includes(q)); }, [data, search]);
  const save = async (key) => { await axios.put(`${API}/taxes/${encodeURIComponent(key)}`, null, { headers, params: { ring: parseFloat(editVals.ring), mtt: parseFloat(editVals.mtt) } }); setEditKey(null); load(); };
  if (loading) return <p style={{ color: "#6b7280" }}>Loading...</p>;
  return (
    <div style={S.card}>
      <div style={S.filterRow}>
        <input style={S.searchBar} placeholder="Search club or player ID..." value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ color: "#6b7280", fontSize: 13 }}>{filtered.length} entries</span>
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>{["Club : Player ID", "Ring Tax %", "MTT Tax %", "Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {filtered.map(([key, val], i) => (
              <tr key={key} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                <td style={{ ...S.td, fontFamily: "monospace", color: "#a5b4fc" }}>{key}</td>
                <td style={S.td}>{editKey === key ? <input style={S.smallInput} value={editVals.ring} onChange={e => setEditVals({ ...editVals, ring: e.target.value })} /> : <span style={{ color: "#f59e0b", fontWeight: 600 }}>{val.ring}%</span>}</td>
                <td style={S.td}>{editKey === key ? <input style={S.smallInput} value={editVals.mtt} onChange={e => setEditVals({ ...editVals, mtt: e.target.value })} /> : <span style={{ color: "#f59e0b", fontWeight: 600 }}>{val.mtt}%</span>}</td>
                <td style={S.td}>{editKey === key ? <><button style={S.saveBtn} onClick={() => save(key)}>Save</button><button style={S.cancelBtn} onClick={() => setEditKey(null)}>Cancel</button></> : <button style={S.editBtn} onClick={() => { setEditKey(key); setEditVals({ ring: val.ring, mtt: val.mtt }); }}>Edit</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DealsTab({ headers }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [clubFilter, setClubFilter] = useState("all");
  const [editKey, setEditKey] = useState(null);
  const [editVals, setEditVals] = useState({});
  const load = async () => { setLoading(true); const res = await axios.get(`${API}/deals`, { headers }); setData(res.data); setLoading(false); };
  useEffect(() => { load(); }, []);
  const clubs = useMemo(() => { const s = new Set(); Object.keys(data).forEach(k => { const p = k.split(":"); if (p[0] && p[1] !== "CLUB") s.add(p[0]); }); return [...s].sort(); }, [data]);
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return Object.entries(data).filter(([k]) => {
      // CLUB-setter deals live in the super-admin Club Deals tab, not here.
      if (k.split(":")[1] === "CLUB") return false;
      if (clubFilter !== "all" && !k.startsWith(clubFilter + ":")) return false;
      return k.toLowerCase().includes(q);
    });
  }, [data, search, clubFilter]);
  const save = async (key) => {
    await axios.put(`${API}/deals/${encodeURIComponent(key)}`, null, { headers, params: { ring_rb: parseFloat(editVals.ring_rb), mtt_rb: parseFloat(editVals.mtt_rb), ring_tax: parseFloat(editVals.ring_tax), mtt_tax: parseFloat(editVals.mtt_tax), tax_mode: editVals.tax_mode || "simple" } });
    setEditKey(null); load();
  };
  if (loading) return <p style={{ color: "#6b7280" }}>Loading...</p>;
  return (
    <div style={S.card}>
      <div style={S.filterRow}>
        <input style={S.searchBar} placeholder="Search by club, setter, or player ID..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={S.select} value={clubFilter} onChange={e => setClubFilter(e.target.value)}>
          <option value="all">All Clubs</option>
          {clubs.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span style={{ color: "#6b7280", fontSize: 13 }}>{filtered.length} deals</span>
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>{["Club", "Setter", "Player", "Ring RB%", "MTT RB%", "Ring Tax%", "MTT Tax%", "Mode", "Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {filtered.map(([key, val], i) => {
              const [club, setter, player] = key.split(":");
              return (
                <tr key={key} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                  <td style={S.td}><span style={S.badge("#818cf8")}>{club}</span></td>
                  <td style={{ ...S.td, fontFamily: "monospace", color: "#60a5fa" }}>{setter}<span style={{ color: "#6b7280", marginLeft: 6, fontSize: 11 }}>{val.setter_nick}</span></td>
                  <td style={{ ...S.td, fontFamily: "monospace", color: "#a5b4fc" }}>{player}<span style={{ color: "#6b7280", marginLeft: 6, fontSize: 11 }}>{val.player_nick}</span></td>
                  <td style={S.td}>{editKey === key ? <input style={S.smallInput} value={editVals.ring_rb} onChange={e => setEditVals({ ...editVals, ring_rb: e.target.value })} /> : <span style={{ color: "#2ecc71", fontWeight: 600 }}>{val.ring_rb}%</span>}</td>
                  <td style={S.td}>{editKey === key ? <input style={S.smallInput} value={editVals.mtt_rb} onChange={e => setEditVals({ ...editVals, mtt_rb: e.target.value })} /> : <span style={{ color: "#2ecc71", fontWeight: 600 }}>{val.mtt_rb}%</span>}</td>
                  <td style={S.td}>{editKey === key ? <input style={S.smallInput} value={editVals.ring_tax} onChange={e => setEditVals({ ...editVals, ring_tax: e.target.value })} /> : <span style={{ color: "#f59e0b" }}>{val.ring_tax}%</span>}</td>
                  <td style={S.td}>{editKey === key ? <input style={S.smallInput} value={editVals.mtt_tax} onChange={e => setEditVals({ ...editVals, mtt_tax: e.target.value })} /> : <span style={{ color: "#f59e0b" }}>{val.mtt_tax}%</span>}</td>
                  <td style={{ ...S.td, color: "#9ca3af", fontSize: 12 }}>{editKey === key ? <select style={S.select} value={editVals.tax_mode || "simple"} onChange={e => setEditVals({ ...editVals, tax_mode: e.target.value })}><option value="simple">simple</option><option value="complex">complex</option></select> : (val.tax_mode || <span style={{ color: "#6b7280" }}>simple</span>)}</td>
                  <td style={S.td}>{editKey === key ? <><button style={S.saveBtn} onClick={() => save(key)}>Save</button><button style={S.cancelBtn} onClick={() => setEditKey(null)}>Cancel</button></> : <button style={S.editBtn} onClick={() => { setEditKey(key); setEditVals({ ring_rb: val.ring_rb, mtt_rb: val.mtt_rb, ring_tax: val.ring_tax, mtt_tax: val.mtt_tax, tax_mode: val.tax_mode || "simple" }); }}>Edit</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Phase 3 — Club Deals (super-admin only) ─────────────────────────────
// CLUB-setter deals: `{club}:CLUB:{sa_id}` or `{club}:CLUB:{sa_id}:via-{X}`.
// Authoritative rate at the SA chain step in settlement (Strategy C).
function ClubDealsTab({ headers }) {
  const [rows, setRows] = useState([]);
  const [week, setWeek] = useState({ clubs: {} });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    club_name: "", sa_player_id: "", via_subtree_id: "",
    ring_rb: "", mtt_rb: "", ring_tax: "15", mtt_tax: "5",
    tax_mode: "simple",
  });
  const [msg, setMsg] = useState("");
  const [editKey, setEditKey] = useState(null);
  const [editVals, setEditVals] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/club-deals`, { headers }),
        axios.get(`${API}/week`, { headers }),
      ]);
      setRows(r1.data);
      setWeek(r2.data || { clubs: {} });
    } catch (e) {
      setMsg(e.response?.data?.detail || String(e));
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const clubList = useMemo(() => Object.keys(week.clubs || {}).sort(), [week]);
  const clubPlayers = useMemo(() => {
    const out = [];
    const seen = new Set();
    const res = (week.clubs?.[form.club_name] || {}).results || [];
    for (const p of res) {
      if (!seen.has(p.member_id)) { seen.add(p.member_id); out.push(p); }
    }
    // Also include referenced-only ids (agents/SAs who didn't play)
    const refNames = new Map();
    for (const p of res) {
      for (const [idF, nickF] of [["agent_id", "agent_nick"], ["super_agent_id", "super_agent_nick"]]) {
        const rid = p[idF];
        if (rid && rid !== "-" && !seen.has(rid)) {
          seen.add(rid);
          refNames.set(rid, p[nickF] || "");
        }
      }
    }
    for (const [rid, nick] of refNames) {
      out.push({ member_id: rid, nickname: nick, role: "(referenced)" });
    }
    return out.sort((a, b) => (a.nickname || "").localeCompare(b.nickname || ""));
  }, [week, form.club_name]);

  const submit = async () => {
    setMsg("");
    const body = {
      club_name: form.club_name,
      sa_player_id: form.sa_player_id,
      via_subtree_id: form.via_subtree_id || null,
      ring_rb: parseFloat(form.ring_rb),
      mtt_rb: parseFloat(form.mtt_rb),
      ring_tax: parseFloat(form.ring_tax),
      mtt_tax: parseFloat(form.mtt_tax),
      tax_mode: form.tax_mode || "simple",
    };
    try {
      await axios.post(`${API}/admin/club-deals`, body, { headers });
      setForm({ ...form, sa_player_id: "", via_subtree_id: "", ring_rb: "", mtt_rb: "", tax_mode: "simple" });
      setMsg("Club deal created.");
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || String(e));
    }
  };
  const save = async (key) => {
    try {
      await axios.put(`${API}/admin/club-deals/${encodeURIComponent(key)}`, null, {
        headers,
        params: {
          ring_rb: parseFloat(editVals.ring_rb),
          mtt_rb: parseFloat(editVals.mtt_rb),
          ring_tax: parseFloat(editVals.ring_tax),
          mtt_tax: parseFloat(editVals.mtt_tax),
          tax_mode: editVals.tax_mode || "simple",
        },
      });
      setEditKey(null); load();
    } catch (e) {
      setMsg(e.response?.data?.detail || String(e));
    }
  };
  const del = async (key) => {
    if (!window.confirm(`Delete CLUB deal?\n\n${key}`)) return;
    try {
      await axios.delete(`${API}/admin/club-deals/${encodeURIComponent(key)}`, { headers });
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || String(e));
    }
  };

  if (loading) return <p style={{ color: "#6b7280" }}>Loading...</p>;

  return (
    <div>
      <div style={S.card}>
        <h3 style={{ color: "#e0e0e0", marginTop: 0 }}>Add Club Deal</h3>
        <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
          Club pays this rate to the SA. Optional <em>via-subtree</em> narrows
          the rate to rake flowing through a specific Agent's downline.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 12 }}>
          <div>
            <label style={S.label}>Club</label>
            <select style={S.input} value={form.club_name} onChange={e => setForm({ ...form, club_name: e.target.value, sa_player_id: "", via_subtree_id: "" })}>
              <option value="">Select club...</option>
              {clubList.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>SA (deal target — the receiver)</label>
            <select style={S.input} value={form.sa_player_id} onChange={e => setForm({ ...form, sa_player_id: e.target.value })} disabled={!form.club_name}>
              <option value="">Select SA...</option>
              {clubPlayers.map(p => <option key={p.member_id} value={p.member_id}>{p.nickname || "(no nick)"} — {p.member_id} {p.role ? `[${p.role}]` : ""}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>Via subtree (optional — blank = default rate)</label>
            <select style={S.input} value={form.via_subtree_id} onChange={e => setForm({ ...form, via_subtree_id: e.target.value })} disabled={!form.club_name}>
              <option value="">(default / no via)</option>
              {clubPlayers.filter(p => p.member_id !== form.sa_player_id).map(p => (
                <option key={p.member_id} value={p.member_id}>{p.nickname || "(no nick)"} — {p.member_id} {p.role ? `[${p.role}]` : ""}</option>
              ))}
            </select>
          </div>
          <div></div>
          <div>
            <label style={S.label}>Ring RB %</label>
            <input style={S.input} type="number" step="0.01" value={form.ring_rb} onChange={e => setForm({ ...form, ring_rb: e.target.value })} />
          </div>
          <div>
            <label style={S.label}>MTT RB %</label>
            <input style={S.input} type="number" step="0.01" value={form.mtt_rb} onChange={e => setForm({ ...form, mtt_rb: e.target.value })} />
          </div>
          <div>
            <label style={S.label}>Ring Tax %</label>
            <input style={S.input} type="number" step="0.01" value={form.ring_tax} onChange={e => setForm({ ...form, ring_tax: e.target.value })} />
          </div>
          <div>
            <label style={S.label}>MTT Tax %</label>
            <input style={S.input} type="number" step="0.01" value={form.mtt_tax} onChange={e => setForm({ ...form, mtt_tax: e.target.value })} />
          </div>
          <div>
            <label style={S.label}>Tax Mode</label>
            <select style={S.input} value={form.tax_mode} onChange={e => setForm({ ...form, tax_mode: e.target.value })}>
              <option value="simple">simple — (winnings + rake × RB) × (1 − tax)</option>
              <option value="complex">complex — (winnings + rake × RB) − tax × (winnings + rake + BBJ)</option>
            </select>
          </div>
        </div>
        <button style={S.saveBtn} onClick={submit} disabled={!form.club_name || !form.sa_player_id || form.ring_rb === "" || form.mtt_rb === ""}>Create Club Deal</button>
        {msg && <p style={{ color: msg.toLowerCase().includes("created") ? "#2ecc71" : "#e74c3c", marginTop: 10 }}>{msg}</p>}
      </div>

      <div style={S.card}>
        <h3 style={{ color: "#e0e0e0", marginTop: 0 }}>Existing Club Deals <span style={{ color: "#6b7280", fontSize: 13, fontWeight: 400 }}>({rows.length})</span></h3>
        {rows.length === 0 ? (
          <p style={{ color: "#6b7280" }}>No club deals configured. Use the form above to add one.</p>
        ) : (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>{["Club", "SA", "Via", "Ring RB%", "MTT RB%", "Ring Tax%", "MTT Tax%", "Mode", "Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.key} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                    <td style={S.td}><span style={S.badge("#818cf8")}>{r.club_name}</span></td>
                    <td style={S.td}>
                      <strong style={{ color: "#e0e0e0" }}>{r.sa_nick || "(no nick)"}</strong>
                      <div style={{ color: "#6b7280", fontSize: 11, fontFamily: "monospace" }}>{r.sa_player_id}</div>
                    </td>
                    <td style={S.td}>
                      {r.via_subtree_id ? (
                        <>
                          <strong style={{ color: "#f59e0b" }}>{r.via_nick || "(no nick)"}</strong>
                          <div style={{ color: "#6b7280", fontSize: 11, fontFamily: "monospace" }}>{r.via_subtree_id}</div>
                        </>
                      ) : (
                        <em style={{ color: "#6b7280" }}>(default)</em>
                      )}
                    </td>
                    {["ring_rb", "mtt_rb", "ring_tax", "mtt_tax"].map(fld => (
                      <td key={fld} style={S.td}>
                        {editKey === r.key
                          ? <input style={S.smallInput} value={editVals[fld]} onChange={e => setEditVals({ ...editVals, [fld]: e.target.value })} />
                          : <span style={{ color: fld.endsWith("rb") ? "#2ecc71" : "#f59e0b", fontWeight: 600 }}>{r[fld]}%</span>
                        }
                      </td>
                    ))}
                    <td style={{ ...S.td, color: "#9ca3af", fontSize: 12 }}>
                      {editKey === r.key
                        ? <select style={S.select} value={editVals.tax_mode || "simple"} onChange={e => setEditVals({ ...editVals, tax_mode: e.target.value })}><option value="simple">simple</option><option value="complex">complex</option></select>
                        : (r.tax_mode || <span style={{ color: "#6b7280" }}>simple</span>)
                      }
                    </td>
                    <td style={S.td}>
                      {editKey === r.key
                        ? (<>
                            <button style={S.saveBtn} onClick={() => save(r.key)}>Save</button>
                            <button style={S.cancelBtn} onClick={() => setEditKey(null)}>Cancel</button>
                          </>)
                        : (<>
                            <button style={S.editBtn} onClick={() => { setEditKey(r.key); setEditVals({ ring_rb: r.ring_rb, mtt_rb: r.mtt_rb, ring_tax: r.ring_tax, mtt_tax: r.mtt_tax, tax_mode: r.tax_mode || "simple" }); }}>Edit</button>
                            <button style={{ ...S.cancelBtn, marginLeft: 4 }} onClick={() => del(r.key)}>Delete</button>
                          </>)
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function LinksTab({ headers }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  useEffect(() => { axios.get(`${API}/links`, { headers }).then(r => { setData(r.data); setLoading(false); }); }, [headers]);
  const entries = useMemo(() => {
    const q = search.toLowerCase();
    return Object.entries(data).filter(([chatId, d]) => chatId.includes(q) || (d.chat_name || "").toLowerCase().includes(q) || (d.player_ids || [d.player_id] || []).join(" ").includes(q));
  }, [data, search]);
  if (loading) return <p style={{ color: "#6b7280" }}>Loading...</p>;
  return (
    <div style={S.card}>
      <div style={S.filterRow}>
        <input style={S.searchBar} placeholder="Search chat name or player ID..." value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ color: "#6b7280", fontSize: 13 }}>{entries.length} linked chats</span>
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>{["Chat ID", "Chat Name", "Player IDs", "Linked At"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {entries.map(([chatId, d], i) => {
              const pids = d.player_ids || (d.player_id ? [d.player_id] : []);
              return (
                <tr key={chatId} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                  <td style={{ ...S.td, fontFamily: "monospace", color: "#6b7280" }}>{chatId}</td>
                  <td style={{ ...S.td, color: "#e0e0e0" }}>{d.chat_name || d.username || "—"}</td>
                  <td style={S.td}>{pids.map(p => <span key={p} style={{ ...S.badge("#60a5fa"), marginRight: 4 }}>{p}</span>)}</td>
                  <td style={{ ...S.td, color: "#6b7280", fontSize: 12 }}>{d.linked_at ? new Date(d.linked_at).toLocaleDateString() : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RakebackHistoryTab({ headers }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [newV, setNewV] = useState({ key: "", ring: "", mtt: "", effective_from: "" });
  const [msg, setMsg] = useState("");

  const load = async () => { setLoading(true); const res = await axios.get(`${API}/admin/rakeback-versions`, { headers }); setVersions(res.data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/admin/rakeback-versions`, { ...newV, ring: parseFloat(newV.ring), mtt: parseFloat(newV.mtt) }, { headers });
    setNewV({ key: "", ring: "", mtt: "", effective_from: "" });
    setMsg("Rakeback version added!"); setTimeout(() => setMsg(""), 3000); load();
  };

  const del = async (id) => { if (!window.confirm("Delete this rakeback version?")) return; await axios.delete(`${API}/admin/rakeback-versions/${id}`, { headers }); load(); };

  const filtered = useMemo(() => {
    if (!search) return versions;
    const q = search.toLowerCase();
    return versions.filter(v => v.key.toLowerCase().includes(q));
  }, [versions, search]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(v => { if (!g[v.key]) g[v.key] = []; g[v.key].push(v); });
    return g;
  }, [filtered]);

  return (
    <div>
      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 4 }}>Add Rakeback Version</div>
        <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 16 }}>Key format: <code style={{ color: "#a5b4fc" }}>ClubName:PlayerID</code></div>
        <form onSubmit={create} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>RAKEBACK KEY</div><input style={{ ...S.searchBar, width: 200 }} value={newV.key} onChange={e => setNewV({ ...newV, key: e.target.value })} placeholder="Club Name:1234-5678" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>RING %</div><input style={{ ...S.searchBar, width: 70 }} value={newV.ring} onChange={e => setNewV({ ...newV, ring: e.target.value })} placeholder="75" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>MTT %</div><input style={{ ...S.searchBar, width: 70 }} value={newV.mtt} onChange={e => setNewV({ ...newV, mtt: e.target.value })} placeholder="75" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>EFFECTIVE FROM (YYYY-MM-DD)</div><input style={{ ...S.searchBar, width: 130 }} value={newV.effective_from} onChange={e => setNewV({ ...newV, effective_from: e.target.value })} placeholder="leave blank = all time" /></div>
          <button style={{ ...S.saveBtn, padding: "8px 20px" }} type="submit">Add Version</button>
        </form>
        {msg && <p style={{ ...S.success, textAlign: "left", marginTop: 8 }}>{msg}</p>}
      </div>
      <div style={S.card}>
        <div style={S.filterRow}>
          <input style={S.searchBar} placeholder="Search rakeback key..." value={search} onChange={e => setSearch(e.target.value)} />
          <span style={{ color: "#6b7280", fontSize: 13 }}>{versions.length} versions for {Object.keys(grouped).length} players</span>
        </div>
        {loading ? <p style={{ color: "#6b7280" }}>Loading...</p> : (
          Object.entries(grouped).map(([key, vers]) => (
            <div key={key} style={{ marginBottom: 16, border: "1px solid #1f2937", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ background: "#1f2937", padding: "8px 14px", fontSize: 12, fontFamily: "monospace", color: "#a5b4fc", fontWeight: 600 }}>{key}</div>
              <table style={S.table}>
                <thead><tr>{["Effective From", "Ring %", "MTT %", "Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {vers.map((v, i) => (
                    <tr key={v.id} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                      <td style={S.td}><span style={{ color: v.effective_from ? "#60a5fa" : "#6b7280" }}>{v.effective_from || "All periods (default)"}</span></td>
                      <td style={{ ...S.td, color: "#2ecc71", fontWeight: 600 }}>{v.ring}%</td>
                      <td style={{ ...S.td, color: "#2ecc71", fontWeight: 600 }}>{v.mtt}%</td>
                      <td style={S.td}><button style={S.cancelBtn} onClick={() => del(v.id)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>
    </div>
  );
}


function DealHistoryTab({ headers }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [newV, setNewV] = useState({ key: "", ring_rb: "", mtt_rb: "", ring_tax: "", mtt_tax: "", effective_from: "" });
  const [msg, setMsg] = useState("");

  const load = async () => { setLoading(true); const res = await axios.get(`${API}/admin/deal-versions`, { headers }); setVersions(res.data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/admin/deal-versions`, { ...newV, ring_rb: parseFloat(newV.ring_rb), mtt_rb: parseFloat(newV.mtt_rb), ring_tax: parseFloat(newV.ring_tax), mtt_tax: parseFloat(newV.mtt_tax) }, { headers });
    setNewV({ key: "", ring_rb: "", mtt_rb: "", ring_tax: "", mtt_tax: "", effective_from: "" });
    setMsg("Deal version added!"); setTimeout(() => setMsg(""), 3000); load();
  };

  const del = async (id) => { if (!window.confirm("Delete this deal version?")) return; await axios.delete(`${API}/admin/deal-versions/${id}`, { headers }); load(); };

  const filtered = useMemo(() => {
    if (!search) return versions;
    const q = search.toLowerCase();
    return versions.filter(v => v.key.toLowerCase().includes(q));
  }, [versions, search]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(v => { if (!g[v.key]) g[v.key] = []; g[v.key].push(v); });
    return g;
  }, [filtered]);

  return (
    <div>
      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 4 }}>Add Deal Version</div>
        <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 16 }}>Key format: <code style={{ color: "#a5b4fc" }}>ClubName:SetterID:PlayerID</code></div>
        <form onSubmit={create} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>DEAL KEY</div><input style={{ ...S.searchBar, width: 220 }} value={newV.key} onChange={e => setNewV({ ...newV, key: e.target.value })} placeholder="Club Name:1234-5678:8765-4321" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>RING RB%</div><input style={{ ...S.searchBar, width: 70 }} value={newV.ring_rb} onChange={e => setNewV({ ...newV, ring_rb: e.target.value })} placeholder="75" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>MTT RB%</div><input style={{ ...S.searchBar, width: 70 }} value={newV.mtt_rb} onChange={e => setNewV({ ...newV, mtt_rb: e.target.value })} placeholder="75" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>RING TAX%</div><input style={{ ...S.searchBar, width: 70 }} value={newV.ring_tax} onChange={e => setNewV({ ...newV, ring_tax: e.target.value })} placeholder="15" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>MTT TAX%</div><input style={{ ...S.searchBar, width: 70 }} value={newV.mtt_tax} onChange={e => setNewV({ ...newV, mtt_tax: e.target.value })} placeholder="5" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>EFFECTIVE FROM (YYYY-MM-DD)</div><input style={{ ...S.searchBar, width: 130 }} value={newV.effective_from} onChange={e => setNewV({ ...newV, effective_from: e.target.value })} placeholder="leave blank = all time" /></div>
          <button style={{ ...S.saveBtn, padding: "8px 20px" }} type="submit">Add Version</button>
        </form>
        {msg && <p style={{ ...S.success, textAlign: "left", marginTop: 8 }}>{msg}</p>}
      </div>
      <div style={S.card}>
        <div style={S.filterRow}>
          <input style={S.searchBar} placeholder="Search deal key..." value={search} onChange={e => setSearch(e.target.value)} />
          <span style={{ color: "#6b7280", fontSize: 13 }}>{versions.length} versions for {Object.keys(grouped).length} deals</span>
        </div>
        {loading ? <p style={{ color: "#6b7280" }}>Loading...</p> : (
          Object.entries(grouped).map(([key, vers]) => (
            <div key={key} style={{ marginBottom: 16, border: "1px solid #1f2937", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ background: "#1f2937", padding: "8px 14px", fontSize: 12, fontFamily: "monospace", color: "#a5b4fc", fontWeight: 600 }}>{key}</div>
              <table style={S.table}>
                <thead><tr>{["Effective From", "Ring RB%", "MTT RB%", "Ring Tax%", "MTT Tax%", "Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {vers.map((v, i) => (
                    <tr key={v.id} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                      <td style={S.td}><span style={{ color: v.effective_from ? "#60a5fa" : "#6b7280" }}>{v.effective_from || "All periods (default)"}</span></td>
                      <td style={{ ...S.td, color: "#2ecc71", fontWeight: 600 }}>{v.ring_rb}%</td>
                      <td style={{ ...S.td, color: "#2ecc71", fontWeight: 600 }}>{v.mtt_rb}%</td>
                      <td style={{ ...S.td, color: "#f59e0b" }}>{v.ring_tax}%</td>
                      <td style={{ ...S.td, color: "#f59e0b" }}>{v.mtt_tax}%</td>
                      <td style={S.td}><button style={S.cancelBtn} onClick={() => del(v.id)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>
    </div>
  );
}


function ReferralsTab({ headers }) {
  const [refs, setRefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [newRef, setNewRef] = useState({ referrer: "", target: "", pct: "", club: "*", start_period: "" });
  const [editKey, setEditKey] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [msg, setMsg] = useState("");
  const load = async () => { setLoading(true); const res = await axios.get(`${API}/admin/referrals`, { headers }); setRefs(res.data); setLoading(false); };
  const [refClubOptions, setRefClubOptions] = useState([]);
  useEffect(() => {
    load();
    (async () => {
      try {
        const r = await axios.get(`${API}/clubs`, { headers });
        const names = Object.keys(r.data || {}).sort();
        // Super Admin is detected via presence of more than 1 assigned club or
        // via the account list endpoint. Simpler heuristic: if /superadmin/accounts
        // succeeds, user is super admin and gets "*" option.
        let isSuper = false;
        try { await axios.get(`${API}/superadmin/accounts`, { headers }); isSuper = true; } catch {}
        const opts = isSuper ? ["*", ...names] : names;
        setRefClubOptions(opts);
        setNewRef(nr => nr.club && opts.includes(nr.club) ? nr : { ...nr, club: opts[0] || "" });
      } catch {}
    })();
    /* eslint-disable-next-line */
  }, []);
  const create = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/admin/referrals`, newRef, { headers });
    setNewRef({ referrer: "", target: "", pct: "", club: "*", start_period: "" });
    setMsg("Referral created!"); setTimeout(() => setMsg(""), 3000); load();
  };
  const del = async (key) => { if (!window.confirm(`Delete referral ${key}?`)) return; await axios.delete(`${API}/admin/referrals/${encodeURIComponent(key)}`, { headers }); load(); };
  const save = async (key) => { await axios.put(`${API}/admin/referrals/${encodeURIComponent(key)}`, editVals, { headers }); setEditKey(null); load(); };
  return (
    <div>
      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 16 }}>Add Referral</div>
        <form onSubmit={create} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>REFERRER ID</div><input style={{ ...S.searchBar, width: 120 }} value={newRef.referrer} onChange={e => setNewRef({ ...newRef, referrer: e.target.value })} placeholder="1234-5678" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>TARGET ID</div><input style={{ ...S.searchBar, width: 120 }} value={newRef.target} onChange={e => setNewRef({ ...newRef, target: e.target.value })} placeholder="8765-4321" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>PCT %</div><input style={{ ...S.searchBar, width: 70 }} value={newRef.pct} onChange={e => setNewRef({ ...newRef, pct: e.target.value })} placeholder="5" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>CLUB</div>
            <select style={{ ...S.searchBar, width: 160 }} value={newRef.club} onChange={e => setNewRef({ ...newRef, club: e.target.value })} required>
              {refClubOptions.map(c => <option key={c} value={c}>{c === "*" ? "* (all clubs)" : c}</option>)}
            </select>
          </div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>START DATE (YYYY-MM-DD)</div><input style={{ ...S.searchBar, width: 130 }} value={newRef.start_period} onChange={e => setNewRef({ ...newRef, start_period: e.target.value })} placeholder="2026-03-30 (optional)" /></div>
          <button style={{ ...S.saveBtn, padding: "8px 20px" }} type="submit">Add Referral</button>
        </form>
        {msg && <p style={{ ...S.success, textAlign: "left", marginTop: 8 }}>{msg}</p>}
      </div>
      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 16 }}>Referrals ({Object.keys(refs).length})</div>
        {loading ? <p style={{ color: "#6b7280" }}>Loading...</p> : (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>{["Key", "PCT %", "Start Date", "Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {Object.entries(refs).map(([key, val], i) => (
                  <tr key={key} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                    <td style={{ ...S.td, fontFamily: "monospace", color: "#a5b4fc" }}>{key}</td>
                    <td style={S.td}>{editKey === key ? <input style={S.smallInput} value={editVals.pct} onChange={e => setEditVals({ ...editVals, pct: parseFloat(e.target.value) })} /> : <span style={{ color: "#2ecc71", fontWeight: 600 }}>{val.pct}%</span>}</td>
                    <td style={S.td}>{editKey === key ? <input style={{ ...S.smallInput, width: 100 }} value={editVals.start_period || ""} onChange={e => setEditVals({ ...editVals, start_period: e.target.value })} placeholder="YYYY-MM-DD" /> : <span style={{ color: "#9ca3af" }}>{val.start_period || "All periods"}</span>}</td>
                    <td style={S.td}>{editKey === key ? <><button style={S.saveBtn} onClick={() => save(key)}>Save</button><button style={S.cancelBtn} onClick={() => setEditKey(null)}>Cancel</button></> : <><button style={{ ...S.editBtn, marginRight: 6 }} onClick={() => { setEditKey(key); setEditVals({ pct: val.pct, start_period: val.start_period || "", referrer: key.split(":")[key.split(":").length-2] || "", target: key.split(":")[key.split(":").length-1] || "", club: "*" }); }}>Edit</button><button style={S.cancelBtn} onClick={() => del(key)}>Delete</button></>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function ChartsTab({ headers, isPlayer, playerId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/weekly-summary`, { headers })
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [headers]);

  if (loading) return <p style={{ color: "#6b7280" }}>Loading chart data...</p>;
  const chartData = [...data].reverse();
  if (data.length === 0) return <div style={S.card}><p style={{ color: "#6b7280" }}>No historical data yet. Upload multiple weeks to see charts.</p></div>;

  const maxRake = Math.max(...data.map(d => d.rake), 1);
  const allVals = data.flatMap(d => [d.net_profit, d.stl, isPlayer ? (d.player_points || 0) : 0]);
  const maxVal = Math.max(...allVals.map(Math.abs), 1);

  const barW = Math.max(40, Math.min(80, Math.floor(600 / data.length) - 10));
  const chartH = 200;

  const Bar = ({ value, max, color, label }) => {
    const pct = Math.abs(value) / max;
    const h = Math.round(pct * chartH * 0.85);
    const isNeg = value < 0;
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
        <div style={{ fontSize: 10, color: color, fontWeight: 600 }}>{value >= 0 ? "+" : ""}{Math.round(value).toLocaleString()}</div>
        <div style={{ width: barW, height: chartH, display: "flex", flexDirection: "column", justifyContent: "center", position: "relative" }}>
          <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "#374151" }} />
          {isNeg
            ? <div style={{ position: "absolute", top: "50%", left: "10%", right: "10%", height: h, background: color + "cc", borderRadius: "0 0 4px 4px" }} />
            : <div style={{ position: "absolute", bottom: "50%", left: "10%", right: "10%", height: h, background: color + "cc", borderRadius: "4px 4px 0 0" }} />
          }
        </div>
        <div style={{ fontSize: 10, color: "#6b7280", textAlign: "center", width: barW }}>{label}</div>
      </div>
    );
  };

  return (
    <div>
      {!isPlayer && (
        <div style={S.card}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 20 }}>📊 Net Profit by Week</div>
          <div style={{ overflowX: "auto" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", minWidth: data.length * (barW + 8), paddingBottom: 8 }}>
              {chartData.map(d => <Bar key={d.period} value={d.net_profit} max={maxVal} color="#2ecc71" label={d.label} />)}
            </div>
          </div>
        </div>
      )}

      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 20 }}>📊 {isPlayer ? "My Rake by Week" : "Total Rake by Week"}</div>
        <div style={{ overflowX: "auto" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", minWidth: data.length * (barW + 8), paddingBottom: 8 }}>
            {chartData.map(d => <Bar key={d.period} value={isPlayer ? (d.player_rake || 0) : d.rake} max={isPlayer ? maxVal : maxRake} color={isPlayer ? "#2ecc71" : "#f59e0b"} label={d.label} />)}
          </div>
        </div>
      </div>

      {!isPlayer && (
        <div style={S.card}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 20 }}>📊 Rake vs Net Profit by Week</div>
          <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "#2ecc71" }}>■ Settlement</span>
            <span style={{ fontSize: 12, color: "#e74c3c" }}>■ Paid Out</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", minWidth: data.length * (barW * 2 + 12), paddingBottom: 8 }}>
              {data.map(d => (
                <div key={d.period} style={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
                  <Bar value={d.rake} max={maxVal} color="#f59e0b" label={d.label} />
                  <Bar value={d.net_profit} max={maxVal} color="#2ecc71" label="" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 16 }}>📋 Weekly Summary Table</div>
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>{(isPlayer ? ["Week", "Rake", "Agent Profit", "Settlement"] : ["Week", "Rake", "Settlement", "Paid Out", "Referrals", "Net Profit"]).map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.map((d, i) => (
                <tr key={d.period} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                  <td style={{ ...S.td, color: "#9ca3af", fontSize: 11, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{d.period}</td>
                  {isPlayer ? (
                    <>
                      <td style={{ ...S.td, color: "#f59e0b" }}>{fmt(d.player_rake || 0, false)}</td>
                      <td style={{ ...S.td, color: clr(d.player_deal_profit || 0), fontWeight: 700 }}>{fmt(d.player_deal_profit || 0)}</td>
                      <td style={{ ...S.td, color: clr(d.player_points || 0) }}>{fmt(d.player_points || 0)}</td>
                    </>
                  ) : (
                    <>
                      <td style={{ ...S.td, color: "#f59e0b" }}>{fmt(d.rake, false)}</td>
                      <td style={{ ...S.td, color: clr(d.stl) }}>{fmt(d.stl)}</td>
                      <td style={{ ...S.td, color: "#e74c3c" }}>{fmt(d.paid)}</td>
                      <td style={{ ...S.td, color: "#e74c3c" }}>{fmt(d.referrals)}</td>
                      <td style={{ ...S.td, color: clr(d.net_profit), fontWeight: 700 }}>{fmt(d.net_profit)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "#1f2937", borderTop: "2px solid #374151" }}>
                <td style={{ ...S.td, color: "#fff", fontWeight: 700 }}>TOTAL</td>
                {isPlayer ? (
                  <>
                    <td style={{ ...S.td, color: "#f59e0b", fontWeight: 700 }}>{fmt(data.reduce((s, d) => s + (d.player_rake || 0), 0), false)}</td>
                    <td style={{ ...S.td, color: clr(data.reduce((s, d) => s + (d.player_deal_profit || 0), 0)), fontWeight: 700 }}>{fmt(data.reduce((s, d) => s + (d.player_deal_profit || 0), 0))}</td>
                    <td style={{ ...S.td, color: clr(data.reduce((s, d) => s + (d.player_points || 0), 0)), fontWeight: 700 }}>{fmt(data.reduce((s, d) => s + (d.player_points || 0), 0))}</td>
                  </>
                ) : (
                  <>
                    <td style={{ ...S.td, color: "#f59e0b", fontWeight: 700 }}>{fmt(data.reduce((s, d) => s + d.rake, 0), false)}</td>
                    <td style={{ ...S.td, color: clr(data.reduce((s, d) => s + d.stl, 0)), fontWeight: 700 }}>{fmt(data.reduce((s, d) => s + d.stl, 0))}</td>
                    <td style={{ ...S.td, color: "#e74c3c", fontWeight: 700 }}>{fmt(data.reduce((s, d) => s + d.paid, 0))}</td>
                    <td style={{ ...S.td, color: "#e74c3c", fontWeight: 700 }}>{fmt(data.reduce((s, d) => s + d.referrals, 0))}</td>
                    <td style={{ ...S.td, color: clr(data.reduce((s, d) => s + d.net_profit, 0)), fontWeight: 700 }}>{fmt(data.reduce((s, d) => s + d.net_profit, 0))}</td>
                  </>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}


function AdminDashboard_DEPRECATED({ token, onLogout }) {
  const [tab, setTab] = useState("clubs");
  const headers = useMemo(() => getAuthHeaders(), []);
  const tabs = ["clubs", "stats", "charts", "player-rates", "deals", "deal-history", "rate-history", "referrals", "club-settings", "links", "accounts"];
  return (
    <div style={S.app}>
      <div style={S.header}>
        <div><h1 style={S.headerTitle}>♠️ Accountobot Dashboard</h1><div style={S.headerSub}>Admin</div></div>
        <button style={S.logoutBtn} onClick={onLogout}>Logout</button>
      </div>
      <div style={S.tabs}>
        {tabs.map(t => <button key={t} style={tab === t ? S.activeTab : S.tab} onClick={() => setTab(t)}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>)}
      </div>
      <div style={S.content}>
        {tab === "clubs" && <ClubsTab headers={headers} />}
        {tab === "stats" && <StatsTab headers={headers} />}
        {tab === "charts" && <ChartsTab headers={headers} isPlayer={false} />}
        {tab === "player-rates" && <PlayerRatesTab headers={headers} />}
        {tab === "deals" && <DealsTab headers={headers} />}
        {tab === "deal-history" && <DealHistoryTab headers={headers} />}
        {tab === "rate-history" && <RateHistoryTab headers={headers} />}
        {tab === "referrals" && <ReferralsTab headers={headers} />}
        {tab === "club-settings" && <ClubSettingsTab headers={headers} />}
        {tab === "links" && <LinksTab headers={headers} />}
        {tab === "accounts" && <PlayerAccountsTab headers={headers} />}
      </div>
    </div>
  );
}

function AddClubRateForm({ headers, onSaved }) {
  const [form, setForm] = useState({ club: "", rb: "0", ring_tax: "0", mtt_tax: "0", tax_mode: "simple" });
  const [msg, setMsg] = useState("");
  const [clubOptions, setClubOptions] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/clubs`, { headers });
        const names = Object.keys(r.data || {}).sort();
        setClubOptions(names);
        setForm(f => f.club ? f : { ...f, club: names[0] || "" });
      } catch {}
    })();
    /* eslint-disable-next-line */
  }, []);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.club) { setMsg("Select a club"); return; }
    await axios.put(`${API}/club-rates/${encodeURIComponent(form.club)}`, null, { headers, params: { rb: parseFloat(form.rb), ring_tax: parseFloat(form.ring_tax), mtt_tax: parseFloat(form.mtt_tax), tax_mode: form.tax_mode } });
    setForm({ club: clubOptions[0] || "", rb: "0", ring_tax: "0", mtt_tax: "0", tax_mode: "simple" });
    setMsg("Club rate added!"); setTimeout(() => setMsg(""), 3000);
    onSaved();
  };
  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 8 }}>
      <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>CLUB</div>
        <select style={{ ...S.searchBar, width: 180 }} value={form.club} onChange={e => setForm({ ...form, club: e.target.value })} required>
          {clubOptions.length === 0 && <option value="">No clubs available</option>}
          {clubOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>RAKEBACK %</div><input style={{ ...S.searchBar, width: 80 }} value={form.rb} onChange={e => setForm({ ...form, rb: e.target.value })} placeholder="0" required /></div>
      <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>RING TAX %</div><input style={{ ...S.searchBar, width: 80 }} value={form.ring_tax} onChange={e => setForm({ ...form, ring_tax: e.target.value })} placeholder="0" required /></div>
      <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>MTT TAX %</div><input style={{ ...S.searchBar, width: 80 }} value={form.mtt_tax} onChange={e => setForm({ ...form, mtt_tax: e.target.value })} placeholder="0" required /></div>
      <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>TAX MODE</div><select style={S.select} value={form.tax_mode} onChange={e => setForm({ ...form, tax_mode: e.target.value })}><option value="simple">Simple</option><option value="complex">Complex</option></select></div>
      <button style={{ ...S.saveBtn, padding: "8px 16px" }} type="submit">Add / Update Club</button>
      {msg && <span style={{ color: "#2ecc71", fontSize: 13 }}>{msg}</span>}
    </form>
  );
}

function TelegramAdminIdsPanel({ headers, isSuperAdmin }) {
  const [msg, setMsg] = useState("");
  const [tgAdminIds, setTgAdminIds] = useState("");
  const [tgAdminSaved, setTgAdminSaved] = useState("");
  const [tgSaving, setTgSaving] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/bot/telegram-admin-ids`, { headers });
      const ids = r.data.telegram_admin_ids || "";
      setTgAdminIds(ids);
      setTgAdminSaved(ids);
    } catch {}
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!isSuperAdmin) load(); }, []);

  const saveTgAdminIds = async () => {
    setTgSaving(true); setMsg("");
    try {
      const cleaned = tgAdminIds.split(",").map(s => s.trim()).filter(s => /^[0-9]+$/.test(s)).join(",");
      const r = await axios.post(`${API}/bot/telegram-admin-ids`, { telegram_admin_ids: cleaned }, { headers });
      if (r.data.success) {
        setTgAdminSaved(cleaned);
        setTgAdminIds(cleaned);
        setMsg(r.data.restarted ? "Telegram admins updated — bot restarted." : "Saved, but bot restart may have failed.");
        setTimeout(() => setMsg(""), 4000);
        await load();
      } else {
        setMsg("Error: " + (r.data.error || "Failed"));
      }
    } catch (e) { setMsg("Error: " + (e.response?.data?.detail || "Failed")); }
    setTgSaving(false);
  };

  if (isSuperAdmin) return null;

  return (
    <div style={S.card}>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 6 }}>👤 Telegram Admin IDs</div>
      <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 12 }}>
        Telegram user IDs allowed to run admin commands on your bot (e.g. /admin, /broadcast, /setrakeback).
        Comma-separated. To find yours, message <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer" style={{ color: "#60a5fa" }}>@userinfobot</a> on Telegram.
        The Super Admin ID is always included automatically.
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 auto", minWidth: 320 }}>
          <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>IDS (comma-separated)</div>
          <input style={{ ...S.searchBar, width: "100%" }} value={tgAdminIds} onChange={e => setTgAdminIds(e.target.value)} placeholder="e.g. 12345678, 87654321" />
        </div>
        <button style={S.saveBtn} onClick={saveTgAdminIds} disabled={tgSaving || tgAdminIds === tgAdminSaved}>
          {tgSaving ? "Saving…" : "Save Admin IDs"}
        </button>
      </div>
      {msg && <p style={{ ...S.success, textAlign: "left", marginTop: 10 }}>{msg}</p>}
    </div>
  );
}

function ClubSettingsTab({ headers, isSuperAdmin }) {
  const [rates, setRates] = useState({});
  const [taxModes, setTaxModes] = useState({});
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editClub, setEditClub] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [msg, setMsg] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    const [r1, r2, r3] = await Promise.all([
      axios.get(`${API}/club-rates`, { headers }),
      axios.get(`${API}/tax-modes`, { headers }),
      axios.get(`${API}/players`, { headers }).catch(() => ({ data: [] })),
    ]);
    setRates(r1.data); setTaxModes(r2.data); setPlayers(r3.data || []); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveRate = async (club) => {
    await axios.put(`${API}/club-rates/${encodeURIComponent(club)}`, null, { headers, params: { rb: parseFloat(editVals.rb), ring_tax: parseFloat(editVals.ring_tax), mtt_tax: parseFloat(editVals.mtt_tax), tax_mode: editVals.tax_mode || 'simple' } });
    setEditClub(null); setMsg("Club rates saved!"); setTimeout(() => setMsg(""), 3000); load();
  };

  const nicknameById = Object.fromEntries(players.map(p => [p.member_id, p.nickname]));
  const filteredModes = Object.entries(taxModes).filter(([k]) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return k.toLowerCase().includes(q) || (nicknameById[k] || "").toLowerCase().includes(q);
  });

  if (loading) return <p style={{ color: "#6b7280" }}>Loading...</p>;
  return (
    <div>
      <TelegramAdminIdsPanel headers={headers} isSuperAdmin={isSuperAdmin} />
      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 16 }}>Club Rakeback & Tax Rates</div>
        {msg && <p style={{ ...S.success, textAlign: "left" }}>{msg}</p>}
        <AddClubRateForm headers={headers} onSaved={load} />
        <div style={{ marginTop: 20 }} />
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>{["Club", "Rakeback %", "Ring Tax %", "MTT Tax %", "Tax Mode", "Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {Object.entries(rates).map(([club, val], i) => (
                <tr key={club} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                  <td style={{ ...S.td, fontWeight: 600, color: "#e0e0e0" }}>{club}</td>
                  <td style={S.td}>{editClub === club ? <input style={S.smallInput} value={editVals.rb} onChange={e => setEditVals({ ...editVals, rb: e.target.value })} /> : <span style={{ color: "#2ecc71", fontWeight: 600 }}>{val.rb}%</span>}</td>
                  <td style={S.td}>{editClub === club ? <input style={S.smallInput} value={editVals.ring_tax} onChange={e => setEditVals({ ...editVals, ring_tax: e.target.value })} /> : <span style={{ color: "#f59e0b" }}>{val.ring_tax}%</span>}</td>
                  <td style={S.td}>{editClub === club ? <input style={S.smallInput} value={editVals.mtt_tax} onChange={e => setEditVals({ ...editVals, mtt_tax: e.target.value })} /> : <span style={{ color: "#f59e0b" }}>{val.mtt_tax}%</span>}</td>
                  <td style={S.td}>{editClub === club ? <select style={{ ...S.select, padding: "2px 6px", fontSize: 12 }} value={editVals.tax_mode || "simple"} onChange={e => setEditVals({ ...editVals, tax_mode: e.target.value })}><option value="simple">Simple</option><option value="complex">Complex</option></select> : <span style={S.badge(val.tax_mode === "complex" ? "#818cf8" : "#2ecc71")}>{val.tax_mode || "simple"}</span>}</td>
                  <td style={S.td}>{editClub === club ? <><button style={S.saveBtn} onClick={() => saveRate(club)}>Save</button><button style={S.cancelBtn} onClick={() => setEditClub(null)}>Cancel</button></> : <button style={S.editBtn} onClick={() => { setEditClub(club); setEditVals({ rb: val.rb, ring_tax: val.ring_tax, mtt_tax: val.mtt_tax, tax_mode: val.tax_mode || "simple" }); }}>Edit</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 6 }}>Player Tax Modes <span style={{ fontSize: 11, fontWeight: 500, color: "#6b7280", marginLeft: 8 }}>(read-only)</span></div>
        <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 12 }}>
          Default is <strong style={{ color: "#2ecc71" }}>simple</strong>. <strong style={{ color: "#818cf8" }}>Complex</strong> means the tax% is applied to winnings + rake directly. Edit these from the <strong style={{ color: "#60a5fa" }}>Player Rates</strong> tab — the Mode dropdown next to each row.
        </div>
        <div style={S.filterRow}>
          <input style={S.searchBar} placeholder="Search player ID or nickname..." value={search} onChange={e => setSearch(e.target.value)} />
          <span style={{ color: "#6b7280", fontSize: 13 }}>{filteredModes.length} entries</span>
        </div>
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>{["Player ID", "Nickname", "Tax Mode"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {filteredModes.map(([pid, mode], i) => (
                <tr key={pid} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                  <td style={{ ...S.td, fontFamily: "monospace", color: "#a5b4fc" }}>{pid}</td>
                  <td style={S.td}>{nicknameById[pid] || <em style={{ color: "#6b7280" }}>—</em>}</td>
                  <td style={S.td}><span style={S.badge(mode === "complex" ? "#818cf8" : "#2ecc71")}>{mode}</span></td>
                </tr>
              ))}
              {filteredModes.length === 0 && (
                <tr><td colSpan={3} style={{ ...S.td, color: "#6b7280", textAlign: "center", padding: 20 }}>No player tax-mode overrides.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SuperAdminAccountsTab({ headers }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ username: "", password: "", display_name: "", assigned_clubs: "", bot_token: "" });
  const [msg, setMsg] = useState("");
  const [editAccount, setEditAccount] = useState(null);

  const load = async () => { setLoading(true); const res = await axios.get(`${API}/superadmin/accounts`, { headers }); setAccounts(res.data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/superadmin/accounts`, {
      ...form,
      assigned_clubs: form.assigned_clubs.split(",").map(s => s.trim()).filter(Boolean),
    }, { headers });
    setForm({ username: "", password: "", display_name: "", assigned_clubs: "", bot_token: "" });
    setMsg("Account created!"); setTimeout(() => setMsg(""), 3000); load();
  };

  const update = async (username) => {
    await axios.put(`${API}/superadmin/accounts/${username}`, {
      ...editAccount,
      assigned_clubs: typeof editAccount.assigned_clubs === "string"
        ? editAccount.assigned_clubs.split(",").map(s => s.trim()).filter(Boolean)
        : editAccount.assigned_clubs,
    }, { headers });
    setEditAccount(null); load();
  };

  const del = async (username) => {
    if (!window.confirm(`Delete account ${username}?`)) return;
    await axios.delete(`${API}/superadmin/accounts/${username}`, { headers });
    load();
  };

  return (
    <div>
      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 16 }}>Create Club Admin Account</div>
        <form onSubmit={create} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>USERNAME</div><input style={{ ...S.searchBar, width: 130 }} value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="username" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>PASSWORD</div><input style={{ ...S.searchBar, width: 130 }} type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Temp password" required /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>DISPLAY NAME</div><input style={{ ...S.searchBar, width: 150 }} value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} placeholder="Display Name" /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>ASSIGNED CLUBS (comma separated)</div><input style={{ ...S.searchBar, width: 200 }} value={form.assigned_clubs} onChange={e => setForm({ ...form, assigned_clubs: e.target.value })} placeholder="Club A, Club B" /></div>
          <div><div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>BOT TOKEN</div><input style={{ ...S.searchBar, width: 200 }} value={form.bot_token} onChange={e => setForm({ ...form, bot_token: e.target.value })} placeholder="Telegram bot token" /></div>
          <button style={{ ...S.saveBtn, padding: "8px 20px" }} type="submit">Create Account</button>
        </form>
        {msg && <p style={{ ...S.success, textAlign: "left", marginTop: 8 }}>{msg}</p>}
      </div>
      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#e0e0e0", marginBottom: 16 }}>Club Admin Accounts ({accounts.length})</div>
        {loading ? <p style={{ color: "#6b7280" }}>Loading...</p> : (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>{["Username", "Display Name", "Assigned Clubs", "Bot Token", "Last Login", "Active", "Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {accounts.map((a, i) => (
                  <tr key={a.username} style={{ background: i % 2 === 0 ? "transparent" : "#0d1520" }}>
                    <td style={{ ...S.td, fontFamily: "monospace", color: "#a5b4fc" }}>{a.username}</td>
                    <td style={S.td}>{editAccount?.username === a.username ? <input style={S.smallInput} value={editAccount.display_name || ""} onChange={e => setEditAccount({ ...editAccount, display_name: e.target.value })} /> : a.display_name || "—"}</td>
                    <td style={S.td}>{editAccount?.username === a.username
                      ? <input style={{ ...S.smallInput, width: 160 }} value={typeof editAccount.assigned_clubs === "string" ? editAccount.assigned_clubs : (editAccount.assigned_clubs || []).join(", ")} onChange={e => setEditAccount({ ...editAccount, assigned_clubs: e.target.value })} />
                      : (a.assigned_clubs || []).map(c => <span key={c} style={{ ...S.badge("#818cf8"), marginRight: 4 }}>{c}</span>)}</td>
                    <td style={{ ...S.td, color: "#6b7280", fontSize: 11 }}>{editAccount?.username === a.username ? <input style={{ ...S.smallInput, width: 160 }} value={editAccount.bot_token || ""} onChange={e => setEditAccount({ ...editAccount, bot_token: e.target.value })} /> : (a.bot_token ? "✅ Set" : "❌ Not set")}</td>
                    <td style={{ ...S.td, color: "#6b7280", fontSize: 12 }}>{a.last_login ? new Date(a.last_login).toLocaleDateString() : "Never"}</td>
                    <td style={S.td}><span style={S.badge(a.is_active ? "#2ecc71" : "#e74c3c")}>{a.is_active ? "Active" : "Disabled"}</span></td>
                    <td style={S.td}>
                      {editAccount?.username === a.username
                        ? <><button style={S.saveBtn} onClick={() => update(a.username)}>Save</button><button style={S.cancelBtn} onClick={() => setEditAccount(null)}>Cancel</button></>
                        : <><button style={{ ...S.editBtn, marginRight: 4 }} onClick={() => setEditAccount({ ...a, assigned_clubs: (a.assigned_clubs || []).join(", ") })}>Edit</button>
                           <button style={{ ...S.cancelBtn, marginRight: 4 }} onClick={() => { const p = prompt("New password:"); if (p) axios.put(`${API}/superadmin/accounts/${a.username}`, { password: p }, { headers }).then(() => setMsg("Password reset!")); }}>Reset PW</button>
                           <button style={S.cancelBtn} onClick={() => del(a.username)}>Delete</button></>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SuperAdminDashboard({ token, onLogout }) {
  const [tab, setTab] = useState("clubs");
  const headers = useMemo(() => getAuthHeaders(), []);
  const tabs = ["clubs", "stats", "charts", "player-rates", "deals", "club-deals", "deal-history", "rate-history", "referrals", "club-settings", "links", "accounts", "admins", "bots", "link-requests"];
  return (
    <div style={S.app}>
      <div style={S.header}>
        <div><h1 style={S.headerTitle}>♠️ Accountobot Super Admin</h1><div style={S.headerSub}>System Administrator</div></div>
        <button style={S.logoutBtn} onClick={onLogout}>Logout</button>
      </div>
      <div style={S.tabs}>
        {tabs.map(t => <button key={t} style={tab === t ? S.activeTab : S.tab} onClick={() => setTab(t)}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>)}
      </div>
      <div style={S.content}>
        {tab === "clubs" && <ClubsTab headers={headers} />}
        {tab === "stats" && <StatsTab headers={headers} />}
        {tab === "charts" && <ChartsTab headers={headers} isPlayer={false} />}
        {tab === "player-rates" && <PlayerRatesTab headers={headers} />}
        {tab === "deals" && <DealsTab headers={headers} />}
        {tab === "club-deals" && <ClubDealsTab headers={headers} />}
        {tab === "deal-history" && <DealHistoryTab headers={headers} />}
        {tab === "rate-history" && <RateHistoryTab headers={headers} />}
        {tab === "referrals" && <ReferralsTab headers={headers} />}
        {tab === "club-settings" && <ClubSettingsTab headers={headers} isSuperAdmin={true} />}
        {tab === "links" && <LinksTab headers={headers} />}
        {tab === "accounts" && <PlayerAccountsTab headers={headers} />}
        {tab === "admins" && <SuperAdminAccountsTab headers={headers} />}
        {tab === "bots" && <BotsTab headers={headers} />}
        {tab === "link-requests" && <LinkRequestsTab headers={headers} />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 5 Part 3 — self-service bot provisioning UI
// Components: BotSetupPage, MyBotPage, BotsTab (+ AuditDrawer, LogsDrawer,
//             RotateTokenModal, RemoveConfirmModal, BotStateBadge helper).
// Backend contracts are frozen at Part 2 — see /opt/accntng/PHASE5_PLAN.md.
// ─────────────────────────────────────────────────────────────────────────────

const TOKEN_RE = /^\d{6,12}:[A-Za-z0-9_-]{30,50}$/;
const TG_ID_RE = /^\d{5,19}$/;

const codeStyle = { background: "#1f2937", color: "#a5f3fc", padding: "2px 6px", borderRadius: 4, fontSize: 12, fontFamily: "monospace" };
const modalOverlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 };
const modalCard = { background: "#111827", borderRadius: 10, border: "1px solid #2ecc71", padding: 24, maxWidth: 480, width: "100%", boxShadow: "0 0 40px rgba(46,204,113,0.2)" };

function mapBotError(err) {
  const st = err.response?.status;
  const detail = err.response?.data?.detail || "Unknown error";
  if (st === 400) return `Invalid input: ${detail}`;
  if (st === 403) return `Forbidden: ${detail}`;
  if (st === 404) return `Not found: ${detail}`;
  if (st === 409) return `Conflict: ${detail}`;
  if (st === 422) return `Telegram rejected the token — double-check it. (${detail})`;
  if (st === 429) return `Too many attempts. ${detail}`;
  if (st === 500) return `Server error: ${detail}`;
  if (st === 504) return `Timed out — script took too long. ${detail}`;
  return `Error ${st || "?"}: ${detail}`;
}

function BotStateBadge({ value, goodValues = ["active", "enabled", "ok"] }) {
  if (!value) return <span style={S.badge("#6b7280")}>unknown</span>;
  const good = goodValues.includes(value);
  return <span style={S.badge(good ? "#2ecc71" : "#e74c3c")}>{value}</span>;
}

function BotSetupPage({ headers, onProvisioned, onLogout, displayName }) {
  const [token, setToken] = useState("");
  const [tgId, setTgId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [cooldownUntil, setCooldownUntil] = useState(0);

  const tokenOk = TOKEN_RE.test(token);
  const tgIdOk = TG_ID_RE.test(tgId);
  const onCooldown = Date.now() < cooldownUntil;
  const canSubmit = tokenOk && tgIdOk && !submitting && !onCooldown;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/bot/provision`, { token, tg_id: tgId }, { headers });
      setSubmitting(false);
      onProvisioned();
    } catch (err) {
      setError(mapBotError(err));
      setSubmitting(false);
      setCooldownUntil(Date.now() + 10000);
    }
  };

  return (
    <div style={S.app}>
      <div style={S.header}>
        <div>
          <h1 style={S.headerTitle}>♠️ Accountobot</h1>
          <div style={S.headerSub}>{displayName || "Club Admin"} — Set up your bot</div>
        </div>
        <button style={S.logoutBtn} onClick={onLogout}>Logout</button>
      </div>

      <div style={S.content}>
        <div style={S.card}>
          <h2 style={{ color: "#2ecc71", marginTop: 0, fontSize: 20 }}>Welcome — let's connect your Telegram bot.</h2>
          <p style={{ color: "#9ca3af", lineHeight: 1.6, margin: 0 }}>
            You don't have a bot connected yet. Follow the three steps below, then paste your token and your Telegram user ID. This takes about two minutes.
          </p>
        </div>

        <div style={S.card}>
          <h3 style={{ color: "#e0e0e0", marginTop: 0, fontSize: 16 }}>1. Create a bot with @BotFather</h3>
          <ol style={{ color: "#d1d5db", lineHeight: 1.8, paddingLeft: 24, margin: 0 }}>
            <li>Open Telegram and DM <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" style={{ color: "#60a5fa" }}>@BotFather</a>.</li>
            <li>Send <code style={codeStyle}>/newbot</code>. BotFather asks for a display name (anything) and a username (must end in <code style={codeStyle}>bot</code>).</li>
            <li>BotFather replies with a <b>token</b> like <code style={codeStyle}>8123456789:AAHb...</code>. Copy it — you'll paste it below.</li>
          </ol>
        </div>

        <div style={S.card}>
          <h3 style={{ color: "#e0e0e0", marginTop: 0, fontSize: 16 }}>2. Find your Telegram user ID</h3>
          <p style={{ color: "#d1d5db", lineHeight: 1.6, margin: 0 }}>
            DM <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer" style={{ color: "#60a5fa" }}>@userinfobot</a>. It replies with your numeric ID (e.g. <code style={codeStyle}>123456789</code>). Copy it.
          </p>
        </div>

        <div style={S.card}>
          <h3 style={{ color: "#e0e0e0", marginTop: 0, fontSize: 16 }}>3. Paste & provision</h3>
          <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 6, marginTop: 12 }}>Bot token</div>
          <input
            type="password"
            placeholder="123456789:AAHbgX_..."
            value={token}
            onChange={e => setToken(e.target.value.trim())}
            style={{ ...S.input, fontFamily: "monospace" }}
            autoComplete="new-password"
          />
          {token && !tokenOk && (
            <div style={{ color: "#f59e0b", fontSize: 12, marginTop: -6, marginBottom: 10 }}>
              Expected: digits:alphanumeric_30-50 (e.g. <code style={codeStyle}>8123456789:AAHbgX_b4Y...</code>)
            </div>
          )}

          <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 6 }}>Your Telegram user ID</div>
          <input
            type="text"
            inputMode="numeric"
            placeholder="123456789"
            value={tgId}
            onChange={e => setTgId(e.target.value.trim())}
            style={S.input}
          />
          {tgId && !tgIdOk && (
            <div style={{ color: "#f59e0b", fontSize: 12, marginTop: -6, marginBottom: 10 }}>
              Expected: 5–19 digits, numeric only.
            </div>
          )}

          {error && <div style={{ ...S.error, textAlign: "left", marginTop: 8, marginBottom: 12 }}>{error}</div>}

          <button
            style={{ ...S.loginBtn, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? "pointer" : "not-allowed" }}
            onClick={submit}
            disabled={!canSubmit}
          >
            {submitting ? "Provisioning… (takes ~10s)" : onCooldown ? "Please wait…" : "Provision bot"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RotateTokenModal({ headers, onClose, onSuccess }) {
  const [newToken, setNewToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const ok = TOKEN_RE.test(newToken);

  const submit = async () => {
    if (!ok || submitting) return;
    setSubmitting(true); setErr("");
    try {
      const r = await axios.post(`${API}/bot/rotate-token`, { new_token: newToken }, { headers });
      setSubmitting(false);
      onSuccess(r.data.bot_handle);
    } catch (e) {
      setErr(mapBotError(e));
      setSubmitting(false);
    }
  };

  return (
    <div style={modalOverlay}>
      <div style={modalCard}>
        <h3 style={{ marginTop: 0, color: "#e0e0e0", fontSize: 18 }}>Rotate bot token</h3>
        <div style={{ color: "#f59e0b", background: "#f59e0b22", border: "1px solid #f59e0b44", borderRadius: 6, padding: 10, fontSize: 12, marginBottom: 14 }}>
          ⚠ The bot will restart. In-flight chats may drop a message or two.
        </div>
        <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 6 }}>New token</div>
        <input
          type="password"
          placeholder="123456789:AAHb..."
          value={newToken}
          onChange={e => setNewToken(e.target.value.trim())}
          style={{ ...S.input, fontFamily: "monospace" }}
          autoComplete="new-password"
          autoFocus
        />
        {newToken && !ok && <div style={{ color: "#f59e0b", fontSize: 12, marginTop: -6, marginBottom: 10 }}>Expected: digits:alphanumeric_30-50</div>}
        {err && <div style={{ color: "#e74c3c", fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button style={S.cancelBtn} onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            style={{ ...S.saveBtn, opacity: ok && !submitting ? 1 : 0.5, cursor: ok && !submitting ? "pointer" : "not-allowed" }}
            onClick={submit}
            disabled={!ok || submitting}
          >
            {submitting ? "Rotating…" : "Rotate"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MyBotPage({ headers, status, refetch }) {
  const [acting, setActing] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [rotateOpen, setRotateOpen] = useState(false);

  const paused = status.enabled === "disabled" || status.active === "inactive";

  const doAction = async (path, verb) => {
    setActing(true); setMsg(""); setErr("");
    try {
      await axios.post(`${API}${path}`, {}, { headers });
      setMsg(`${verb} ok.`);
      await refetch();
      setTimeout(() => setMsg(""), 3000);
    } catch (e) {
      setErr(mapBotError(e));
    }
    setActing(false);
  };

  let subtitle = "";
  if (status.active === "active" && status.enabled === "enabled") subtitle = "✔ Your bot is running. It will auto-restart on reboot.";
  else if (status.active === "inactive" && status.enabled === "disabled") subtitle = "⏸ Bot is paused. It will stay stopped across reboots until you resume.";
  else if (status.active === "failed") subtitle = "⚠ Bot service is in a failed state. Contact your Super Admin to check the logs.";
  else if (status.active === "inactive" && status.enabled === "enabled") subtitle = "Bot is stopped but will restart on boot. Pause keeps it stopped across reboots.";

  return (
    <div>
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
          <div>
            <div style={{ color: "#6b7280", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8 }}>Bot handle</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#e0e0e0", marginTop: 4 }}>
              {status.bot_handle
                ? `@${status.bot_handle}`
                : <span style={{ color: "#6b7280", fontStyle: "italic", fontSize: 14, fontWeight: 400 }}>(not recorded — rotate your token to populate)</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ color: "#6b7280", fontSize: 12 }}>Active:</span>
            <BotStateBadge value={status.active} />
            <span style={{ color: "#6b7280", fontSize: 12, marginLeft: 12 }}>Enabled:</span>
            <BotStateBadge value={status.enabled} goodValues={["enabled", "static"]} />
          </div>
        </div>
        {subtitle && <div style={{ color: "#9ca3af", fontSize: 13, marginTop: 16 }}>{subtitle}</div>}
      </div>

      <div style={S.card}>
        <h3 style={{ marginTop: 0, color: "#e0e0e0", fontSize: 16 }}>Actions</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {!paused && (
            <button style={{ ...S.editBtn, background: "#f59e0b", color: "#000" }} disabled={acting} onClick={() => doAction("/bot/pause", "Pause")}>
              ⏸ Pause
            </button>
          )}
          {paused && (
            <button style={{ ...S.editBtn, background: "#2ecc71", color: "#000" }} disabled={acting} onClick={() => doAction("/bot/resume", "Resume")}>
              ▶ Resume
            </button>
          )}
          <button style={S.editBtn} disabled={acting} onClick={() => setRotateOpen(true)}>
            🔑 Rotate token
          </button>
        </div>
        {msg && <div style={{ color: "#2ecc71", marginTop: 12, fontSize: 13 }}>{msg}</div>}
        {err && <div style={{ color: "#e74c3c", marginTop: 12, fontSize: 13 }}>{err}</div>}
      </div>

      {rotateOpen && (
        <RotateTokenModal
          headers={headers}
          onClose={() => setRotateOpen(false)}
          onSuccess={async (newHandle) => {
            setRotateOpen(false);
            setMsg(newHandle ? `Rotated. Your bot is now @${newHandle}.` : "Rotated.");
            await refetch();
            setTimeout(() => setMsg(""), 6000);
          }}
        />
      )}
    </div>
  );
}

function AuditDrawer({ data }) {
  if (data === undefined) return <div style={{ color: "#6b7280" }}>Loading audit…</div>;
  if (data && data._error) return <div style={{ color: "#e74c3c" }}>{data._error}</div>;
  if (!data || data.length === 0) return <div style={{ color: "#6b7280" }}>No audit entries.</div>;
  return (
    <div>
      <div style={{ color: "#9ca3af", fontSize: 12, marginBottom: 8 }}>Last {data.length} audit entries (most recent first)</div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>{["When", "Actor", "Action", "Exit", "Success", "Meta"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.map(r => (
              <tr key={r.id}>
                <td style={S.td}>{r.ts}</td>
                <td style={S.td}>{r.actor_username}{r.actor_is_super ? " (super)" : ""}</td>
                <td style={S.td}>{r.action}</td>
                <td style={S.td}>{r.exit_code ?? "—"}</td>
                <td style={S.td}><BotStateBadge value={r.success ? "ok" : "fail"} goodValues={["ok"]} /></td>
                <td style={{ ...S.td, fontFamily: "monospace", fontSize: 11, maxWidth: 360, whiteSpace: "normal", wordBreak: "break-all" }}>
                  {r.meta ? JSON.stringify(r.meta) : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LogsDrawer({ data }) {
  if (data === undefined) return <div style={{ color: "#6b7280" }}>Loading logs…</div>;
  if (data && data._error) return <div style={{ color: "#e74c3c" }}>{data._error}</div>;
  if (!data?.ok) return <div style={{ color: "#e74c3c" }}>{data?.error || "journalctl failed"}</div>;
  return (
    <div>
      <div style={{ color: "#9ca3af", fontSize: 12, marginBottom: 8 }}>journalctl -u {data.unit} (tail 30)</div>
      <pre style={{ background: "#000", color: "#e5e7eb", padding: 14, borderRadius: 6, fontSize: 11, maxHeight: 360, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all", margin: 0 }}>
        {data.lines || "(empty)"}
      </pre>
    </div>
  );
}

function RemoveConfirmModal({ username, onCancel, onConfirm }) {
  const [typed, setTyped] = useState("");
  const ok = typed === username;
  return (
    <div style={modalOverlay}>
      <div style={{ ...modalCard, border: "1px solid #e74c3c", boxShadow: "0 0 40px rgba(231,76,60,0.3)" }}>
        <h3 style={{ marginTop: 0, color: "#e74c3c", fontSize: 18 }}>Remove bot for {username}?</h3>
        <div style={{ color: "#f59e0b", background: "#f59e0b22", border: "1px solid #f59e0b44", borderRadius: 6, padding: 10, fontSize: 12, marginBottom: 14 }}>
          ⚠ Stops the bot, disables the unit, archives the env file with a timestamp suffix. The admin_accounts row is preserved so this admin can re-provision with a new token. Archive is recoverable, but the bot stops responding to users immediately.
        </div>
        <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 6 }}>Type <code style={codeStyle}>{username}</code> to confirm:</div>
        <input
          type="text"
          value={typed}
          onChange={e => setTyped(e.target.value)}
          style={S.input}
          autoFocus
        />
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button style={S.cancelBtn} onClick={onCancel}>Cancel</button>
          <button
            style={{ ...S.saveBtn, background: "#e74c3c", color: "#fff", opacity: ok ? 1 : 0.5, cursor: ok ? "pointer" : "not-allowed" }}
            onClick={onConfirm}
            disabled={!ok}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

function BotsTab({ headers }) {
  const [bots, setBots] = useState(null);   // null = loading
  const [err, setErr] = useState("");
  const [expandedAudit, setExpandedAudit] = useState({});
  const [expandedLogs, setExpandedLogs] = useState({});
  const [auditCache, setAuditCache] = useState({});
  const [logsCache, setLogsCache] = useState({});
  const [acting, setActing] = useState({});
  const [msg, setMsg] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(null);

  const refetch = async () => {
    try {
      const r = await axios.get(`${API}/admin/bots`, { headers });
      setBots(r.data.bots || []);
      setErr("");
    } catch (e) {
      setErr(mapBotError(e));
      setBots([]);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refetch(); }, []);

  const loadAudit = async (u) => {
    if (auditCache[u] !== undefined) return;
    try {
      const r = await axios.get(`${API}/admin/bots/${encodeURIComponent(u)}/audit?limit=50`, { headers });
      setAuditCache(prev => ({ ...prev, [u]: r.data.audit || [] }));
    } catch (e) {
      setAuditCache(prev => ({ ...prev, [u]: { _error: mapBotError(e) } }));
    }
  };
  const loadLogs = async (u) => {
    if (logsCache[u] !== undefined) return;
    try {
      const r = await axios.get(`${API}/admin/bots/${encodeURIComponent(u)}/logs?lines=30`, { headers });
      setLogsCache(prev => ({ ...prev, [u]: r.data }));
    } catch (e) {
      setLogsCache(prev => ({ ...prev, [u]: { _error: mapBotError(e) } }));
    }
  };
  const toggleAudit = async (u) => {
    const next = !expandedAudit[u];
    setExpandedAudit(prev => ({ ...prev, [u]: next }));
    if (next) await loadAudit(u);
  };
  const toggleLogs = async (u) => {
    const next = !expandedLogs[u];
    setExpandedLogs(prev => ({ ...prev, [u]: next }));
    if (next) await loadLogs(u);
  };

  const invalidate = (u) => {
    setAuditCache(prev => { const { [u]: _x, ...rest } = prev; return rest; });
    setLogsCache(prev => { const { [u]: _x, ...rest } = prev; return rest; });
    setExpandedAudit(prev => ({ ...prev, [u]: false }));
    setExpandedLogs(prev => ({ ...prev, [u]: false }));
  };

  const act = async (u, verb) => {
    setActing(prev => ({ ...prev, [u]: true }));
    setMsg("");
    try {
      await axios.post(`${API}/admin/bots/${encodeURIComponent(u)}/${verb}`, {}, { headers });
      setMsg(`${verb} ok: ${u}`);
      invalidate(u);
      await refetch();
      setTimeout(() => setMsg(""), 4000);
    } catch (e) {
      setMsg(`Error on ${verb} ${u}: ${mapBotError(e)}`);
    }
    setActing(prev => ({ ...prev, [u]: false }));
  };

  const confirmRemoveGo = async () => {
    const u = confirmRemove;
    setConfirmRemove(null);
    await act(u, "remove");
  };

  if (bots === null) return <div style={{ color: "#9ca3af", padding: 20 }}>Loading…</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ color: "#e0e0e0", margin: 0, fontSize: 18 }}>All tenant bots ({bots.length})</h2>
        <button style={S.editBtn} onClick={refetch}>↻ Refresh</button>
      </div>
      {msg && (
        <div style={{
          background: msg.startsWith("Error") ? "#e74c3c22" : "#2ecc7122",
          color: msg.startsWith("Error") ? "#e74c3c" : "#2ecc71",
          padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13,
        }}>{msg}</div>
      )}
      {err && <div style={S.error}>{err}</div>}

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Username", "Handle", "Active", "Enabled", "Actions", "Drawers"].map(h =>
                <th key={h} style={S.th}>{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {bots.length === 0 && (
              <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: "#6b7280", padding: 30 }}>No provisioned bots.</td></tr>
            )}
            {bots.flatMap(b => {
              const u = b.username;
              const busy = !!acting[u];
              const rows = [
                <tr key={`${u}-main`}>
                  <td style={S.td}><strong style={{ color: "#e0e0e0" }}>{u}</strong></td>
                  <td style={S.td}>
                    {b.bot_handle
                      ? <span style={{ color: "#60a5fa" }}>@{b.bot_handle}</span>
                      : <span style={{ color: "#6b7280", fontStyle: "italic" }}>(not recorded)</span>}
                  </td>
                  <td style={S.td}><BotStateBadge value={b.active} /></td>
                  <td style={S.td}><BotStateBadge value={b.enabled} goodValues={["enabled", "static"]} /></td>
                  <td style={S.td}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      <button style={S.editBtn} disabled={busy} onClick={() => act(u, "stop")}>Stop</button>
                      <button style={S.editBtn} disabled={busy} onClick={() => act(u, "start")}>Start</button>
                      <button style={S.editBtn} disabled={busy} onClick={() => act(u, "restart")}>Restart</button>
                      <button style={{ ...S.editBtn, background: "#e74c3c" }} disabled={busy} onClick={() => setConfirmRemove(u)}>Remove</button>
                    </div>
                  </td>
                  <td style={S.td}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      <button style={S.editBtn} onClick={() => toggleAudit(u)}>{expandedAudit[u] ? "▼" : "▶"} Audit</button>
                      <button style={S.editBtn} onClick={() => toggleLogs(u)}>{expandedLogs[u] ? "▼" : "▶"} Logs</button>
                    </div>
                  </td>
                </tr>
              ];
              if (expandedAudit[u]) {
                rows.push(
                  <tr key={`${u}-audit`}>
                    <td colSpan={6} style={{ ...S.td, background: "#0a0f1a", padding: 14 }}>
                      <AuditDrawer data={auditCache[u]} />
                    </td>
                  </tr>
                );
              }
              if (expandedLogs[u]) {
                rows.push(
                  <tr key={`${u}-logs`}>
                    <td colSpan={6} style={{ ...S.td, background: "#0a0f1a", padding: 14 }}>
                      <LogsDrawer data={logsCache[u]} />
                    </td>
                  </tr>
                );
              }
              return rows;
            })}
          </tbody>
        </table>
      </div>

      {confirmRemove && (
        <RemoveConfirmModal
          username={confirmRemove}
          onCancel={() => setConfirmRemove(null)}
          onConfirm={confirmRemoveGo}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ClubAdminDashboard({ token, displayName, onLogout }) {
  const [tab, setTab] = useState("clubs");
  const [botStatus, setBotStatus] = useState(null); // null = loading
  const headers = useMemo(() => getAuthHeaders(), []);
  const tabs = ["clubs", "stats", "charts", "player-rates", "deals", "deal-history", "rate-history", "referrals", "groups", "notes", "club-settings", "links", "accounts", "my-bot", "my-links"];

  const refetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/bot/status`, { headers });
      setBotStatus(r.data);
    } catch (e) {
      // Treat any error as "unprovisioned" for routing purposes so the user
      // still sees the setup page instead of a blank screen.
      setBotStatus({ provisioned: false, _error: true });
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refetchStatus(); }, []);

  if (botStatus === null) {
    return (
      <div style={{ ...S.app, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "#9ca3af", fontSize: 14 }}>Loading…</div>
      </div>
    );
  }
  if (!botStatus.provisioned) {
    return <BotSetupPage headers={headers} onProvisioned={refetchStatus} onLogout={onLogout} displayName={displayName} />;
  }

  return (
    <div style={S.app}>
      <div style={S.header}>
        <div><h1 style={S.headerTitle}>♠️ Accountobot Dashboard</h1><div style={S.headerSub}>{displayName || "Club Admin"}</div></div>
        <button style={S.logoutBtn} onClick={onLogout}>Logout</button>
      </div>
      <div style={S.tabs}>
        {tabs.map(t => <button key={t} style={tab === t ? S.activeTab : S.tab} onClick={() => setTab(t)}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>)}
      </div>
      <div style={S.content}>
        {tab === "clubs" && <ClubsTab headers={headers} />}
        {tab === "stats" && <StatsTab headers={headers} />}
        {tab === "charts" && <ChartsTab headers={headers} isPlayer={false} />}
        {tab === "player-rates" && <PlayerRatesTab headers={headers} />}
        {tab === "deals" && <DealsTab headers={headers} />}
        {tab === "deal-history" && <DealHistoryTab headers={headers} />}
        {tab === "rate-history" && <RateHistoryTab headers={headers} />}
        {tab === "referrals" && <ReferralsTab headers={headers} />}
        {tab === "groups" && <GroupsTab headers={headers} token={token} />}
        {tab === "notes" && <NotesTab headers={headers} />}
        {tab === "club-settings" && <ClubSettingsTab headers={headers} />}
        {tab === "links" && <LinksTab headers={headers} />}
        {tab === "accounts" && <PlayerAccountsTab headers={headers} />}
        {tab === "my-bot" && <MyBotPage headers={headers} status={botStatus} refetch={refetchStatus} />}
        {tab === "my-links" && <MyLinksTab headers={headers} />}
      </div>
    </div>
  );
}

// Phase 2 view-mode banner. Lives at App() level above whichever dashboard
// is active, so toggling between admin / player view doesn't unmount it and
// so its state survives tab switches inside the dashboards. Renders nothing
// when there are no linked tokens — i.e. for super admin, players, and Club
// Admins with zero approved links.
function ViewModeBanner({ adminUsername, viewMode, linkedIds, onChange, revokedNotice, onDismissRevoked }) {
  const inPlayerView = viewMode !== "admin";
  const baseStyle = {
    padding: "10px 20px",
    fontSize: 13,
    display: "flex",
    alignItems: "center",
    gap: 14,
    borderBottom: "1px solid #1f2937",
  };
  const adminStyle  = { ...baseStyle, background: "#1e293b", color: "#cbd5e1" };
  const playerStyle = { ...baseStyle, background: "#78350f", color: "#fde68a", fontWeight: 600 };
  return (
    <>
      {revokedNotice ? (
        <div style={{ padding: "10px 20px", background: "#7f1d1d", color: "#fee2e2", fontSize: 13, display: "flex", alignItems: "center", gap: 14, borderBottom: "1px solid #1f2937" }}>
          <span>⚠ Your linked player access was revoked. Re-login to refresh.</span>
          <button onClick={onDismissRevoked} style={{ marginLeft: "auto", background: "transparent", border: "1px solid #fca5a5", color: "#fee2e2", padding: "3px 10px", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>Dismiss</button>
        </div>
      ) : null}
      <div style={inPlayerView ? playerStyle : adminStyle}>
        {inPlayerView ? (
          <span>Viewing as player {viewMode}. Rate changes must be made via player login.</span>
        ) : (
          <span>Viewing as Club Admin: {adminUsername}</span>
        )}
        <select
          value={viewMode}
          onChange={(e) => onChange(e.target.value)}
          style={{ marginLeft: "auto", background: "#0f172a", color: "#e0e0e0", border: "1px solid #374151", borderRadius: 6, padding: "5px 10px", fontSize: 12 }}
        >
          <option value="admin">Admin view</option>
          {linkedIds.map(pid => (
            <option key={pid} value={pid}>Player view: {pid}</option>
          ))}
        </select>
      </div>
    </>
  );
}

export default function App() {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [userType, setUserType] = useState(localStorage.getItem("userType"));
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [playerId, setPlayerId] = useState(localStorage.getItem("playerId"));
  const [displayName, setDisplayName] = useState(localStorage.getItem("displayName"));
  const [viewMode, setViewModeState] = useState(localStorage.getItem(LS_VIEW_MODE) || "admin");
  const [linkedTokens, setLinkedTokens] = useState(getLinkedPlayerTokens());
  const [revokedNotice, setRevokedNotice] = useState(false);

  const setViewMode = (m) => {
    localStorage.setItem(LS_VIEW_MODE, m);
    setViewModeState(m);
  };

  const logout = () => {
    localStorage.removeItem("token"); localStorage.removeItem("userType");
    localStorage.removeItem("playerId"); localStorage.removeItem("displayName");
    localStorage.removeItem(LS_LINKED_TOKENS); localStorage.removeItem(LS_VIEW_MODE);
    setToken(null); setUserType(null); setPlayerId(null); setMustChangePassword(false); setDisplayName(null);
    setLinkedTokens({}); setViewModeState("admin"); setRevokedNotice(false);
  };

  // Global axios response interceptor:
  //   - 401 with detail "Link revoked or invalidated…" → purge linked-token
  //     cache, drop to admin view, surface a non-blocking banner. Don't log
  //     the user out — their admin JWT is still valid.
  //   - 401 on any other endpoint → full logout (existing behavior).
  //   - 401 on a login endpoint → skipped; Login surfaces "Invalid credentials".
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const url = err?.config?.url || "";
        const detail = err?.response?.data?.detail || "";
        const isLoginEndpoint = /\/(token|admin\/token|player\/token)$/.test(url);
        if (status === 401 && !isLoginEndpoint) {
          if (typeof detail === "string" && detail.startsWith("Link revoked or invalidated")) {
            // Phase 2: linked-player token went stale. Purge cache, drop to
            // admin view, ping the user — admin JWT is unaffected.
            clearLinkedPlayerTokens();
            setLinkedTokens({});
            setViewModeState("admin");
            setRevokedNotice(true);
          } else {
            localStorage.removeItem("token"); localStorage.removeItem("userType");
            localStorage.removeItem("playerId"); localStorage.removeItem("displayName");
            localStorage.removeItem(LS_LINKED_TOKENS); localStorage.removeItem(LS_VIEW_MODE);
            setToken(null); setUserType(null); setPlayerId(null);
            setMustChangePassword(false); setDisplayName(null);
            setLinkedTokens({}); setViewModeState("admin"); setRevokedNotice(false);
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const handleAdminLogin = (t) => { setToken(t); setUserType("superadmin"); setLinkedTokens({}); setViewModeState("admin"); };
  const handleClubAdminLogin = (t, name) => {
    setToken(t); setUserType("clubadmin"); setDisplayName(name);
    setLinkedTokens(getLinkedPlayerTokens());
    setViewModeState(localStorage.getItem(LS_VIEW_MODE) || "admin");
  };
  const handlePlayerLogin = (t, mustChange, pid) => { setToken(t); setUserType("player"); setPlayerId(pid); setMustChangePassword(mustChange); };

  if (!token) return <Login onAdminLogin={handleAdminLogin} onClubAdminLogin={handleClubAdminLogin} onPlayerLogin={handlePlayerLogin} />;
  if (userType === "player" && mustChangePassword) return <ChangePassword token={token} onDone={() => setMustChangePassword(false)} />;
  if (userType === "player") return <PlayerDashboard token={token} playerId={playerId} onLogout={logout} />;

  if (userType === "clubadmin") {
    const linkedIds = Object.keys(linkedTokens || {});
    const showBanner = linkedIds.length > 0;
    // Defensive: if viewMode points at a player_id no longer in the cache
    // (e.g. stale localStorage after a mid-session revoke), snap back to admin.
    const effectiveViewMode = (viewMode !== "admin" && !linkedTokens[viewMode]) ? "admin" : viewMode;
    if (effectiveViewMode !== viewMode) {
      // Sync state without rerender thrash — set on next tick.
      setTimeout(() => setViewMode("admin"), 0);
    }
    const inPlayerView = effectiveViewMode !== "admin";
    return (
      <>
        {showBanner ? (
          <ViewModeBanner
            adminUsername={displayName || "Club Admin"}
            viewMode={effectiveViewMode}
            linkedIds={linkedIds}
            onChange={setViewMode}
            revokedNotice={revokedNotice}
            onDismissRevoked={() => setRevokedNotice(false)}
          />
        ) : null}
        {inPlayerView ? (
          <PlayerDashboard
            key={`player-${effectiveViewMode}`}
            token={linkedTokens[effectiveViewMode]}
            playerId={effectiveViewMode}
            onLogout={logout}
            viaLink={true}
          />
        ) : (
          <ClubAdminDashboard token={token} displayName={displayName} onLogout={logout} />
        )}
      </>
    );
  }
  return <SuperAdminDashboard token={token} onLogout={logout} />;
}